import { z } from 'zod';
import { sha256 } from '@noble/hashes/sha256';

// src/models/wire/enums.ts
var TransactionStatus = {
  PENDING: "pending",
  MINED: "mined",
  CONFIRMED: "confirmed",
  FAILED: "failed",
  DISPUTED: "disputed",
  ARCHIVED: "archived"
};
var CALLER_OVERRIDABLE_STATUSES = [
  TransactionStatus.DISPUTED,
  TransactionStatus.ARCHIVED
];
var TransactionDirection = {
  SENT: "sent",
  RECEIVED: "received",
  SELF: "self"
};
var EntityMatch = {
  ASSIGNED: "assigned",
  ROLLUP: "rollup",
  BOTH: "both"
};
var SearchTermKind = {
  PROTOCOL: "protocol",
  ADDRESS: "address",
  CHAIN: "chain",
  ENTITY: "entity",
  TAG: "tag",
  LABEL: "label",
  GROUP: "group"
};

// src/models/wire/coerce.ts
function coerceCsv(value) {
  if (value == null) return null;
  if (Array.isArray(value)) {
    const out = value.filter((v) => v != null && String(v) !== "").map((v) => String(v));
    return out;
  }
  if (typeof value === "string") {
    const parts = value.split(",").map((p) => p.trim()).filter((p) => p.length > 0);
    return parts.length > 0 ? parts : null;
  }
  return [String(value)];
}
function coerceBool(value) {
  if (value == null || typeof value === "boolean") return value ?? null;
  if (typeof value === "number") return Boolean(value);
  if (typeof value === "string") {
    const n = value.trim().toLowerCase();
    if (["true", "1", "yes", "y", "on"].includes(n)) return true;
    if (["false", "0", "no", "n", "off", ""].includes(n)) return false;
  }
  throw new Error(`cannot coerce ${JSON.stringify(value)} to bool`);
}
function normaliseHash(value) {
  if (typeof value !== "string") return value;
  const s = value.trim().toLowerCase();
  if (s.startsWith("0x")) return s;
  if (s.length === 64 && /^[0-9a-f]+$/.test(s)) return `0x${s}`;
  return s;
}

// src/tenancy/constants.ts
var GLOBAL_TENANT = "main";
var GLOBAL_TENANT_COMPOSITE = "main-main";

// src/singleton/index.ts
var NAMESPACE = "@authe/fe-commons/singleton/";
function globalSingleton(key, init) {
  const slot = /* @__PURE__ */ Symbol.for(NAMESPACE + key);
  const realm = globalThis;
  const existing = realm[slot];
  if (existing !== void 0) return existing;
  const created = init();
  realm[slot] = created;
  return created;
}
var BRAND_NAMESPACE = "@authe/fe-commons/brand/";
var ordinaryHasInstance = Function.prototype[Symbol.hasInstance];
function brandClass(cls, key) {
  const brand = /* @__PURE__ */ Symbol.for(BRAND_NAMESPACE + key);
  Object.defineProperty(cls.prototype, brand, { value: true });
  Object.defineProperty(cls, Symbol.hasInstance, {
    value(value) {
      if (this !== cls) return ordinaryHasInstance.call(this, value);
      return typeof value === "object" && value !== null && value[brand] === true;
    }
  });
}
var EvidenceSourceSchema = z.object({ "kind": z.any().default("abi"), "id": z.union([z.string(), z.null()]).default(null), "external_id": z.union([z.string(), z.null()]).default(null), "url": z.union([z.string(), z.null()]).default(null), "raw_payload_ref": z.union([z.string(), z.null()]).default(null), "visibility": z.union([z.any(), z.null()]).default(null) }).describe('Who / how asserted an :class:`Evidence` entry.\n\n``id`` is the source-local identifier: a provider slug (``"moralis"``), an\nABI source (``"sourcify"``), a protocol-plugin id, or a user id \u2014 depending\non :attr:`kind`. For ``kind == PROVIDER`` the id is normalised to a\ncanonical provider slug via :func:`commons.providers.normalize_provider_id`\n(mirrors the old ``ProviderAttribution.provider_id`` discipline).');
var EvidenceSchema = z.object({ "source": z.any().optional(), "confidence": z.number().gte(0).lte(1).default(1), "reasons": z.array(z.string()).optional(), "signals": z.record(z.string(), z.any()).optional(), "asserted_fields": z.array(z.string()).optional(), "asserted_at": z.union([z.number().int(), z.null()]).default(null), "last_seen_at": z.union([z.number().int(), z.null()]).default(null), "context": z.record(z.string(), z.any()).optional(), "metadata": z.record(z.string(), z.any()).optional() }).describe("One assertion: *this source claims X, with this confidence, because \u2026*.\n\nReplaces both ``SemanticEvidence`` (decode) and ``ProviderAttribution``\n(catalog). ``confidence`` is clamped to ``[0, 1]``. ``asserted_at`` /\n``last_seen_at`` default to wall-clock seconds so a fresh assertion merges\nwith an older one without a ``None`` branch (see\n:func:`commons.evidence.engine.merge_evidence`).\n\nDecode-specific coordinates (chain / block / cross-chain hop / proxy\ntraversal) live in :attr:`context` under the ``CTX_*`` keys; read them via\nthe convenience properties (:attr:`chain_id`, :attr:`dest_chain_id`, \u2026).");

// src/models/wire/graph-scene.ts
var MEDIA_REF_KINDS = ["placeholder", "cdn_key", "url"];
var GRAPH_KINDS = ["semantic", "flow", "interaction", "knowledge"];
var GRAPH_LAYOUT_HINTS = ["hierarchical", "temporal", "force"];
var SCENE_NODE_KINDS = [
  "address",
  "contract",
  "entity",
  "cluster",
  "exchange",
  "defi",
  "token",
  "oracle",
  "bridge",
  "don",
  "mixer",
  "virtual",
  "verification",
  "attestation",
  "relayer",
  "message",
  "settlement"
];
var OFFCHAIN_SCENE_NODE_KINDS = [
  "entity",
  "cluster",
  "exchange",
  "oracle",
  "bridge",
  "don",
  "mixer",
  "virtual",
  "verification",
  "attestation",
  "relayer",
  "settlement"
];
var SCENE_EDGE_KINDS = [
  "transfer",
  "call",
  "contains",
  "bridge_out",
  "bridge_in",
  "cross_chain",
  "flow",
  "verify",
  "creates"
];
var SCENE_GROUP_TYPES = ["chain", "protocol", "cluster"];
var SCENE_DETAIL_TIERS = ["compact", "standard", "expanded"];
var SCENE_EMPTY_REASONS = [
  "no_semantic_tree",
  "decluttered_empty",
  "no_flow_source",
  "no_value_moved",
  "new_issuance",
  "shielded",
  "pooled"
];
var SCENE_EMPTY_REASON_ATTR = "empty_reason";
var SCENE_ELEMENT_ORIGINS = ["decoded", "synthesized", "user"];
var MediaRefSchema = z.object({
  kind: z.string().default("placeholder"),
  value: z.string(),
  fallback: z.union([z.string(), z.null()]).default(null),
  alt: z.union([z.string(), z.null()]).default(null)
});
var SceneAssetSchema = z.object({
  token_address: z.union([z.string(), z.null()]).default(null),
  token_chain_id: z.union([z.string(), z.null()]).default(null),
  symbol: z.union([z.string(), z.null()]).default(null),
  decimals: z.union([z.number().int(), z.null()]).default(null),
  amount_raw: z.union([z.string(), z.null()]).default(null),
  amount: z.union([z.string(), z.null()]).default(null),
  usd: z.union([z.string(), z.null()]).default(null),
  direction: z.union([z.string(), z.null()]).default(null),
  // 'actual' | 'quoted_minimum' — mirrors commons AmountBasis. Lets a label
  // flag a swap min-return floor as "(min)" instead of an exact amount.
  basis: z.union([z.string(), z.null()]).default(null),
  min_amount_raw: z.union([z.string(), z.null()]).default(null),
  min_amount: z.union([z.string(), z.null()]).default(null),
  icon: z.union([MediaRefSchema, z.null()]).default(null),
  // The well-known asset this token CLAIMS to be and is not (see
  // `commons.tokens.impersonation`). Its `symbol` stays what it calls itself;
  // a renderer must not show that bare. Absent for an honest token.
  impersonates: z.union([z.record(z.string(), z.unknown()), z.null()]).optional()
});
var SceneSourceRefSchema = z.object({
  ref_type: z.string(),
  address: z.union([z.string(), z.null()]).default(null),
  log_index: z.union([z.number().int(), z.null()]).default(null),
  internal_tx_index: z.union([z.number().int(), z.null()]).default(null),
  path: z.array(z.string()).default([])
});
var SceneFactSchema = z.object({
  key: z.string(),
  label: z.string().default(""),
  kind: z.union([z.string(), z.null()]).default(null),
  value: z.unknown(),
  protocol_id: z.union([z.string(), z.null()]).default(null),
  source: z.union([z.string(), z.null()]).default(null),
  path_id: z.union([z.string(), z.null()]).default(null),
  semantic_node_id: z.union([z.string(), z.null()]).default(null),
  source_refs: z.array(SceneSourceRefSchema).default([]),
  evidence: z.array(EvidenceSchema).default([]),
  confidence: z.union([z.number().min(0).max(1), z.null()]).default(null),
  icon: z.union([z.string(), z.null()]).default(null),
  description: z.union([z.string(), z.null()]).default(null)
});
var SceneNodeSchema = z.object({
  id: z.string(),
  kind: z.string().default("address"),
  label: z.string().default(""),
  sublabel: z.union([z.string(), z.null()]).default(null),
  chain_id: z.union([z.string(), z.null()]).default(null),
  address: z.union([z.string(), z.null()]).default(null),
  group_id: z.union([z.string(), z.null()]).default(null),
  off_chain: z.boolean().default(false),
  entity_id: z.union([z.string(), z.null()]).default(null),
  icon: z.union([MediaRefSchema, z.null()]).default(null),
  assets: z.array(SceneAssetSchema).default([]),
  value: z.union([z.string(), z.null()]).default(null),
  badge: z.union([z.string(), z.null()]).default(null),
  attributes: z.record(z.string(), z.unknown()).default({}),
  source_refs: z.array(SceneSourceRefSchema).default([]),
  evidence: z.array(EvidenceSchema).default([]),
  confidence: z.number().min(0).max(1).default(1),
  detail_tier: z.string().default("compact"),
  facts: z.array(SceneFactSchema).default([]),
  origin: z.string().default("decoded")
});
var SceneEdgeSchema = z.object({
  id: z.string(),
  source: z.string(),
  target: z.string(),
  kind: z.string().default("flow"),
  label: z.union([z.string(), z.null()]).default(null),
  asset: z.union([SceneAssetSchema, z.null()]).default(null),
  direction: z.union([z.string(), z.null()]).default(null),
  from_chain: z.union([z.string(), z.null()]).default(null),
  to_chain: z.union([z.string(), z.null()]).default(null),
  cross_chain: z.boolean().default(false),
  virtual: z.boolean().default(false),
  icon: z.union([MediaRefSchema, z.null()]).default(null),
  attributes: z.record(z.string(), z.unknown()).default({}),
  detail_tier: z.string().default("compact"),
  facts: z.array(SceneFactSchema).default([]),
  origin: z.string().default("decoded")
});
var MERGED_COUNT_ATTR = "merged_count";
var MERGED_TXS_ATTR = "merged_txs";
var MERGED_TXS_LIMIT = 100;
function mergedTxRef(parts) {
  const id = parts.canonicalTxId || parts.txHash;
  if (!id) return void 0;
  return {
    id,
    ...parts.txHash ? { tx_hash: parts.txHash } : {},
    ...parts.chainId ? { chain_id: parts.chainId } : {}
  };
}
function mergedTxRefs(attributes) {
  return parseTxRefs(attributes?.[MERGED_TXS_ATTR]);
}
var TX_MEMBERSHIP_ATTR = "in_txs";
function txMembership(attributes) {
  return parseTxRefs(attributes?.[TX_MEMBERSHIP_ATTR]);
}
function groupSceneLegs(attributes) {
  const raw = attributes?.legs;
  if (!Array.isArray(raw)) return [];
  const legs = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const { id, role, chain_id, tx_hash } = entry;
    if (typeof id !== "string" || !id) continue;
    legs.push({
      id,
      index: legs.length,
      ...typeof role === "string" && role ? { role } : {},
      ...typeof chain_id === "string" && chain_id ? { chain_id } : {},
      ...typeof tx_hash === "string" && tx_hash ? { tx_hash } : {}
    });
  }
  return legs;
}
function parseTxRefs(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = /* @__PURE__ */ new Set();
  const refs = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const { id, tx_hash, chain_id, amount_raw } = entry;
    if (typeof id !== "string" || !id || seen.has(id)) continue;
    seen.add(id);
    refs.push({
      id,
      ...typeof tx_hash === "string" && tx_hash ? { tx_hash } : {},
      ...typeof chain_id === "string" && chain_id ? { chain_id } : {},
      ...typeof amount_raw === "string" && /^-?\d+$/.test(amount_raw) ? { amount_raw } : {}
    });
  }
  return refs;
}
var TX_COUNT_ATTR = "tx_count";
function txCountSublabel(count) {
  return `${count} tx`;
}
function isTxCountSublabel(sublabel) {
  return sublabel == null || sublabel === "" || /^\d+ tx$/.test(sublabel);
}
function mergedCount(attributes) {
  const value = attributes?.[MERGED_COUNT_ATTR];
  return typeof value === "number" && value > 0 ? value : 1;
}
var SceneGroupSchema = z.object({
  id: z.string(),
  group_type: z.string().default("chain"),
  chain_id: z.union([z.string(), z.null()]).default(null),
  label: z.string().default(""),
  member_node_ids: z.array(z.string()).default([]),
  color: z.union([z.string(), z.null()]).default(null),
  icon: z.union([MediaRefSchema, z.null()]).default(null)
});
var GraphSceneSchema = z.object({
  version: z.string().default("1"),
  kind: z.string().default("semantic"),
  summary: z.union([z.string(), z.null()]).default(null),
  nodes: z.array(SceneNodeSchema).default([]),
  edges: z.array(SceneEdgeSchema).default([]),
  groups: z.array(SceneGroupSchema).default([]),
  // The transaction's headline "business metrics" — the SAME resolved
  // `key_attributes` groups the detail-page highlights panel shows, carried on
  // EVERY chart kind so the graph view renders a consistent highlights strip.
  // Kept as the raw snake_case wire shape (a passthrough) so the wire layer
  // stays below `storage`; a consumer normalises it with `parseKeyAttributeGroups`
  // (the same parser the detail panel + group view use) → `KeyAttributeGroup[]`.
  highlights: z.array(z.record(z.string(), z.unknown())).default([]),
  chain_ids: z.array(z.string()).default([]),
  multi_chain: z.boolean().default(false),
  layout_hint: z.string().default("hierarchical"),
  // Compact-first: mirrors the backend `GraphBuildPolicy.default_view_tier`
  // default so a scene missing the field opens on the minimal intent + highlights
  // (the user raises the tier live). The scene always carries every tier.
  default_detail_tier: z.string().default("compact"),
  attributes: z.record(z.string(), z.unknown()).default({})
});
function parseGraphScene(value) {
  return GraphSceneSchema.parse(value);
}
var SceneOverlaySchema = z.object({
  nodes: z.array(SceneNodeSchema).default([]),
  edges: z.array(SceneEdgeSchema).default([]),
  groups: z.array(SceneGroupSchema).default([]),
  hidden_node_ids: z.array(z.string()).default([]),
  hidden_edge_ids: z.array(z.string()).default([]),
  node_patches: z.record(z.string(), z.record(z.string(), z.unknown())).default({}),
  edge_patches: z.record(z.string(), z.record(z.string(), z.unknown())).default({}),
  /**
   * Where the author put an authored node. Honoured as a pin, not a hint, and
   * only for nodes the overlay itself added — pinning a decoded node freezes a
   * layout the backend is entitled to change, and the position was never the
   * author's to give.
   */
  positions: z.record(z.string(), z.object({ x: z.number(), y: z.number() })).default({}),
  /**
   * Who asserted this overlay, when, and why. Stamped by the service from the
   * caller's identity, never read from the element bodies — a client can no more
   * forge an author than it can forge `origin`.
   */
  authored_by: z.union([z.string(), z.null()]).default(null),
  authored_at: z.union([z.string(), z.null()]).default(null),
  authored_reason: z.union([z.string(), z.null()]).default(null),
  /**
   * The tier this overlay was published at, stamped by the read.
   *
   * On the BODY rather than left on the row, because the body is all a fold
   * receives — and without it a fold cannot tell one overlay overriding a lower
   * rung (precedence working) from two peers at the same rung contradicting each
   * other. Written by `overlay_wire` on the API and by the tx_graph store.
   */
  authored_scope: z.union([z.string(), z.null()]).default(null)
});
var AUTHORED_BY_ATTR = "authored_by";
var AUTHORED_AT_ATTR = "authored_at";
var AUTHORED_REASON_ATTR = "authored_reason";
var AUTHORED_SCOPE_ATTR = "authored_scope";
var CONTESTED_ATTR = "contested";
function emptySceneOverlay() {
  return {
    nodes: [],
    edges: [],
    groups: [],
    hidden_node_ids: [],
    hidden_edge_ids: [],
    node_patches: {},
    edge_patches: {},
    positions: {},
    authored_by: null,
    authored_at: null,
    authored_reason: null,
    authored_scope: null
  };
}
var SUBJECT_ANY = "*";
var OVERLAY_BODY_KEYS = /* @__PURE__ */ new Set([
  "nodes",
  "edges",
  "groups",
  "hidden_node_ids",
  "hidden_edge_ids",
  "node_patches",
  "edge_patches",
  "positions",
  AUTHORED_BY_ATTR,
  AUTHORED_AT_ATTR,
  AUTHORED_REASON_ATTR,
  AUTHORED_SCOPE_ATTR
]);
function isSceneOverlayBody(value) {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  return Object.keys(value).some((key) => OVERLAY_BODY_KEYS.has(key));
}
function annotationLayers(annotations, subjectKey) {
  const read = (value) => {
    try {
      return parseSceneOverlay(value);
    } catch {
      return emptySceneOverlay();
    }
  };
  if (isSceneOverlayBody(annotations)) return { [subjectKey]: read(annotations) };
  if (typeof annotations !== "object" || annotations === null) return {};
  const out = {};
  for (const [key, value] of Object.entries(annotations)) {
    if (typeof value === "object" && value !== null) out[key] = read(value);
  }
  return out;
}
function mergeAnnotationLayer(annotations, subjectKey, overlay) {
  return { ...annotationLayers(annotations, subjectKey), [subjectKey]: overlay };
}
function layerForSubject(annotations, subjectKey) {
  return annotationLayers(annotations, subjectKey)[subjectKey] ?? emptySceneOverlay();
}
function parseSceneOverlay(value) {
  return SceneOverlaySchema.parse(value);
}
var GROUPING_DIMENSIONS = [
  "hop",
  "chain",
  "protocol",
  "entity",
  "tag",
  "label",
  "kind",
  "saga",
  "transaction"
];
var GroupingDimensionOptionsSchema = z.object({
  /** Drop a band with fewer members than this (a band of one is just a node). */
  min_members: z.number().int().positive().optional(),
  /** Keep at most this many bands (largest first); the rest go ungrouped. */
  max_bands: z.number().int().positive().optional()
});
var GroupingSpecSchema = z.object({
  dimensions: z.array(z.enum(GROUPING_DIMENSIONS)).default([]),
  options: z.record(z.string(), GroupingDimensionOptionsSchema).default({})
});
function defaultGroupingSpec() {
  return { dimensions: ["chain"] };
}
function parseGroupingSpec(value) {
  if (!value || typeof value !== "object") return null;
  const raw = value;
  if (!Array.isArray(raw.dimensions)) return null;
  const known = new Set(GROUPING_DIMENSIONS);
  const seen = /* @__PURE__ */ new Set();
  const dimensions = [];
  for (const entry of raw.dimensions) {
    if (typeof entry !== "string" || !known.has(entry) || seen.has(entry)) continue;
    seen.add(entry);
    dimensions.push(entry);
  }
  const options = {};
  if (raw.options && typeof raw.options === "object") {
    for (const [key, opts] of Object.entries(raw.options)) {
      if (!known.has(key)) continue;
      const parsed = GroupingDimensionOptionsSchema.safeParse(opts);
      if (parsed.success) options[key] = parsed.data;
    }
  }
  return Object.keys(options).length > 0 ? { dimensions, options } : { dimensions };
}

// src/logging/index.ts
var LEVEL_WEIGHT = { debug: 10, info: 20, warn: 30, error: 40 };
var DEBUG_FLAG_KEY = "authe:debug";
function storage() {
  try {
    const s = typeof localStorage !== "undefined" ? localStorage : void 0;
    return s && typeof s.getItem === "function" ? s : void 0;
  } catch {
    return void 0;
  }
}
function isDebugEnabled() {
  try {
    if (typeof location !== "undefined") {
      const param = new URLSearchParams(location.search).get("debug");
      if (param === "1") {
        enableDebug();
        return true;
      }
      if (param === "0") {
        disableDebug();
        return false;
      }
    }
  } catch {
  }
  return storage()?.getItem(DEBUG_FLAG_KEY) === "1";
}
function enableDebug() {
  storage()?.setItem(DEBUG_FLAG_KEY, "1");
}
function disableDebug() {
  storage()?.removeItem(DEBUG_FLAG_KEY);
}
var logging = globalSingleton("logging/state", () => ({
  sinks: [],
  /** Minimum level that reaches the console (debug-flag lowers it to `debug`). */
  minConsoleLevel: "warn"
}));
function createLogger(namespace) {
  function emit(level, message, context) {
    const record = { level, namespace, message, context, timestamp: Date.now() };
    for (const sink of logging.sinks) {
      try {
        sink(record);
      } catch {
      }
    }
    const threshold = isDebugEnabled() ? LEVEL_WEIGHT.debug : LEVEL_WEIGHT[logging.minConsoleLevel];
    if (LEVEL_WEIGHT[level] < threshold) return;
    const prefix = `[${namespace}]`;
    const fn = level === "debug" ? console.debug : level === "info" ? console.info : level === "warn" ? console.warn : console.error;
    if (context) fn(prefix, message, context);
    else fn(prefix, message);
  }
  return {
    debug: (m, c) => emit("debug", m, c),
    info: (m, c) => emit("info", m, c),
    warn: (m, c) => emit("warn", m, c),
    error: (m, c) => emit("error", m, c),
    child: (suffix) => createLogger(`${namespace}:${suffix}`)
  };
}

// src/chains/family.ts
var ChainFamily = {
  EVM: "evm",
  UTXO: "utxo",
  SOLANA: "solana",
  COSMOS: "cosmos",
  /** Hyperliquid's L1 (HyperCore): one signed action per transaction, EVM-shaped identifiers. */
  HYPERCORE: "hypercore",
  XRPL: "xrpl",
  TRON: "tron",
  NEAR: "near",
  OTHER: "other"
};
function isChainFamily(value) {
  return typeof value === "string" && Object.values(ChainFamily).includes(value);
}

// src/chains/catalogue.generated.json
var catalogue_generated_default = {
  chains: [
    {
      aliases: [
        "ape-chain",
        "apechain-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 0.9,
      brand_color: null,
      caip2: "eip155:33139",
      chain_id_hex: "0x8173",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://apescan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "ApeChain",
      native_token: "APE",
      providers: {
        blockscout: "https://apechain.calderaexplorer.xyz/api",
        blockscout_v2: "https://apechain.calderaexplorer.xyz/api/v2",
        coingecko: "apechain",
        coingecko_native: "apecoin",
        defillama: "apechain",
        dexscreener: "apechain",
        evm_rpc: "discovery",
        sourcify: "33139"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.apechain.com/http"
      ],
      slug: "apechain",
      status: "active",
      wrapped_native_address: "0x48b62137edfa95a428d35c09e44256a739f6b557"
    },
    {
      aliases: [
        "curtis",
        "apechain-curtis",
        "apechain-testnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 0.9,
      brand_color: null,
      caip2: "eip155:33111",
      chain_id_hex: "0x8157",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.curtis.apechain.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:33139",
      metadata: {},
      name: "ApeChain Curtis",
      native_token: "APE",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.curtis.apechain.com"
      ],
      slug: "apechain_curtis",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "arbitrum-one",
        "arb",
        "arbitrum-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 0.25,
      brand_color: "#28A0F0",
      caip2: "eip155:42161",
      chain_id_hex: "0xa4b1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://arbiscan.io",
      family: "evm",
      finality_blocks: 32,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Arbitrum",
      native_token: "ETH",
      providers: {
        alchemy: "arb-mainnet",
        ankr: "arbitrum",
        arkham: "arbitrum_one",
        blockchair: "arbitrum",
        blockscan: "42161",
        blockscout: "https://arbitrum.blockscout.com/api",
        blocksec: "42161",
        chainflip: "4",
        codeslaw: "arbitrum",
        coingecko: "arbitrum-one",
        coingecko_native: "ethereum",
        defillama: "arbitrum",
        dexscreener: "arbitrum",
        etherscan_scan: "arbiscan.io",
        infura: "arbitrum-mainnet",
        metasleuth: "arbitrum",
        moralis: "arbitrum",
        moralis_wallet: "arbitrum",
        sourcify: "42161",
        thorchain: "ARB"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://arb1.arbitrum.io/rpc",
        "https://arbitrum-one-rpc.publicnode.com"
      ],
      slug: "arbitrum",
      status: "active",
      wrapped_native_address: "0x82af49447d8a07e3bd95bd0d56f35241523fbab1"
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 0.25,
      brand_color: "#28A0F0",
      caip2: "eip155:421614",
      chain_id_hex: "0x66eee",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.arbiscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:42161",
      metadata: {},
      name: "Arbitrum Sepolia",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "arbitrum_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#70D44B",
      caip2: "eip155:1313161554",
      chain_id_hex: "0x4e454152",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.aurora.dev",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "sidechain",
      mainnet: null,
      metadata: {},
      name: "Aurora",
      native_token: "ETH",
      providers: {
        coingecko_native: "ethereum",
        defillama: "aurora",
        dexscreener: "aurora"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "aurora",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#70D44B",
      caip2: "eip155:1313161555",
      chain_id_hex: "0x4e454153",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.testnet.aurora.dev",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "sidechain",
      mainnet: "eip155:1313161554",
      metadata: {},
      name: "Aurora Testnet",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "aurora_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "avax",
        "avalanche-c-chain",
        "avax-c-chain",
        "avalanche-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#E84142",
      caip2: "eip155:43114",
      chain_id_hex: "0xa86a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://snowtrace.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Avalanche",
      native_token: "AVAX",
      providers: {
        alchemy: "avax-mainnet",
        ankr: "avalanche",
        arkham: "avalanche",
        blockchair: "avalanche",
        blockscan: "43114",
        blocksec: "43114",
        coingecko: "avalanche",
        coingecko_native: "avalanche-2",
        defillama: "avax",
        dexscreener: "avalanche",
        etherscan_scan: "snowtrace.io",
        infura: "avalanche-mainnet",
        metasleuth: "avalanche",
        moralis: "avalanche",
        moralis_wallet: "avalanche",
        sourcify: "43114",
        thorchain: "AVAX"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api.avax.network/ext/bc/C/rpc",
        "https://avalanche-c-chain-rpc.publicnode.com"
      ],
      slug: "avalanche",
      status: "active",
      wrapped_native_address: "0xb31f66aa3c1e785363f0875a1b74e27b85fd66c7"
    },
    {
      aliases: [
        "base-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#0052FF",
      caip2: "eip155:8453",
      chain_id_hex: "0x2105",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://basescan.org",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Base",
      native_token: "ETH",
      providers: {
        alchemy: "base-mainnet",
        ankr: "base",
        arkham: "base",
        blockchair: "base",
        blockscan: "8453",
        blockscout: "https://base.blockscout.com/api",
        codeslaw: "base",
        coingecko: "base",
        coingecko_native: "ethereum",
        defillama: "base",
        dexscreener: "base",
        etherscan_scan: "basescan.org",
        infura: "base-mainnet",
        metasleuth: "base",
        moralis: "base",
        moralis_wallet: "base",
        sourcify: "8453",
        thorchain: "BASE"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.base.org",
        "https://base-rpc.publicnode.com"
      ],
      slug: "base",
      status: "active",
      wrapped_native_address: "0x4200000000000000000000000000000000000006"
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#0052FF",
      caip2: "eip155:84531",
      chain_id_hex: "0x14a33",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:8453",
      metadata: {},
      name: "Base Goerli",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "base_goerli",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#0052FF",
      caip2: "eip155:84532",
      chain_id_hex: "0x14a34",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.basescan.org",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:8453",
      metadata: {},
      name: "Base Sepolia",
      native_token: "ETH",
      providers: {
        moralis: "base_sepolia"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "base_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "bitcoin-mainnet",
        "0x0",
        "btc"
      ],
      bech32_hrp: null,
      block_time_seconds: 600,
      brand_color: "#F7931A",
      caip2: "bip122:000000000019d6689c085ae165831e93",
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {},
      explorer_url: "https://blockstream.info",
      family: "utxo",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Bitcoin",
      native_token: "BTC",
      providers: {
        blockchair: "bitcoin",
        chainflip: "3",
        coingecko_native: "bitcoin",
        dexscreener: "bitcoin",
        esplora: "mainnet",
        scanscope: "bitcoin",
        thorchain: "BTC"
      },
      reorg_depth: 6,
      rpc_urls: [],
      slug: "bitcoin",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 600,
      brand_color: "#F7931A",
      caip2: "bip122:000000000933ea01ad0ee984209779ba",
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {},
      explorer_url: null,
      family: "utxo",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "bip122:000000000019d6689c085ae165831e93",
      metadata: {},
      name: "Bitcoin Testnet",
      native_token: "BTC",
      providers: {
        esplora: "testnet"
      },
      reorg_depth: 6,
      rpc_urls: [],
      slug: "bitcoin-testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "blast-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#FCFC03",
      caip2: "eip155:81457",
      chain_id_hex: "0x13e31",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://blastscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Blast",
      native_token: "ETH",
      providers: {
        arkham: "blast",
        codeslaw: "blast",
        coingecko_native: "ethereum",
        defillama: "blast",
        dexscreener: "blast",
        sourcify: "81457"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://blast-rpc.publicnode.com"
      ],
      slug: "blast",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#FCFC03",
      caip2: "eip155:168587773",
      chain_id_hex: "0xa0c71fd",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.blastscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:81457",
      metadata: {},
      name: "Blast Sepolia",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "blast_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "binance",
        "binance-smart-chain",
        "bnb",
        "bnb-chain",
        "bnb-smart-chain"
      ],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#F0B90B",
      caip2: "eip155:56",
      chain_id_hex: "0x38",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://bscscan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "BNB Chain",
      native_token: "BNB",
      providers: {
        alchemy: "bnb-mainnet",
        ankr: "bsc",
        arkham: "bsc",
        blockchair: "binance-smart-chain",
        blockscan: "56",
        blocksec: "56",
        chainflip: "8",
        codeslaw: "bnbchain",
        coingecko: "binance-smart-chain",
        coingecko_native: "binancecoin",
        defillama: "bsc",
        dexscreener: "bsc",
        etherscan_scan: "bscscan.com",
        infura: "bsc-mainnet",
        metasleuth: "bsc",
        moralis: "bsc",
        moralis_wallet: "bsc",
        sourcify: "56",
        thorchain: "BSC"
      },
      reorg_depth: 15,
      rpc_urls: [
        "https://bsc-dataseed1.binance.org",
        "https://bsc-rpc.publicnode.com"
      ],
      slug: "bsc",
      status: "active",
      wrapped_native_address: "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c"
    },
    {
      aliases: [
        "bnb-testnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#F0B90B",
      caip2: "eip155:97",
      chain_id_hex: "0x61",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.bscscan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:56",
      metadata: {},
      name: "BSC Testnet",
      native_token: "tBNB",
      providers: {
        blockchair: "bsc-testnet",
        infura: "bsc-testnet",
        moralis: "bsc_testnet"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "bsc_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#FCFF52",
      caip2: "eip155:42220",
      chain_id_hex: "0xa4ec",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://celoscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Celo",
      native_token: "CELO",
      providers: {
        coingecko_native: "celo",
        defillama: "celo",
        dexscreener: "celo"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://celo-rpc.publicnode.com"
      ],
      slug: "celo",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#FCFF52",
      caip2: "eip155:44787",
      chain_id_hex: "0xaef3",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:42220",
      metadata: {},
      name: "Celo Alfajores",
      native_token: "CELO",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "celo_alfajores",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:88888",
      chain_id_hex: "0x15b38",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Chiliz",
      native_token: "CHZ",
      providers: {
        coingecko_native: "chiliz",
        moralis: "chiliz",
        moralis_wallet: "chiliz"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "chiliz",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:88882",
      chain_id_hex: "0x15b32",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "eip155:88888",
      metadata: {},
      name: "Chiliz Testnet",
      native_token: "CHZ",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "chiliz_spicy",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "cosmoshub-mainnet",
        "cosmoshub-4",
        "gaia"
      ],
      bech32_hrp: "cosmos",
      block_time_seconds: 5.5,
      brand_color: "#2E3148",
      caip2: "cosmos:cosmoshub-4",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        token: null
      },
      explorer_url: "https://www.mintscan.io/cosmos",
      family: "cosmos",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Cosmos Hub",
      native_token: "ATOM",
      providers: {
        coingecko_native: "cosmos",
        cosmos_directory: "https://rest.cosmos.directory/cosmoshub",
        cosmos_lcd: "https://cosmos-rest.publicnode.com",
        thorchain: "GAIA"
      },
      reorg_depth: 1,
      rpc_urls: [],
      slug: "cosmoshub",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "cro"
      ],
      bech32_hrp: null,
      block_time_seconds: 6,
      brand_color: "#002D74",
      caip2: "eip155:25",
      chain_id_hex: "0x19",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://cronoscan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "sidechain",
      mainnet: null,
      metadata: {},
      name: "Cronos",
      native_token: "CRO",
      providers: {
        arkham: "cronos",
        coingecko_native: "crypto-com-chain",
        defillama: "cronos",
        dexscreener: "cronos",
        moralis: "cronos",
        moralis_wallet: "cronos"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "cronos",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 13,
      brand_color: "#328332",
      caip2: "eip155:61",
      chain_id_hex: "0x3d",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://blockscout.com/etc/mainnet",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {
        note: "ETC shares pre-fork tx hashes with ETH. Audit sec. 2 calls out this collision risk."
      },
      name: "Ethereum Classic",
      native_token: "ETC",
      providers: {
        blockscout: "https://etc.blockscout.com/api",
        coingecko_native: "ethereum-classic"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://etc.etcdesktop.com"
      ],
      slug: "etc",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "ethereum",
        "ethereum-mainnet",
        "eth-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#627EEA",
      caip2: "eip155:1",
      chain_id_hex: "0x1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://etherscan.io",
      family: "evm",
      finality_blocks: 96,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Ethereum",
      native_token: "ETH",
      providers: {
        alchemy: "eth-mainnet",
        ankr: "eth",
        arkham: "ethereum",
        blockchair: "ethereum",
        blockscan: "1",
        blockscout: "https://eth.blockscout.com/api",
        blocksec: "1",
        chainflip: "1",
        codeslaw: "ethereum",
        coingecko: "ethereum",
        coingecko_native: "ethereum",
        defillama: "ethereum",
        dexscreener: "ethereum",
        etherscan_scan: "etherscan.io",
        infura: "mainnet",
        metasleuth: "eth",
        moralis: "eth",
        moralis_wallet: "eth",
        scanscope: "eth",
        sourcify: "1",
        thorchain: "ETH"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://ethereum-rpc.publicnode.com",
        "https://eth.drpc.org",
        "https://mainnet.gateway.tenderly.co"
      ],
      slug: "eth",
      status: "active",
      wrapped_native_address: "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2"
    },
    {
      aliases: [
        "ftm",
        "fantom-opera"
      ],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#1969FF",
      caip2: "eip155:250",
      chain_id_hex: "0xfa",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://ftmscan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Fantom",
      native_token: "FTM",
      providers: {
        blockchair: "fantom",
        blockscan: "250",
        blocksec: "250",
        coingecko: "fantom",
        coingecko_native: "fantom",
        defillama: "fantom",
        dexscreener: "fantom",
        etherscan_scan: "ftmscan.com",
        metasleuth: "fantom",
        sourcify: "250"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpcapi.fantom.network"
      ],
      slug: "fantom",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#1969FF",
      caip2: "eip155:4002",
      chain_id_hex: "0xfa2",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:250",
      metadata: {},
      name: "Fantom Testnet",
      native_token: "FTM",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "fantom_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:747",
      chain_id_hex: "0x2eb",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://evm.flowscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Flow",
      native_token: "FLOW",
      providers: {
        coingecko_native: "flow",
        moralis: "flow",
        moralis_wallet: "flow"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "flow",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:545",
      chain_id_hex: "0x221",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.evm.flow.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:747",
      metadata: {},
      name: "Flow Testnet",
      native_token: "FLOW",
      providers: {
        moralis: "flow_testnet"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "flow_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#B4F9BA",
      caip2: "eip155:122",
      chain_id_hex: "0x7a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Fuse",
      native_token: "FUSE",
      providers: {
        coingecko_native: "fuse-network-token"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "fuse",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#B4F9BA",
      caip2: "eip155:123",
      chain_id_hex: "0x7b",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.fusespark.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:122",
      metadata: {},
      name: "Fuse Sparknet",
      native_token: "SPARK",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "fuse_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "xdai",
        "gnosis-chain"
      ],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#0B6E4F",
      caip2: "eip155:100",
      chain_id_hex: "0x64",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://gnosisscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "sidechain",
      mainnet: null,
      metadata: {},
      name: "Gnosis",
      native_token: "xDAI",
      providers: {
        ankr: "gnosis",
        arkham: "gnosis",
        blockchair: "gnosis",
        blockscan: "100",
        coingecko_native: "xdai",
        defillama: "xdai",
        dexscreener: "gnosis",
        moralis: "gnosis",
        moralis_wallet: "gnosis"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.gnosischain.com"
      ],
      slug: "gnosis",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#0B6E4F",
      caip2: "eip155:10200",
      chain_id_hex: "0x27d8",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://gnosis-chiado.blockscout.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "sidechain",
      mainnet: "eip155:100",
      metadata: {},
      name: "Gnosis Chiado",
      native_token: "xDAI",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "gnosis_chiado",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#627EEA",
      caip2: "eip155:5",
      chain_id_hex: "0x5",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://goerli.etherscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:1",
      metadata: {},
      name: "Goerli",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "goerli",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:325",
      chain_id_hex: "0x145",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "GRVT",
      native_token: "GBT",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "grvt",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#00AEE9",
      caip2: "eip155:1666600000",
      chain_id_hex: "0x63564c40",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Harmony",
      native_token: "ONE",
      providers: {
        coingecko_native: "harmony",
        defillama: "harmony",
        dexscreener: "harmony"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "harmony",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#00AEE9",
      caip2: "eip155:1666700000",
      chain_id_hex: "0x6357d2e0",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:1666600000",
      metadata: {},
      name: "Harmony Testnet",
      native_token: "ONE",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "harmony_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#627EEA",
      caip2: "eip155:17000",
      chain_id_hex: "0x4268",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:1",
      metadata: {},
      name: "Holesky",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "holesky",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [
        "hypercore-mainnet",
        "hyperliquid",
        "hyperliquid-core",
        "hyperliquid-l1"
      ],
      bech32_hrp: null,
      block_time_seconds: 0.2,
      brand_color: "#97FCE4",
      caip2: "hyperliq:mainnet",
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {
        token: null
      },
      explorer_url: "https://app.hyperliquid.xyz/explorer",
      family: "hypercore",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "HyperCore",
      native_token: "HYPE",
      providers: {
        coingecko_native: "hyperliquid",
        hyperliquid: "mainnet"
      },
      reorg_depth: 1,
      rpc_urls: [],
      slug: "hypercore",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "hyperliquid-testnet",
        "hypercore_testnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 0.2,
      brand_color: "#97FCE4",
      caip2: "hyperliq:testnet",
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {
        token: null
      },
      explorer_url: "https://app.hyperliquid-testnet.xyz/explorer",
      family: "hypercore",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "hyperliq:mainnet",
      metadata: {},
      name: "HyperCore Testnet",
      native_token: "HYPE",
      providers: {
        hyperliquid: "testnet"
      },
      reorg_depth: 1,
      rpc_urls: [],
      slug: "hypercore-testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "hyper-evm",
        "hyperliquid-evm"
      ],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#97FCE4",
      caip2: "eip155:999",
      chain_id_hex: "0x3e7",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://hyperevmscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "HyperEVM",
      native_token: "HYPE",
      providers: {
        coingecko_native: "hyperliquid",
        evm_rpc: "ingest",
        sourcify: "999"
      },
      reorg_depth: 1,
      rpc_urls: [
        "https://rpc.hyperliquid.xyz/evm"
      ],
      slug: "hyperevm",
      status: "active",
      wrapped_native_address: "0x5555555555555555555555555555555555555555"
    },
    {
      aliases: [
        "hyperevm-testnet",
        "hyperliquid-evm-testnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#97FCE4",
      caip2: "eip155:998",
      chain_id_hex: "0x3e6",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:999",
      metadata: {},
      name: "HyperEVM Testnet",
      native_token: "HYPE",
      providers: {
        evm_rpc: "ingest",
        sourcify: "998"
      },
      reorg_depth: 1,
      rpc_urls: [
        "https://rpc.hyperliquid-testnet.xyz/evm"
      ],
      slug: "hyperevm_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:57073",
      chain_id_hex: "0xdef1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.inkonchain.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Ink",
      native_token: "ETH",
      providers: {
        blockscout_v2: "https://explorer.inkonchain.com/api/v2",
        coingecko_native: "ethereum",
        sourcify: "57073"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "ink",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: "#FF433E",
      caip2: "eip155:2222",
      chain_id_hex: "0x8ae",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Kava",
      native_token: "KAVA",
      providers: {
        coingecko_native: "kava",
        defillama: "kava"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "kava",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: "#FF433E",
      caip2: "eip155:2221",
      chain_id_hex: "0x8ad",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:2222",
      metadata: {},
      name: "Kava Testnet",
      native_token: "TKAVA",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "kava_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#FF1B1B",
      caip2: "eip155:8217",
      chain_id_hex: "0x2019",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Klaytn",
      native_token: "KLAY",
      providers: {
        coingecko_native: "kaia",
        defillama: "klaytn"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "klaytn",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#FF1B1B",
      caip2: "eip155:1001",
      chain_id_hex: "0x3e9",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:8217",
      metadata: {},
      name: "Klaytn Baobab",
      native_token: "KLAY",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "klaytn_baobab",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "linea-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#61DFFF",
      caip2: "eip155:59144",
      chain_id_hex: "0xe708",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://lineascan.build",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Linea",
      native_token: "ETH",
      providers: {
        arkham: "linea",
        coingecko_native: "ethereum",
        defillama: "linea",
        dexscreener: "linea",
        etherscan_scan: "lineascan.build",
        moralis: "linea",
        moralis_wallet: "linea",
        sourcify: "59144"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.linea.build"
      ],
      slug: "linea",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#61DFFF",
      caip2: "eip155:59141",
      chain_id_hex: "0xe705",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.lineascan.build",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:59144",
      metadata: {},
      name: "Linea Sepolia",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "linea_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#61DFFF",
      caip2: "eip155:59140",
      chain_id_hex: "0xe704",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:59144",
      metadata: {},
      name: "Linea Goerli",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "linea_testnet",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1135",
      chain_id_hex: "0x46f",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://blockscout.lisk.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Lisk",
      native_token: "LSK",
      providers: {
        coingecko_native: "lisk",
        moralis: "lisk",
        moralis_wallet: "lisk"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "lisk",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:4202",
      chain_id_hex: "0x106a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:1135",
      metadata: {},
      name: "Lisk Sepolia",
      native_token: "LSK",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "lisk_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#000000",
      caip2: "eip155:5000",
      chain_id_hex: "0x1388",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.mantle.xyz",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Mantle",
      native_token: "MNT",
      providers: {
        arkham: "mantle",
        coingecko_native: "mantle",
        defillama: "mantle",
        dexscreener: "mantle"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.mantle.xyz"
      ],
      slug: "mantle",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "mantle-testnet",
        "mantle_testnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#000000",
      caip2: "eip155:5003",
      chain_id_hex: "0x138b",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.mantlescan.xyz",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:5000",
      metadata: {},
      name: "Mantle Sepolia",
      native_token: "MNT",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "mantle_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 4,
      brand_color: "#00DACC",
      caip2: "eip155:1088",
      chain_id_hex: "0x440",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Metis",
      native_token: "METIS",
      providers: {
        coingecko_native: "metis-token",
        defillama: "metis"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "metis",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 4,
      brand_color: "#00DACC",
      caip2: "eip155:588",
      chain_id_hex: "0x24c",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:1088",
      metadata: {},
      name: "Metis Goerli",
      native_token: "METIS",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "metis_goerli",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:143",
      chain_id_hex: "0x8f",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://monadexplorer.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Monad",
      native_token: "MON",
      providers: {
        coingecko_native: "monad",
        moralis: "monad",
        moralis_wallet: "monad"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "monad",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "monero-mainnet",
        "xmr"
      ],
      bech32_hrp: null,
      block_time_seconds: 120,
      brand_color: "#FF6600",
      caip2: "monero:418015bb9ae982a1975da7d79277c270",
      chain_id_hex: null,
      decimals: 12,
      explorer_paths: {
        address: null,
        token: null
      },
      explorer_url: "https://xmrchain.net",
      family: "utxo",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Monero",
      native_token: "XMR",
      providers: {
        blockchair: "monero",
        blockchair_api: "monero",
        coingecko_native: "monero",
        xmrchain: "mainnet"
      },
      reorg_depth: 10,
      rpc_urls: [],
      slug: "monero",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#53CBC8",
      caip2: "eip155:1287",
      chain_id_hex: "0x507",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://moonbase.moonscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:1284",
      metadata: {},
      name: "Moonbase",
      native_token: "DEV",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "moonbase",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#53CBC8",
      caip2: "eip155:1284",
      chain_id_hex: "0x504",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://moonscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Moonbeam",
      native_token: "GLMR",
      providers: {
        coingecko_native: "moonbeam",
        defillama: "moonbeam",
        dexscreener: "moonbeam",
        metasleuth: "moonbeam",
        moralis: "moonbeam",
        moralis_wallet: "moonbeam"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "moonbeam",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#C0C0C0",
      caip2: "eip155:1285",
      chain_id_hex: "0x505",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Moonriver",
      native_token: "MOVR",
      providers: {
        coingecko_native: "moonriver",
        defillama: "moonriver",
        dexscreener: "moonriver",
        moralis: "moonriver"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "moonriver",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#8247E5",
      caip2: "eip155:80001",
      chain_id_hex: "0x13881",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:137",
      metadata: {},
      name: "Polygon Mumbai",
      native_token: "MATIC",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "mumbai",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [
        "near-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "near:mainnet",
      chain_id_hex: null,
      decimals: 24,
      explorer_paths: {
        address: "address",
        block: "blocks",
        token: null,
        tx: "txns"
      },
      explorer_url: "https://nearblocks.io",
      family: "near",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "NEAR",
      native_token: "NEAR",
      providers: {
        coingecko_native: "near",
        near_rpc: "https://archival-rpc.mainnet.near.org"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.mainnet.fastnear.com",
        "https://archival-rpc.mainnet.near.org"
      ],
      slug: "near",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "near:testnet",
      chain_id_hex: null,
      decimals: 24,
      explorer_paths: {
        address: "address",
        block: "blocks",
        token: null,
        tx: "txns"
      },
      explorer_url: "https://testnet.nearblocks.io",
      family: "near",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "near:mainnet",
      metadata: {},
      name: "NEAR Testnet",
      native_token: "NEAR",
      providers: {
        near_rpc: "https://archival-rpc.testnet.near.org"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.testnet.near.org"
      ],
      slug: "near-testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:41443",
      chain_id_hex: "0xa1e3",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer-evm.nesa.ai",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Nesa",
      native_token: "NES",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "nesa",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:204",
      chain_id_hex: "0xcc",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://opbnbscan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "opBNB",
      native_token: "BNB",
      providers: {
        coingecko_native: "binancecoin",
        defillama: "op_bnb"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "opbnb",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "op",
        "op-mainnet",
        "optimism-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#FF0420",
      caip2: "eip155:10",
      chain_id_hex: "0xa",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://optimistic.etherscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Optimism",
      native_token: "ETH",
      providers: {
        alchemy: "opt-mainnet",
        arkham: "optimism",
        blockchair: "optimism",
        blockscan: "10",
        blockscout: "https://explorer.optimism.io/api",
        blocksec: "10",
        codeslaw: "optimism",
        coingecko: "optimistic-ethereum",
        coingecko_native: "ethereum",
        defillama: "optimism",
        dexscreener: "optimism",
        etherscan_scan: "optimistic.etherscan.io",
        infura: "optimism-mainnet",
        metasleuth: "optimism",
        moralis: "optimism",
        moralis_wallet: "optimism",
        sourcify: "10"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.optimism.io",
        "https://optimism-rpc.publicnode.com"
      ],
      slug: "optimism",
      status: "active",
      wrapped_native_address: "0x4200000000000000000000000000000000000006"
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#FF0420",
      caip2: "eip155:420",
      chain_id_hex: "0x1a4",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:10",
      metadata: {},
      name: "Optimism Goerli",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "optimism_goerli",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#FF0420",
      caip2: "eip155:11155420",
      chain_id_hex: "0xaa37dc",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia-optimism.etherscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:10",
      metadata: {},
      name: "Optimism Sepolia",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "optimism_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "osmosis-mainnet",
        "osmosis-1"
      ],
      bech32_hrp: "osmo",
      block_time_seconds: 1.6,
      brand_color: "#750BBB",
      caip2: "cosmos:osmosis-1",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        token: null
      },
      explorer_url: "https://www.mintscan.io/osmosis",
      family: "cosmos",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Osmosis",
      native_token: "OSMO",
      providers: {
        coingecko_native: "osmosis",
        cosmos_directory: "https://rest.cosmos.directory/osmosis",
        cosmos_lcd: "https://osmosis-rest.publicnode.com"
      },
      reorg_depth: 1,
      rpc_urls: [],
      slug: "osmosis",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:11297108109",
      chain_id_hex: "0x2a15c308d",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "sidechain",
      mainnet: null,
      metadata: {},
      name: "Palm",
      native_token: "PALM",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "palm",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:3338",
      chain_id_hex: "0xd0a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://peaq.subscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "peaq",
      native_token: "PEAQ",
      providers: {
        coingecko_native: "peaq-2"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "peaq",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:9745",
      chain_id_hex: "0x2611",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://plasmascan.to",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Plasma",
      native_token: "XPL",
      providers: {
        coingecko_native: "plasma"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "plasma",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "polkadot-mainnet",
        "0x162",
        "dot"
      ],
      bech32_hrp: null,
      block_time_seconds: 6,
      brand_color: "#E6007A",
      caip2: "polkadot:91b171bb158e2d3848fa23a9f1c25182",
      chain_id_hex: null,
      decimals: 10,
      explorer_paths: {},
      explorer_url: "https://polkadot.subscan.io",
      family: "other",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Polkadot",
      native_token: "DOT",
      providers: {
        chainflip: "2",
        coingecko_native: "polkadot"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "polkadot",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "matic",
        "matic-network",
        "polygon-mainnet",
        "polygon-pos"
      ],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#8247E5",
      caip2: "eip155:137",
      chain_id_hex: "0x89",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://polygonscan.com",
      family: "evm",
      finality_blocks: 512,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Polygon",
      native_token: "POL",
      providers: {
        alchemy: "polygon-mainnet",
        arkham: "polygon",
        blockchair: "polygon",
        blockscan: "137",
        blockscout: "https://polygon.blockscout.com/api",
        blocksec: "137",
        codeslaw: "polygon",
        coingecko: "polygon-pos",
        coingecko_native: "polygon-ecosystem-token",
        defillama: "polygon",
        dexscreener: "polygon",
        etherscan_scan: "polygonscan.com",
        infura: "polygon-mainnet",
        metasleuth: "polygon",
        moralis: "polygon",
        moralis_wallet: "polygon",
        sourcify: "137"
      },
      reorg_depth: 256,
      rpc_urls: [
        "https://polygon-bor-rpc.publicnode.com",
        "https://polygon.drpc.org",
        "https://polygon.gateway.tenderly.co"
      ],
      slug: "polygon",
      status: "active",
      wrapped_native_address: "0x0d500b1d8e8ef31e21c99d1db9a6444d3adf1270"
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#8247E5",
      caip2: "eip155:80002",
      chain_id_hex: "0x13882",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://amoy.polygonscan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:137",
      metadata: {},
      name: "Polygon Amoy",
      native_token: "MATIC",
      providers: {
        moralis: "polygon_amoy"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "polygon_amoy",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "polygon-zkevm",
        "zkevm"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1101",
      chain_id_hex: "0x44d",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://zkevm.polygonscan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Polygon zkEVM",
      native_token: "ETH",
      providers: {
        arkham: "polygon_zkevm",
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "polygon_zkevm",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2442",
      chain_id_hex: "0x98a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:1101",
      metadata: {},
      name: "Polygon zkEVM Cardona",
      native_token: "MATIC",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "polygon_zkevm_cardona",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "pls"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:369",
      chain_id_hex: "0x171",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://scan.pulsechain.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "PulseChain",
      native_token: "PLS",
      providers: {
        arkham: "pulsechain",
        coingecko_native: "pulsechain",
        moralis: "pulsechain",
        moralis_wallet: "pulsechain"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "pulsechain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:4663",
      chain_id_hex: "0x1237",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://robinhoodchain.blockscout.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Robinhood Chain",
      native_token: "ETH",
      providers: {
        blockscout_v2: "https://robinhoodchain.blockscout.com/api/v2",
        coingecko: "robinhood",
        coingecko_native: "ethereum",
        defillama: "robinhood",
        evm_rpc: "discovery",
        moralis: "robinhood",
        moralis_wallet: "robinhood"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.mainnet.chain.robinhood.com"
      ],
      slug: "robinhood",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "ron"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2020",
      chain_id_hex: "0x7e4",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://app.roninchain.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Ronin",
      native_token: "RON",
      providers: {
        arkham: "ronin",
        coingecko_native: "ronin",
        defillama: "ronin",
        moralis: "ronin",
        moralis_wallet: "ronin"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "ronin",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2021",
      chain_id_hex: "0x7e5",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://saigon-app.roninchain.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:2020",
      metadata: {},
      name: "Ronin Saigon Testnet",
      native_token: "RON",
      providers: {
        moralis: "ronin_saigon"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "ronin_saigon",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "scroll-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#EBC28E",
      caip2: "eip155:534352",
      chain_id_hex: "0x82750",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://scrollscan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Scroll",
      native_token: "ETH",
      providers: {
        codeslaw: "scroll",
        coingecko_native: "ethereum",
        defillama: "scroll",
        dexscreener: "scroll",
        etherscan_scan: "scrollscan.com"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.scroll.io"
      ],
      slug: "scroll",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#EBC28E",
      caip2: "eip155:534351",
      chain_id_hex: "0x8274f",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:534352",
      metadata: {},
      name: "Scroll Sepolia",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "scroll_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "sei-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1329",
      chain_id_hex: "0x531",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://seitrace.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Sei",
      native_token: "SEI",
      providers: {
        coingecko_native: "sei-network",
        moralis: "sei",
        moralis_wallet: "sei"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "sei",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1328",
      chain_id_hex: "0x530",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:1329",
      metadata: {},
      name: "Sei Testnet",
      native_token: "SEI",
      providers: {
        moralis: "sei_testnet"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "sei_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#627EEA",
      caip2: "eip155:11155111",
      chain_id_hex: "0xaa36a7",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.etherscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:1",
      metadata: {},
      name: "Sepolia",
      native_token: "ETH",
      providers: {
        alchemy: "eth-sepolia",
        ankr: "eth_sepolia",
        infura: "sepolia",
        moralis: "sepolia"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://ethereum-sepolia-rpc.publicnode.com"
      ],
      slug: "sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "solana-mainnet",
        "0x1399c",
        "sol"
      ],
      bech32_hrp: null,
      block_time_seconds: 0.4,
      brand_color: "#14F195",
      caip2: "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
      chain_id_hex: null,
      decimals: 9,
      explorer_paths: {
        address: "account"
      },
      explorer_url: "https://solscan.io",
      family: "solana",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Solana",
      native_token: "SOL",
      providers: {
        arkham: "solana",
        blockchair: "solana",
        chainflip: "5",
        coingecko: "solana",
        coingecko_native: "solana",
        defillama: "solana",
        dexscreener: "solana",
        helius: "mainnet",
        thorchain: "SOL"
      },
      reorg_depth: 32,
      rpc_urls: [
        "https://solana-rpc.publicnode.com",
        "https://api.mainnet-beta.solana.com"
      ],
      slug: "solana",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 0.4,
      brand_color: "#14F195",
      caip2: "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1",
      chain_id_hex: null,
      decimals: 9,
      explorer_paths: {},
      explorer_url: null,
      family: "solana",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
      metadata: {},
      name: "Solana Devnet",
      native_token: "SOL",
      providers: {
        helius: "devnet"
      },
      reorg_depth: 32,
      rpc_urls: [
        "https://api.devnet.solana.com"
      ],
      slug: "solana-devnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 0.4,
      brand_color: "#14F195",
      caip2: "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z",
      chain_id_hex: null,
      decimals: 9,
      explorer_paths: {},
      explorer_url: null,
      family: "solana",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
      metadata: {},
      name: "Solana Testnet",
      native_token: "SOL",
      providers: {
        helius: "testnet"
      },
      reorg_depth: 32,
      rpc_urls: [
        "https://api.testnet.solana.com"
      ],
      slug: "solana-testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "thorchain-mainnet"
      ],
      bech32_hrp: "thor",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:thorchain-1",
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {
        address: "address",
        block: "block",
        token: null,
        tx: "tx"
      },
      explorer_url: "https://thorchain.net",
      family: "cosmos",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "THORChain",
      native_token: "RUNE",
      providers: {
        coingecko_native: "thorchain",
        thorchain: "THOR"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://gateway.liquify.com/chain/thorchain_api"
      ],
      slug: "thorchain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "trx",
        "tron-mainnet",
        "tron:0x2b6653dc"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "tron:728126428",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        token: "token20",
        tx: "transaction"
      },
      explorer_url: "https://tronscan.org/#",
      family: "tron",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "TRON",
      native_token: "TRX",
      providers: {
        blockchair: "tron",
        chainflip: "7",
        coingecko_native: "tron",
        thorchain: "TRON",
        tronscan: "mainnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api.trongrid.io"
      ],
      slug: "tron",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "tron-testnet-nile",
        "tron:0xcd8690dc"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "tron:3448148188",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        token: "token20",
        tx: "transaction"
      },
      explorer_url: "https://nile.tronscan.org/#",
      family: "tron",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "tron:728126428",
      metadata: {},
      name: "TRON Nile",
      native_token: "TRX",
      providers: {
        tronscan: "nile"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://nile.trongrid.io"
      ],
      slug: "tron-nile",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "tron-testnet-shasta",
        "tron:0x94a9059e"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "tron:2494104990",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        token: "token20",
        tx: "transaction"
      },
      explorer_url: "https://shasta.tronscan.org/#",
      family: "tron",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "tron:728126428",
      metadata: {},
      name: "TRON Shasta",
      native_token: "TRX",
      providers: {
        tronscan: "shasta"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api.shasta.trongrid.io"
      ],
      slug: "tron-shasta",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "xrpl",
        "ripple",
        "xrp-mainnet",
        "xrpl-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "xrpl:0",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        address: "account",
        block: "ledger",
        token: null
      },
      explorer_url: "https://xrpscan.com",
      family: "xrpl",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "XRP Ledger",
      native_token: "XRP",
      providers: {
        blockchair: "ripple",
        coingecko_native: "ripple",
        thorchain: "XRP",
        xrpscan: "mainnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://s1.ripple.com:51234",
        "https://s2.ripple.com:51234"
      ],
      slug: "xrp",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "xrpl-testnet"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "xrpl:1",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        address: "account",
        block: "ledger",
        token: null
      },
      explorer_url: "https://testnet.xrpscan.com",
      family: "xrpl",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "xrpl:0",
      metadata: {},
      name: "XRP Ledger Testnet",
      native_token: "XRP",
      providers: {
        xrpscan: "testnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://s.altnet.rippletest.net:51234"
      ],
      slug: "xrp-testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "zcash-mainnet",
        "zec"
      ],
      bech32_hrp: null,
      block_time_seconds: 75,
      brand_color: "#F4B728",
      caip2: "bip122:00040fe8ec8471911baa1db1266ea15d",
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {
        token: null,
        tx: "transaction"
      },
      explorer_url: "https://blockchair.com/zcash",
      family: "utxo",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Zcash",
      native_token: "ZEC",
      providers: {
        blockchair: "zcash",
        blockchair_api: "zcash",
        coingecko_native: "zcash"
      },
      reorg_depth: 24,
      rpc_urls: [],
      slug: "zcash",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:7000",
      chain_id_hex: "0x1b58",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.zetachain.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "ZetaChain",
      native_token: "ZETA",
      providers: {
        coingecko_native: "zetachain"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "zetachain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:7001",
      chain_id_hex: "0x1b59",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.zetascan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:7000",
      metadata: {},
      name: "Zetachain Testnet",
      native_token: "ZETA",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "zetachain_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "zksync",
        "zksync-era",
        "era"
      ],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#8C8DFC",
      caip2: "eip155:324",
      chain_id_hex: "0x144",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.zksync.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "zkSync Era",
      native_token: "ETH",
      providers: {
        alchemy: "zksync-mainnet",
        arkham: "zksync_era",
        coingecko: "zksync",
        coingecko_native: "ethereum",
        defillama: "era",
        dexscreener: "zksync",
        infura: "zksync-mainnet",
        sourcify: "324"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.era.zksync.io"
      ],
      slug: "zksync_era",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#8C8DFC",
      caip2: "eip155:300",
      chain_id_hex: "0x12c",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.explorer.zksync.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:324",
      metadata: {},
      name: "zkSync Sepolia",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "zksync_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#8C8DFC",
      caip2: "eip155:280",
      chain_id_hex: "0x118",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:324",
      metadata: {},
      name: "zkSync Goerli",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "zksync_testnet",
      status: "deprecated",
      wrapped_native_address: null
    }
  ],
  provider_keys: {
    alchemy: "Alchemy network subdomain: https://{id}.g.alchemy.com/v2/<key>",
    ankr: "Ankr RPC path: https://rpc.ankr.com/{id}/<key>",
    arkham: "Arkham chain name",
    blockchair: "Blockchair chain name",
    blockchair_api: "Blockchair API chain (UTXO ingest alongside xmrchain)",
    blockscan: "Blockscan chain id",
    blockscout: "Blockscout Etherscan-compatible API base (ABIs, source)",
    blockscout_v2: "Blockscout v2 API base that ingests the chain's transactions",
    blocksec: "BlockSec (MetaSleuth API) chain id",
    chainflip: "Chainflip ForeignChain index, as its vault events encode srcChain / dstChain",
    codeslaw: "Codeslaw platform name",
    coingecko: "CoinGecko asset platform id",
    coingecko_native: "CoinGecko coin id of the chain's native (gas) coin \u2014 what prices its native transfers. Measured per chain, not derived from the platform: CoinGecko's own platform \u2192 native-coin map names AURORA for Aurora and wrapped SEI for Sei, whose gas is ETH and SEI",
    cosmos_directory: "cosmos.directory LCD proxy base URL, the Cosmos fallback",
    cosmos_lcd: "Cosmos SDK LCD (REST) base URL, first choice (Cosmos ingest)",
    defillama: "DefiLlama chain name",
    dexscreener: "DexScreener chain id",
    esplora: "Esplora network (UTXO ingest)",
    etherscan_scan: "Etherscan-family explorer host scraped for labels",
    evm_rpc: "'ingest' (transactions read over the chain's JSON-RPC) or 'discovery' (block windows found over it, transactions read through another route)",
    helius: "Helius cluster (Solana ingest)",
    hyperliquid: "Hyperliquid network (HyperCore ingest)",
    infura: "Infura network subdomain: https://{id}.infura.io/v3/<key>",
    metasleuth: "MetaSleuth chain name",
    moralis: "Moralis chain name; the Moralis ingest route reads every chain carrying it",
    moralis_wallet: "Moralis chain name, where Moralis's mainnet-only WALLET endpoints (wallet chains / history / net worth) accept the chain \u2014 measured per chain, because Moralis's idea of mainnet is not the registry's (it refuses Moonriver)",
    near_rpc: "NEAR archival RPC URL (NEAR ingest)",
    scanscope: "ScanScope chain name",
    sourcify: "Sourcify chain id",
    thorchain: "THORChain chain ticker \u2014 the prefix of its asset notation (ETH in ETH.USDT)",
    tronscan: "TronScan network (TRON ingest)",
    xmrchain: "xmrchain onion-explorer network (Monero ingest)",
    xrpscan: "XRPScan network (XRP Ledger ingest)"
  }
};
var ChainStatus = {
  /** Offered in pickers and processed. */
  ACTIVE: "active"};
var EXPLORER_LINK_TYPES = ["tx", "address", "block", "token"];
var ChainWireSchema = z.object({
  caip2: z.string().min(1),
  slug: z.string().min(1),
  chain_id_hex: z.string().nullable().default(null),
  family: z.string().refine(isChainFamily, { message: "unknown chain family" }),
  aliases: z.array(z.string()).default([]),
  name: z.string().min(1),
  native_token: z.string().min(1),
  decimals: z.number().int().min(0).default(18),
  wrapped_native_address: z.string().nullable().default(null),
  status: z.enum(["active", "deprecated", "disabled"]).default("active"),
  is_testnet: z.boolean().default(false),
  mainnet: z.string().nullable().default(null),
  layer: z.enum(["l1", "l2", "sidechain"]).nullable().default(null),
  // Required: rows arrive resolved (Python fills the mainnet's / family's depth).
  reorg_depth: z.number().int().min(0),
  block_time_seconds: z.number().positive().nullable().default(null),
  finality_blocks: z.number().int().positive().nullable().default(null),
  explorer_url: z.string().nullable().default(null),
  explorer_paths: z.record(z.string(), z.string().nullable()).default({}),
  rpc_urls: z.array(z.string()).default([]),
  providers: z.record(z.string(), z.string()).default({}),
  bech32_hrp: z.string().nullable().default(null),
  brand_color: z.string().nullable().default(null),
  metadata: z.record(z.string(), z.unknown()).default({})
});
function foldAliases(aliases) {
  const seen = /* @__PURE__ */ new Set();
  for (const raw of aliases ?? []) {
    const alias = raw.trim().toLowerCase();
    if (alias) seen.add(alias);
  }
  return [...seen];
}
function explorerPathsOf(raw) {
  const out = {};
  for (const type of EXPLORER_LINK_TYPES) if (type in raw) out[type] = raw[type] ?? null;
  return out;
}
function chainFromWire(row, mainnetSlugOf) {
  const wire = ChainWireSchema.parse(row);
  return Object.freeze({
    caip2: wire.caip2,
    slug: wire.slug,
    chainIdHex: wire.chain_id_hex,
    family: wire.family,
    aliases: Object.freeze(foldAliases(wire.aliases)),
    name: wire.name,
    nativeToken: wire.native_token,
    decimals: wire.decimals,
    wrappedNativeAddress: wire.wrapped_native_address,
    status: wire.status,
    isTestnet: wire.is_testnet,
    mainnet: wire.mainnet,
    mainnetSlug: wire.mainnet ? mainnetSlugOf?.(wire.mainnet) ?? null : null,
    layer: wire.layer,
    reorgDepth: wire.reorg_depth,
    blockTimeSec: wire.block_time_seconds,
    finalityBlocks: wire.finality_blocks,
    explorerUrl: wire.explorer_url ? wire.explorer_url.replace(/\/+$/, "") : null,
    explorerPaths: Object.freeze(explorerPathsOf(wire.explorer_paths)),
    rpcUrls: Object.freeze([...wire.rpc_urls]),
    providers: Object.freeze({ ...wire.providers }),
    bech32Hrp: wire.bech32_hrp,
    brandColor: wire.brand_color,
    logoUrl: null,
    metadata: Object.freeze({ ...wire.metadata })
  });
}
function isActiveChain(chain) {
  return chain.status === ChainStatus.ACTIVE;
}

// src/chains/catalogue.ts
var CatalogueSchema = z.object({
  chains: z.array(ChainWireSchema).default([]),
  provider_keys: z.record(z.string(), z.string()).default({})
});
var SNAPSHOT = CatalogueSchema.parse(catalogue_generated_default);
var BUNDLED_SLUG_BY_CAIP2 = new Map(
  SNAPSHOT.chains.map((row) => [row.caip2, row.slug])
);
var BUNDLED_CHAINS = Object.freeze(
  SNAPSHOT.chains.map((row) => chainFromWire(row, (caip2) => BUNDLED_SLUG_BY_CAIP2.get(caip2)))
);
Object.freeze({
  ...SNAPSHOT.provider_keys
});

// src/chains/registry.ts
var log = createLogger("chains:registry");
function hexKey(value) {
  let token = value.trim().toLowerCase();
  if (!token) return null;
  if (!token.startsWith("0x")) token = `0x${token}`;
  try {
    return `0x${BigInt(token).toString(16)}`;
  } catch {
    return null;
  }
}
var ChainRegistry = class {
  byCaip2 = /* @__PURE__ */ new Map();
  bySlug = /* @__PURE__ */ new Map();
  byHex = /* @__PURE__ */ new Map();
  // Alternate spellings; consulted by `resolve` only after the canonical
  // lookups, so an alias can never shadow a caip2 / hex / slug.
  byAlias = /* @__PURE__ */ new Map();
  listeners = /* @__PURE__ */ new Set();
  revision = 0;
  batchDepth = 0;
  pending = false;
  sorted = null;
  constructor(chains = []) {
    for (const chain of chains) this.register(chain);
    this.pending = false;
  }
  /**
   * Changes whenever the answer to any lookup may have changed — a register,
   * an unregister, a hydration. What `useSyncExternalStore` snapshots.
   */
  get version() {
    return this.revision;
  }
  /**
   * Add `chain`; `replace: true` overwrites the row with the same CAIP-2 id.
   *
   * Throws when the chain's slug or hex id already names a DIFFERENT chain —
   * two rows can never share a spelling, with or without `replace` (Python's
   * `ChainRegistry.register` refuses the same).
   */
  register(chain, { replace = false } = {}) {
    const caip2 = normalizeCaip2(chain.caip2);
    const existing = this.byCaip2.get(caip2);
    if (existing && existing !== chain && !replace) {
      throw new Error(`Chain ${caip2} is already registered; pass replace=true to overwrite.`);
    }
    const hex = chain.chainIdHex ? hexKey(chain.chainIdHex) : null;
    const slugOwner = this.bySlug.get(chain.slug);
    if (slugOwner && normalizeCaip2(slugOwner.caip2) !== caip2) {
      throw new Error(`Chain slug "${chain.slug}" of ${caip2} already names ${slugOwner.caip2}.`);
    }
    const hexOwner = hex ? this.byHex.get(hex) : void 0;
    if (hexOwner && normalizeCaip2(hexOwner.caip2) !== caip2) {
      throw new Error(`Chain hex ${hex} of ${caip2} already names ${hexOwner.caip2}.`);
    }
    if (existing) this.drop(existing);
    this.byCaip2.set(caip2, chain);
    this.bySlug.set(chain.slug, chain);
    if (hex) this.byHex.set(hex, chain);
    for (const alias of foldAliases(chain.aliases)) this.byAlias.set(alias, chain);
    this.touch();
  }
  /** Remove and return the chain `value` resolves to (any spelling). */
  unregister(value) {
    const chain = this.resolve(value);
    if (!chain) return null;
    this.drop(chain);
    this.touch();
    return chain;
  }
  drop(chain) {
    this.byCaip2.delete(normalizeCaip2(chain.caip2));
    if (this.bySlug.get(chain.slug) === chain) this.bySlug.delete(chain.slug);
    const hex = chain.chainIdHex ? hexKey(chain.chainIdHex) : null;
    if (hex && this.byHex.get(hex) === chain) this.byHex.delete(hex);
    for (const alias of foldAliases(chain.aliases)) {
      if (this.byAlias.get(alias) === chain) this.byAlias.delete(alias);
    }
  }
  /** Drop every registered chain (test-only). */
  clear() {
    this.byCaip2.clear();
    this.bySlug.clear();
    this.byHex.clear();
    this.byAlias.clear();
    this.touch();
  }
  /**
   * Run `fn` and notify subscribers ONCE at the end, however many rows it
   * changed — a hydration of a hundred rows is one re-render, not a hundred.
   */
  batch(fn) {
    this.batchDepth += 1;
    try {
      return fn();
    } finally {
      this.batchDepth -= 1;
      if (this.batchDepth === 0 && this.pending) {
        this.pending = false;
        this.emit();
      }
    }
  }
  /** Call `listener` after every change; returns the unsubscribe. */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
  touch() {
    this.revision += 1;
    this.sorted = null;
    if (this.batchDepth > 0) this.pending = true;
    else this.emit();
  }
  emit() {
    for (const listener of [...this.listeners]) {
      try {
        listener();
      } catch (err) {
        log.error("chain registry listener threw", { err });
      }
    }
  }
  /** The chain with this slug (case-folded), or null. */
  get(slug) {
    return this.bySlug.get((slug ?? "").trim().toLowerCase()) ?? null;
  }
  /** Lookup by EVM hex chain id (auto `0x`-prefixes, lowercases, drops leading zeros). */
  getByHex(chainIdHex) {
    const key = hexKey(chainIdHex ?? "");
    return key ? this.byHex.get(key) ?? null : null;
  }
  /** Lookup by CAIP-2 chain id (e.g. "eip155:1"); Base58 references are case-sensitive. */
  getByCaip2(caip2) {
    return this.byCaip2.get(normalizeCaip2(caip2 ?? "")) ?? null;
  }
  /**
   * Resolve by CAIP-2, hex, bare decimal, slug, or alias — in that order, the
   * same order as `ChainRegistry.resolve` in `commons/chains/registry.py`, so a
   * spelling resolves to the same chain on both sides of the wire. Every status
   * resolves.
   */
  resolve(value) {
    if (value == null) return null;
    const token = String(value).trim();
    if (!token) return null;
    if (token.includes(":")) {
      const hit = this.getByCaip2(token);
      if (hit) return hit;
    }
    const folded = token.toLowerCase();
    if (folded.startsWith("0x")) {
      const hit = this.getByHex(folded);
      if (hit) return hit;
    } else if (/^\d+$/.test(folded)) {
      const hit = this.byHex.get(`0x${BigInt(folded).toString(16)}`);
      if (hit) return hit;
    }
    return this.bySlug.get(folded) ?? this.byAlias.get(folded) ?? null;
  }
  /** Every registered chain, whatever its status, sorted by slug. A fresh array. */
  all() {
    this.sorted ??= Object.freeze(
      [...this.byCaip2.values()].sort((a, b) => a.slug.localeCompare(b.slug))
    );
    return [...this.sorted];
  }
  /** The chains offered for new work (status `active`), sorted by slug. */
  active() {
    return this.all().filter(isActiveChain);
  }
  byFamily(family) {
    return this.all().filter((c) => c.family === family);
  }
};
var registry = globalSingleton(
  "chains/registry.v2",
  () => new ChainRegistry(BUNDLED_CHAINS)
);
globalSingleton("chains/hydrated-only.v1", () => /* @__PURE__ */ new Set());
function normalizeCaip2(value) {
  const split = value.trim().indexOf(":");
  if (split < 0) return value.trim();
  const namespace = value.trim().slice(0, split).toLowerCase();
  let reference = value.trim().slice(split + 1);
  if (["bip122", "polkadot"].includes(namespace)) reference = reference.toLowerCase();
  if (namespace === "eip155" && /^\d+$/.test(reference)) reference = BigInt(reference).toString();
  return `${namespace}:${reference}`;
}
function resolveChain(value) {
  return registry.resolve(value);
}
function getChainMetadata(chainId) {
  return registry.resolve(chainId);
}
globalSingleton("chains/explorers", () => ({
  overrides: /* @__PURE__ */ new Map(),
  pathDefaults: {}
}));

// src/display/coerce.ts
function asTrimmedString(value) {
  const text2 = typeof value === "string" ? value.trim() : "";
  return text2 === "" ? void 0 : text2;
}
var BASE58 = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
var BECH32 = "qpzry9x8gf2tvdw0s3jn54khce6mua7l";
var GENERATORS = [996825010, 642813549, 513874426, 1027748829, 705979059];
var BITCOIN_ADDRESS_NETWORKS = {
  bitcoin: { hrp: "bc", versions: [0, 5] },
  "bitcoin-testnet": { hrp: "tb", versions: [111, 196] },
  "bitcoin-regtest": { hrp: "bcrt", versions: [111, 196] }
};
function decodeBase58(address, alphabet = BASE58) {
  if (!address || address.length > 90) return null;
  let value = 0n;
  for (const char of address) {
    const digit = alphabet.indexOf(char);
    if (digit < 0) return null;
    value = value * 58n + BigInt(digit);
  }
  const bytes = [];
  while (value) {
    bytes.push(Number(value & 255n));
    value >>= 8n;
  }
  for (const char of address) {
    if (char !== alphabet[0]) break;
    bytes.push(0);
  }
  return Uint8Array.from(bytes.reverse());
}
var ZCASH_ADDRESS_NETWORKS = {
  zcash: { transparent: [[28, 184], [28, 189]], sapling: "zs", unified: "u" }
};
var MONERO_ADDRESS_NETWORKS = {
  monero: { 95: ["4", "8"], 106: ["4"] }
};
var BECH32_CONST = 1;
var BECH32M_CONST = 734539939;
var SAPLING_PAYLOAD_BYTES = 43;
function decodeBech32(address, maxLength = 90) {
  if (maxLength !== null && address.length > maxLength) return null;
  const lower = address.toLowerCase();
  if (address !== lower && address !== address.toUpperCase()) return null;
  const separator = lower.lastIndexOf("1");
  if (separator < 1) return null;
  const hrp = lower.slice(0, separator);
  const data = [...lower.slice(separator + 1)].map((c) => BECH32.indexOf(c));
  if (data.length < 7 || data.some((v) => v < 0)) return null;
  const expanded = [...hrp].map((c) => c.charCodeAt(0) >> 5).concat([0], [...hrp].map((c) => c.charCodeAt(0) & 31), data);
  let checksum = 1;
  for (const value of expanded) {
    const top = checksum >>> 25;
    checksum = (checksum & 33554431) << 5 ^ value;
    GENERATORS.forEach((generator, bit) => {
      if (top >>> bit & 1) checksum ^= generator;
    });
  }
  if (checksum !== BECH32_CONST && checksum !== BECH32M_CONST) return null;
  return { hrp, data: data.slice(0, -6), constant: checksum };
}
function convertedLength(data) {
  let bits = 0, accumulator = 0, length = 0;
  for (const value of data) {
    accumulator = (accumulator << 5 | value) & 4095;
    bits += 5;
    if (bits >= 8) {
      bits -= 8;
      length++;
    }
  }
  return bits < 5 && (accumulator << 8 - bits & 255) === 0 ? length : null;
}
function base58checkPayload(address) {
  const decoded = decodeBase58(address);
  if (!decoded || decoded.length < 5) return null;
  const checksum = sha256(sha256(decoded.slice(0, -4)));
  return decoded.slice(-4).every((value, index) => value === checksum[index]) ? decoded.slice(0, -4) : null;
}
function validCosmosAddress(address, hrp) {
  const decoded = decodeBech32(address);
  return decoded !== null && decoded.hrp === hrp && decoded.constant === BECH32_CONST && [20, 32].includes(convertedLength(decoded.data) ?? -1);
}
function validBitcoinAddress(address, network) {
  if (address.length < 14 || address.length > 90) return false;
  const formats = network ? [BITCOIN_ADDRESS_NETWORKS[network]].filter((format) => format !== void 0) : Object.values(BITCOIN_ADDRESS_NETWORKS);
  if (!formats.length) return false;
  if (/^(bc1|tb1|bcrt1)/i.test(address)) {
    const decoded = decodeBech32(address);
    if (!decoded || !formats.some((format) => format.hrp === decoded.hrp) || !decoded.data.length) return false;
    const version = decoded.data[0];
    if (version > 16 || decoded.constant !== (version === 0 ? BECH32_CONST : BECH32M_CONST)) return false;
    const length = convertedLength(decoded.data.slice(1));
    return length !== null && length >= 2 && length <= 40 && (version !== 0 || length === 20 || length === 32);
  }
  const payload = base58checkPayload(address);
  return payload !== null && payload.length === 21 && formats.some((format) => format.versions.includes(payload[0]));
}
function validZcashAddress(address, network = "zcash") {
  const format = ZCASH_ADDRESS_NETWORKS[network];
  if (!format || !address) return false;
  if (address.startsWith("t")) {
    const payload = base58checkPayload(address);
    return payload !== null && payload.length === 22 && format.transparent.some(([a, b]) => payload[0] === a && payload[1] === b);
  }
  const unified = address.toLowerCase().startsWith(`${format.unified}1`);
  const decoded = decodeBech32(address, unified ? null : 90);
  if (!decoded) return false;
  if (decoded.hrp === format.sapling && decoded.constant === BECH32_CONST) {
    return convertedLength(decoded.data) === SAPLING_PAYLOAD_BYTES;
  }
  return decoded.hrp === format.unified && decoded.constant === BECH32M_CONST;
}
function validMoneroAddress(address, network = "monero") {
  const prefixes = MONERO_ADDRESS_NETWORKS[network]?.[address.length];
  return Boolean(prefixes?.some((prefix) => address.startsWith(prefix))) && [...address].every((char) => BASE58.includes(char));
}
function validLedgerAddress(address, family) {
  const alphabet = family === "xrpl" ? "rpshnaf39wBUDNEGHJKLM4PQRST7VWXYZ2bcdeCg65jkm8oFqi1tuvAxyz" : BASE58;
  const bytes = decodeBase58(address, alphabet);
  if (!bytes || bytes.length !== 25 || bytes[0] !== (family === "xrpl" ? 0 : 65)) return false;
  const digest = sha256(sha256(bytes.slice(0, -4)));
  return bytes.slice(-4).every((value, index) => value === digest[index]);
}

// src/chains/address.ts
var RE_EVM = /^0x[0-9a-fA-F]{40}$/;
var RE_BTC_BECH32 = /^(bc1|tb1|bcrt1|zs1)[0-9ac-hj-np-z]{11,87}$/i;
var RE_XRPL = /^r[1-9A-HJ-NP-Za-km-z]{24,34}$/;
var RE_TRON = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;
var RE_SOLANA = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;
var RE_COSMOS_BECH32 = /^[a-z][a-z0-9]{1,10}1[0-9ac-hj-np-z]{38,58}$/;
function addressFamily(address, chainId) {
  if (RE_EVM.test(address)) return ChainFamily.EVM;
  if (validBitcoinAddress(address) || RE_BTC_BECH32.test(address) || validZcashAddress(address) || validMoneroAddress(address)) {
    return ChainFamily.UTXO;
  }
  if (RE_XRPL.test(address) && validLedgerAddress(address, "xrpl")) return ChainFamily.XRPL;
  if (RE_TRON.test(address) && validLedgerAddress(address, "tron")) return ChainFamily.TRON;
  if (RE_SOLANA.test(address)) return ChainFamily.SOLANA;
  if (RE_COSMOS_BECH32.test(address)) return ChainFamily.COSMOS;
  return null;
}
function isValidAddress(address, chainId) {
  const addr = address.trim();
  if (!addr) return false;
  const fam = chainId ? getChainMetadata(chainId)?.family ?? null : addressFamily(addr);
  switch (fam) {
    case ChainFamily.EVM:
    case ChainFamily.HYPERCORE:
      return RE_EVM.test(addr);
    case ChainFamily.UTXO: {
      const slug = chainId ? resolveChain(chainId)?.slug : void 0;
      if (slug && ZCASH_ADDRESS_NETWORKS[slug]) return validZcashAddress(addr, slug);
      if (slug && MONERO_ADDRESS_NETWORKS[slug]) return validMoneroAddress(addr, slug);
      if (slug && !BITCOIN_ADDRESS_NETWORKS[slug]) return true;
      if (!slug) return validBitcoinAddress(addr) || validZcashAddress(addr) || validMoneroAddress(addr);
      return validBitcoinAddress(addr, slug);
    }
    case ChainFamily.XRPL:
      return RE_XRPL.test(addr) && validLedgerAddress(addr, "xrpl");
    case ChainFamily.TRON:
      return RE_TRON.test(addr) && validLedgerAddress(addr, "tron");
    case ChainFamily.NEAR:
      return addr.length >= 2 && addr.length <= 64 && /^[a-z0-9]+(?:[._-][a-z0-9]+)*$/.test(addr);
    case ChainFamily.SOLANA:
      return RE_SOLANA.test(addr) && decodeBase58(addr)?.length === 32;
    case ChainFamily.COSMOS: {
      const hrp = chainId ? resolveChain(chainId)?.bech32Hrp : null;
      return hrp ? validCosmosAddress(addr, hrp) || validCosmosAddress(addr, `${hrp}valoper`) : RE_COSMOS_BECH32.test(addr);
    }
    default:
      return RE_EVM.test(addr) || validBitcoinAddress(addr) || validZcashAddress(addr) || validMoneroAddress(addr) || RE_SOLANA.test(addr) && decodeBase58(addr)?.length === 32 || RE_COSMOS_BECH32.test(addr);
  }
}

// src/tenancy/index.ts
var TenantContext = class _TenantContext {
  static {
    brandClass(this, "tenancy/TenantContext");
  }
  customerId;
  orgId;
  tenantId;
  userId;
  constructor(init = {}) {
    this.customerId = init.customerId ?? null;
    this.orgId = init.orgId || GLOBAL_TENANT;
    this.tenantId = init.tenantId || GLOBAL_TENANT;
    this.userId = init.userId ?? null;
  }
  /** Composite key `{org_id}-{tenant_id}` used in DDB SKs + global-tier checks. */
  get orgIdTenantId() {
    return `${this.orgId}-${this.tenantId}`;
  }
  get isGlobal() {
    return this.orgIdTenantId === GLOBAL_TENANT_COMPOSITE;
  }
  get isUserScoped() {
    return this.userId != null && this.userId.length > 0;
  }
  get hasCustomerScope() {
    return this.customerId != null && this.customerId.length > 0 || this.orgId !== GLOBAL_TENANT;
  }
  withUser(userId) {
    return new _TenantContext({
      customerId: this.customerId,
      orgId: this.orgId,
      tenantId: this.tenantId,
      userId
    });
  }
  withOrg(orgId) {
    return new _TenantContext({
      customerId: this.customerId,
      orgId,
      tenantId: this.tenantId,
      userId: this.userId
    });
  }
  withTenant(tenantId) {
    return new _TenantContext({
      customerId: this.customerId,
      orgId: this.orgId,
      tenantId,
      userId: this.userId
    });
  }
  withCustomer(customerId) {
    return new _TenantContext({
      customerId,
      orgId: this.orgId,
      tenantId: this.tenantId,
      userId: this.userId
    });
  }
  toJSON() {
    return {
      customerId: this.customerId,
      orgId: this.orgId,
      tenantId: this.tenantId,
      userId: this.userId,
      orgIdTenantId: this.orgIdTenantId
    };
  }
};

// src/auth/gateway-auth.ts
var AUTH_TYPES = {
  internal: {
    description: "Internal authentication for employees and services",
    allowedApplications: ["admin-portal", "qa-portal", "analytics-portal", "dashboard"]
  },
  external: {
    description: "External authentication for end users",
    allowedApplications: ["dashboard", "selfservice-portal"]
  }
};
var APPLICATIONS = {
  dashboard: {
    name: "User Dashboard",
    description: "Main application dashboard for end users",
    type: "external"
  },
  "admin-portal": {
    name: "Admin Portal",
    description: "Internal portal for application management",
    type: "internal"
  },
  "qa-portal": {
    name: "QA Portal",
    description: "Internal portal for quality assurance",
    type: "internal"
  },
  "selfservice-portal": {
    name: "Self-Service Portal",
    description: "Customer-facing self-service administration",
    type: "external"
  },
  "analytics-portal": {
    name: "Analytics Portal",
    description: "Internal analytics and reporting",
    type: "internal"
  }
};
function isValidAuthType(authType) {
  return authType.toLowerCase() in AUTH_TYPES;
}
function isValidApplication(application) {
  return application in APPLICATIONS;
}
function isApplicationAllowedForType(authType, application) {
  if (!isValidAuthType(authType) || !isValidApplication(application)) {
    return false;
  }
  return AUTH_TYPES[authType.toLowerCase()].allowedApplications.includes(
    application
  );
}

// src/models/wire/request.ts
var RequestAuth = class _RequestAuth {
  static {
    brandClass(this, "models/RequestAuth");
  }
  tenant;
  roles;
  scopes;
  apiKeyId;
  authProvider;
  isService;
  /** `"internal" | "external"` for API-Gateway invocations, else null. */
  gatewayAuthType;
  application;
  constructor(init = {}) {
    this.tenant = init.tenant instanceof TenantContext ? init.tenant : new TenantContext(init.tenant ?? {});
    this.roles = init.roles ?? [];
    this.scopes = init.scopes ?? [];
    this.apiKeyId = init.apiKeyId ?? null;
    this.authProvider = init.authProvider ?? null;
    this.isService = init.isService ?? false;
    this.gatewayAuthType = typeof init.gatewayAuthType === "string" ? init.gatewayAuthType.toLowerCase() : null;
    this.application = init.application ?? null;
  }
  /** Return the inner `TenantContext` (single source of truth). */
  toTenantContext() {
    return this.tenant;
  }
  get tenantContext() {
    return this.tenant;
  }
  // Back-compat parallel-field proxies.
  get userId() {
    return this.tenant.userId;
  }
  get orgId() {
    return this.tenant.orgId;
  }
  get tenantId() {
    return this.tenant.tenantId;
  }
  get orgIdTenantId() {
    return this.tenant.orgIdTenantId;
  }
  get customerId() {
    return this.tenant.customerId;
  }
  /** Back-compat for `auth.type` — returns `gatewayAuthType`. */
  get type() {
    return this.gatewayAuthType;
  }
  isInternal() {
    return (this.gatewayAuthType ?? "").toLowerCase() === "internal";
  }
  isExternal() {
    return (this.gatewayAuthType ?? "").toLowerCase() === "external";
  }
  /**
   * `true` when `application` is allowed for this request's
   * `gatewayAuthType`. Returns `false` for non-gateway invocations
   * (`gatewayAuthType == null`) — those routes must not be app-gated.
   */
  canAccessApplication(application) {
    if (!this.gatewayAuthType) return false;
    return isApplicationAllowedForType(this.gatewayAuthType, application);
  }
  static fromTenantContext(tenant, opts = {}) {
    return new _RequestAuth({ tenant, ...opts });
  }
};
function validateRecipientEmails(value) {
  const coerced = coerceCsv(value) ?? [];
  for (const entry of coerced) {
    if (entry.includes(" ") || (entry.match(/@/g)?.length ?? 0) !== 1) {
      throw new Error(`invalid email address: ${JSON.stringify(entry)}`);
    }
    const [local, domain] = entry.split("@");
    if (!local || !domain || !domain.includes(".")) {
      throw new Error(`invalid email address: ${JSON.stringify(entry)}`);
    }
  }
  return coerced;
}

// src/models/wire/response.ts
function isSuccess(r) {
  return r.ok === true;
}
function isError(r) {
  return r.ok === false;
}

// src/api/wire.ts
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// src/models/wire/transaction-filters.ts
var TX_INDEXED_FILTERS = ["chain", "protocol", "entity"];
var TX_INDEXED_FILTER_PARAMS = {
  chain: "chain_ids",
  protocol: "protocol_ids",
  entity: "primary_entity_id"
};
var TX_FILTERS_CAPABILITY_PATH = "/transactions/filters";
var FILTER_INDEXES_SETTING_KEY = "persistence.filter_indexes";
var FILTER_INDEX_DISABLED_NOTICE = "FILTER_INDEX_DISABLED";
var FILTER_INDEXES_DISABLED_REASON = "filter_indexes_disabled";
var ENABLE_VIA_CUSTOM_PIPELINE = "custom_pipeline";
var DISCOVERY_INDEX_SETTING_KEY = "persistence.discovery_index";
var DISCOVERY_INDEX_DISABLED_NOTICE = "DISCOVERY_INDEX_DISABLED";
var DISCOVERY_INDEX_DISABLED_REASON = "discovery_index_disabled";
var KNOWN = new Set(TX_INDEXED_FILTERS);
function text(value) {
  return asTrimmedString(value) ?? null;
}
function isTxIndexedFilterId(value) {
  return typeof value === "string" && KNOWN.has(value);
}
function byDisplayOrder(ids) {
  const set = new Set(ids);
  return TX_INDEXED_FILTERS.filter((id) => set.has(id));
}
function parseTxFiltersCapability(raw) {
  const body = isRecord(raw) ? raw : {};
  const seen = /* @__PURE__ */ new Set();
  const filters = [];
  for (const entry of Array.isArray(body.filters) ? body.filters : []) {
    if (!isRecord(entry) || !isTxIndexedFilterId(entry.id) || seen.has(entry.id)) continue;
    seen.add(entry.id);
    filters.push({
      id: entry.id,
      param: text(entry.param) ?? TX_INDEXED_FILTER_PARAMS[entry.id],
      // Only an explicit `false` is a refusal.
      available: entry.available !== false,
      reason: text(entry.reason),
      enableVia: text(entry.enable_via)
    });
  }
  const setting = isRecord(body.setting) ? body.setting : {};
  return {
    filters,
    setting: {
      key: text(setting.key) ?? FILTER_INDEXES_SETTING_KEY,
      default: setting.default === true
    },
    address: parseAddressCapability(body.address)
  };
}
function parseAddressCapability(raw) {
  if (!isRecord(raw)) return null;
  const setting = isRecord(raw.setting) ? raw.setting : {};
  return {
    // Only an explicit `false` is a refusal, as with a filter entry.
    available: raw.available !== false,
    reason: text(raw.reason),
    enableVia: text(raw.enable_via),
    setting: {
      key: text(setting.key) ?? DISCOVERY_INDEX_SETTING_KEY,
      // The platform default here is ON, the opposite of the filter indexes:
      // address lookup is a core read path, so only an explicit `false` is off.
      default: setting.default !== false
    }
  };
}
function addressFeedLimit(notices = [], capability) {
  const notice = notices.find((n) => n.code === DISCOVERY_INDEX_DISABLED_NOTICE);
  if (notice) return { message: notice.message, enableVia: notice.enableVia };
  const address = capability?.address;
  if (address && address.available === false) {
    return { message: null, enableVia: address.enableVia };
  }
  return null;
}
function parseNotice(raw) {
  if (!isRecord(raw)) return null;
  const code = text(raw.code);
  if (!code) return null;
  const named = Array.isArray(raw.filters) ? raw.filters : raw.filters == null ? [] : [raw.filters];
  return {
    code,
    filters: byDisplayOrder(named.filter(isTxIndexedFilterId)),
    message: text(raw.message),
    enableVia: text(raw.enable_via)
  };
}
function txListNoticesOf(...candidates) {
  const out = [];
  const keys = /* @__PURE__ */ new Set();
  for (const candidate of candidates) {
    if (!isRecord(candidate)) continue;
    const metadata = isRecord(candidate.metadata) ? candidate.metadata : {};
    for (const list of [candidate.notices, metadata.notices]) {
      if (!Array.isArray(list)) continue;
      for (const raw of list) {
        const notice = parseNotice(raw);
        if (!notice) continue;
        const key = `${notice.code}|${notice.filters.join(",")}`;
        if (keys.has(key)) continue;
        keys.add(key);
        out.push(notice);
      }
    }
  }
  return out;
}
function unavailableTxFilters(capability, notices = [], among) {
  const wanted = among ? new Set(among) : null;
  const remedies = /* @__PURE__ */ new Map();
  const add = (id, enableVia2) => {
    if (wanted && !wanted.has(id)) return;
    const set = remedies.get(id) ?? /* @__PURE__ */ new Set();
    if (enableVia2) set.add(enableVia2);
    remedies.set(id, set);
  };
  for (const filter of capability?.filters ?? []) {
    if (filter.available) continue;
    if (filter.reason && filter.reason !== FILTER_INDEXES_DISABLED_REASON) continue;
    add(filter.id, filter.enableVia);
  }
  for (const notice of notices) {
    if (notice.code !== FILTER_INDEX_DISABLED_NOTICE) continue;
    for (const id of notice.filters) add(id, notice.enableVia);
  }
  const filters = byDisplayOrder(remedies.keys());
  const stated = new Set(filters.flatMap((id) => [...remedies.get(id) ?? []]));
  const enableVia = filters.length === 0 ? null : stated.size === 0 ? ENABLE_VIA_CUSTOM_PIPELINE : stated.size === 1 ? [...stated][0] : null;
  return { filters, enableVia };
}
var LambdaRequestSchema = z.object({ "path": z.union([z.string(), z.null()]).default(null), "method": z.union([z.string(), z.null()]).default(null), "path_parameters": z.record(z.string(), z.string()).optional(), "query_parameters": z.record(z.string(), z.any()).optional(), "headers": z.record(z.string(), z.string()).optional(), "payload": z.union([z.any(), z.null()]).default(null), "auth": z.any().optional(), "metadata": z.any() }).catchall(z.any()).describe("Canonical Lambda request envelope.\n\nParameterized on the payload type ``T`` so downstream handlers can\ndeclare ``LambdaRequest[GetTransactionPayload]`` and get Pydantic\nvalidation on the body.");
var RequestAuthSchema = z.object({ "tenant": z.any().optional(), "roles": z.array(z.string()).optional(), "scopes": z.array(z.string()).optional(), "api_key_id": z.union([z.string(), z.null()]).default(null), "auth_provider": z.union([z.string(), z.null()]).default(null), "is_service": z.boolean().default(false), "continent": z.union([z.string(), z.null()]).default(null), "team_ids": z.array(z.string()).optional(), "policy_version": z.union([z.string(), z.null()]).default(null), "plan_id": z.union([z.string(), z.null()]).default(null), "plan_limits": z.record(z.string(), z.number().int()).optional(), "plan_settings": z.union([z.record(z.string(), z.any()), z.null()]).default(null), "plan_entitlements": z.union([z.array(z.string()), z.null()]).default(null), "impersonated": z.boolean().default(false), "act_sub": z.union([z.string(), z.null()]).default(null), "impersonation_id": z.union([z.string(), z.null()]).default(null), "impersonation_reason": z.union([z.string(), z.null()]).default(null), "impersonation_access_level": z.union([z.string(), z.null()]).default(null), "impersonation_expires_at": z.union([z.string(), z.null()]).default(null), "gateway_auth_type": z.union([z.string(), z.null()]).default(null), "application": z.union([z.string(), z.null()]).default(null), "qa_config": z.union([z.any(), z.null()]).default(null) }).describe("Resolved request authorization envelope (Auth0 / API key / S2S).\n\nAudit \xA77 #6 (task H) endgame \u2014 one tenancy carrier\n---------------------------------------------------\n\nTenancy lives on a single inner :class:`commons.tenancy.TenantContext`\ninstance; the request-authz fields (``roles``, ``scopes``,\n``api_key_id``, ``auth_provider``, ``is_service``) plus the\nAPI-Gateway authorizer fields (``gateway_auth_type`` /\n``application``) stay on the outer envelope because they don't\nbelong on a tenancy carrier.\n\nRead paths that only care about tenancy should call\n:meth:`to_tenant_context` once at the request boundary and\nthread the projection through their own code instead of holding\na reference to the full :class:`RequestAuth`. Back-compat\nproperties (``user_id`` / ``org_id`` / ``tenant_id`` /\n``org_id_tenant_id`` / ``customer_id`` / ``type``) proxy onto the\ninner ``tenant`` (tenancy fields) or onto ``gateway_auth_type``\n(the legacy ``type`` shim) so existing read-site code keeps\nworking unchanged.");
var RequestMetadataSchema = z.object({ "correlation_id": z.string(), "request_id": z.union([z.string(), z.null()]).default(null), "received_at": z.number().int(), "source": z.string().default("unknown"), "user_agent": z.union([z.string(), z.null()]).default(null), "ip_address": z.union([z.string(), z.null()]).default(null) }).describe("Per-invocation metadata stamped by the handler decorator.");
var ImportTransactionRequestSchema = z.object({ "hash": z.string().min(10).max(80), "chain_id": z.string().min(1).max(64), "address": z.union([z.string(), z.null()]).default(null), "notes": z.union([z.string().max(2e3), z.null()]).default(null), "tags": z.array(z.string()).optional(), "labels": z.array(z.string()).optional(), "refresh": z.boolean().default(false) }).strict().describe("``POST /transactions/import`` \u2014 fetch + persist a tx by hash.\n\nThe handler resolves ``hash`` on ``chain_id`` against the\nconfigured provider waterfall (Moralis \u2192 ddb cache \u2192 RPC) and\npersists the canonical row + any catalog enrichments the\ncaller asked for.");
var UpdateTransactionRequestSchema = z.object({ "notes": z.union([z.string().max(2e3), z.null()]).default(null), "disputed": z.union([z.boolean(), z.null()]).default(null), "archived": z.union([z.boolean(), z.null()]).default(null), "status": z.union([z.any(), z.null()]).default(null), "tags_add": z.array(z.union([z.string(), z.any()])).optional(), "tags_remove": z.array(z.string()).optional(), "labels_add": z.array(z.union([z.string(), z.any()])).optional(), "labels_remove": z.array(z.string()).optional(), "groups_add": z.array(z.union([z.string(), z.any()])).optional(), "groups_remove": z.array(z.string()).optional(), "entities_add": z.array(z.union([z.string(), z.any()])).optional(), "entities_remove": z.array(z.string()).optional(), "assigned_to_id": z.union([z.string(), z.null()]).default(null) }).strict().describe('``PATCH /transactions/{id}`` \u2014 mutate caller-owned attributes.\n\nAll fields are optional; the handler treats unset fields as\n"leave unchanged" rather than "clear". To explicitly clear a\nfield, send an empty string / empty list.');
var ShareTransactionRequestSchema = z.object({ "recipient_emails": z.array(z.string()).max(50).optional(), "expires_in_hours": z.number().int().gte(1).lte(720).default(24), "include_token_flows": z.boolean().default(true), "include_decoded_calls": z.boolean().default(true), "message": z.union([z.string().max(500), z.null()]).default(null) }).strict().describe("``POST /transactions/{id}/share`` \u2014 generate a share link.\n\nThe handler signs a short-lived JWT scoped to a read-only view\nof the row and emits an ``ActivityLogEvent`` so the originator\ncan audit who they shared with.");
var ListTransactionsQuerySchema = z.object({ "limit": z.number().int().gte(1).lte(1e3).default(50), "cursor": z.union([z.string(), z.null()]).default(null), "hide_dust": z.boolean().default(false), "fallback": z.string().default("system"), "chain_ids": z.union([z.array(z.string()), z.null()]).default(null), "addresses": z.union([z.array(z.string()), z.null()]).default(null), "protocol_ids": z.union([z.array(z.string()), z.null()]).default(null), "status": z.union([z.any(), z.null()]).default(null), "assigned_to_id": z.union([z.string(), z.null()]).default(null), "primary_entity_id": z.union([z.string(), z.null()]).default(null), "catalog_kind": z.union([z.any(), z.null()]).default(null), "catalog_id": z.union([z.string(), z.null()]).default(null), "entity_match": z.any().default("both"), "include_archived": z.boolean().default(false), "since": z.union([z.number().int().gte(0), z.null()]).default(null), "until": z.union([z.number().int().gte(0), z.null()]).default(null) }).strict().describe('``GET /transactions`` query string.\n\nEvery list filter is optional; empty filters mean "no constraint".\nComma-separated query-string values (``?chain_ids=eip155:1,eip155:137``)\nare coerced to lists.\n\n**Every field here is actually applied by the service.** Filters that were\ndeclared but silently ignored (``tags`` / ``labels`` / ``direction`` /\n``min_value_usd`` / ``max_value_usd``) were removed rather than left as a\ncontract that returns unfiltered results with a 200. Use ``catalog_kind`` +\n``catalog_id`` for tag/label/group filtering \u2014 that lane is index-backed and\nreal.\n\nValue filtering is deliberately absent and should stay that way: nothing\nindexes ``value_usd``, so it could only be a ``FilterExpression``, and this\nendpoint does not re-query to refill a page the filter emptied. A caller\nwould get arbitrarily short pages alongside a live cursor, which reads as\nlost rows. Filter on value client-side, or add an index first.');
var ByStatusQuerySchema = z.object({ "limit": z.number().int().gte(1).lte(1e3).default(50), "cursor": z.union([z.string(), z.null()]).default(null), "status": z.any(), "chain_ids": z.union([z.array(z.string()), z.null()]).default(null), "assigned_to_id": z.union([z.string(), z.null()]).default(null) }).strict().describe('``GET /transactions/by-status/{status}`` query string.\n\nSpecialised list endpoint that pre-binds the ``status`` filter\nfrom the path. The remaining knobs are a subset of\n:class:`ListTransactionsQuery` for "transactions matching this\nfinality bucket only" pages.');
var PaginationCursorSchema = z.object({ "next": z.union([z.string(), z.null()]).default(null), "prev": z.union([z.string(), z.null()]).default(null), "limit": z.number().int().default(50), "total": z.union([z.number().int(), z.null()]).default(null) }).describe("Opaque pagination cursor surfaced on list endpoints.");
var SuccessResponseSchema = z.object({ "statusCode": z.number().int().default(200), "ok": z.boolean().default(true), "data": z.union([z.any(), z.null()]).default(null), "pagination": z.union([z.any(), z.null()]).default(null), "metadata": z.record(z.string(), z.any()).optional() }).describe("Canonical success envelope.");
var ErrorResponseSchema = z.object({ "statusCode": z.number().int().default(200), "ok": z.boolean().default(false), "error": z.any() }).describe("Canonical error envelope.");
var ErrorBodySchema = z.object({ "message": z.string(), "error_type": z.string(), "error_code": z.union([z.string(), z.null()]).default(null), "error_level": z.union([z.any(), z.null()]).default(null), "details": z.record(z.string(), z.any()).optional(), "correlation_id": z.union([z.string(), z.null()]).default(null) });
var SearchLabelRefSchema = z.object({ "logo": z.union([z.string(), z.null()]).default(null), "color": z.union([z.string(), z.null()]).default(null), "confidence": z.union([z.number(), z.null()]).default(null), "confidence_status": z.union([z.string(), z.null()]).default(null), "lifecycle": z.union([z.string(), z.null()]).default(null), "explanation": z.array(z.string()).optional(), "label": z.string(), "value": z.union([z.string(), z.null()]).default(null), "scope": z.union([z.string(), z.null()]).default(null), "severity": z.union([z.any(), z.null()]).default(null) }).describe("A catalog label attached to a hit (provenance-aware).");
var SearchTagRefSchema = z.object({ "logo": z.union([z.string(), z.null()]).default(null), "color": z.union([z.string(), z.null()]).default(null), "confidence": z.union([z.number(), z.null()]).default(null), "confidence_status": z.union([z.string(), z.null()]).default(null), "lifecycle": z.union([z.string(), z.null()]).default(null), "explanation": z.array(z.string()).optional(), "label": z.string(), "severity": z.union([z.any(), z.null()]).default(null) }).describe("A catalog tag attached to a hit.");
var SearchEntityRefSchema = z.object({ "logo": z.union([z.string(), z.null()]).default(null), "color": z.union([z.string(), z.null()]).default(null), "confidence": z.union([z.number(), z.null()]).default(null), "confidence_status": z.union([z.string(), z.null()]).default(null), "lifecycle": z.union([z.string(), z.null()]).default(null), "explanation": z.array(z.string()).optional(), "id": z.string(), "name": z.string(), "entity_type": z.union([z.string(), z.null()]).default(null), "is_primary": z.boolean().default(false) }).describe("The owning / attributed entity for a hit.");
var SearchGroupRefSchema = z.object({ "logo": z.union([z.string(), z.null()]).default(null), "color": z.union([z.string(), z.null()]).default(null), "confidence": z.union([z.number(), z.null()]).default(null), "confidence_status": z.union([z.string(), z.null()]).default(null), "lifecycle": z.union([z.string(), z.null()]).default(null), "explanation": z.array(z.string()).optional(), "id": z.string(), "name": z.union([z.string(), z.null()]).default(null), "count": z.union([z.number().int(), z.null()]).default(null) }).describe('A group / cluster membership for a hit.\n\nCarries provenance like every other kind: "why is this group here" is the\nsame question as "why is this tag here", and this was the one ref that could\nnot answer it.');
var SearchHitEnrichmentSchema = z.object({ "name": z.union([z.string(), z.null()]).default(null), "chains": z.array(z.string()).optional(), "labels": z.array(z.any()).optional(), "tags": z.array(z.any()).optional(), "entity": z.union([z.any(), z.null()]).default(null), "groups": z.array(z.any()).optional(), "risk_level": z.union([z.any(), z.null()]).default(null), "risk_score": z.union([z.number(), z.null()]).default(null), "logo_url": z.union([z.string(), z.null()]).default(null), "scope": z.union([z.string(), z.null()]).default(null), "scopes": z.array(z.any()).optional(), "extra": z.record(z.string(), z.any()).optional() }).describe("Optional preview context carried by a :class:`SearchHit`.\n\nA resolver attaches whatever it cheaply has \u2014 chains, catalog\nlabels/tags, owning entity, group membership, a risk bucket/score, a\nlogo \u2014 and the front-end preview layer decides how much to render (inline\nvs hover) per resource kind. ``extra`` keeps it extensible for\nsource/tenant-specific fields without a model change.");
var SearchHitSchema = z.object({ "id": z.string(), "kind": z.any(), "title": z.string(), "subtitle": z.union([z.string(), z.null()]).default(null), "chain_id": z.union([z.string(), z.null()]).default(null), "score": z.union([z.number(), z.null()]).default(null), "href": z.union([z.string(), z.null()]).default(null), "enrichment": z.union([z.any(), z.null()]).default(null), "metadata": z.record(z.string(), z.any()).optional() }).describe("A single search result row, provider-agnostic.");
var SearchQuerySchema = z.object({ "text": z.string(), "kinds": z.array(z.any()).optional(), "chain_id": z.union([z.string(), z.null()]).default(null), "limit": z.number().int().default(20), "cursor": z.union([z.string(), z.null()]).default(null) }).describe("The ``POST /search`` request body.");
var SearchFacetValueSchema = z.object({ "value": z.string(), "label": z.string(), "count": z.union([z.number().int(), z.null()]).default(null) });
var SearchFacetSchema = z.object({ "field": z.string(), "label": z.string(), "values": z.array(z.any()).optional() });
var SearchResponseSchema = z.object({ "hits": z.array(z.any()).optional(), "facets": z.array(z.any()).optional(), "next_cursor": z.union([z.string(), z.null()]).default(null), "total": z.union([z.number().int(), z.null()]).default(null) }).describe("The ``POST /search`` response body (wrapped in ``SuccessResponse.data``).");
var AddressSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "address": z.string(), "chain": z.string().default("eth"), "address_type": z.any().default("unknown"), "address_type_pk": z.union([z.string(), z.null()]).default(null), "is_primary_chain": z.boolean().default(false), "name": z.union([z.string(), z.null()]).default(null), "notes": z.union([z.string(), z.null()]).default(null), "symbol": z.union([z.string(), z.null()]).describe("DEPRECATED \u2014 token identity now lives on the token SSOT (:class:`commons.interfaces.models.token.Token`, resolved via ``commons.tokens``) and is overlaid at read time (:class:`commons.authe.transaction.processing.TokenOverlayResolver`). Kept for back-compat on existing rows; new writes should not set it and readers should prefer the token overlay so a re-pointed / tenant-overridden definition is reflected.").default(null), "decimals": z.union([z.number().int(), z.null()]).describe("DEPRECATED \u2014 see :attr:`symbol`. Token decimals come from the token SSOT read overlay now; this address-row copy is legacy.").default(null), "is_contract": z.union([z.boolean(), z.null()]).default(null), "deployer": z.union([z.string(), z.null()]).default(null), "deployed_at": z.union([z.number().int(), z.null()]).default(null), "last_verified_at": z.union([z.number().int().gte(0), z.null()]).describe("Last successful seed verification, independent of unrelated edits.").default(null), "last_seen_at": z.union([z.number().int(), z.null()]).default(null), "risk_level": z.any().default("safe"), "primary_entity_id": z.union([z.string(), z.null()]).default(null), "logo_s3_uri": z.union([z.string(), z.null()]).default(null), "metadata": z.record(z.string(), z.any()).describe("Free-form enrichment sidecar. NOTE: the ``proxy`` sub-blob (``{pattern, is_proxy, current_implementation, implementations}``) is DEPRECATED as a stored copy \u2014 proxy resolution now lives on the block-windowed proxies-table SSOT (``commons.aws.dynamodb.queries.proxy_writes`` / proxy-mgmt) and is overlaid at read time (:class:`commons.authe.address.proxy_overlay.ProxyOverlayResolver`), mirroring the token ``symbol`` / ``decimals`` / ``logo`` deprecation. Kept for back-compat on existing rows; readers should prefer the proxy overlay so an admin override or fresh re-index is reflected.").optional(), "source": z.union([z.string(), z.null()]).default(null), "confidence_scored": z.union([z.boolean(), z.null()]).default(null), "confidence": z.number().gte(0).lte(1).default(1), "evidence": z.array(z.any()).optional(), "explanation": z.array(z.string()).optional(), "is_human_approved": z.boolean().default(false), "system_default": z.boolean().default(false), "lifecycle": z.any().default("candidate"), "aliases": z.array(z.string()).optional(), "severity": z.any().default("info"), "created_by": z.union([z.string(), z.null()]).default(null), "created_by_service": z.union([z.string(), z.null()]).default(null), "updated_by": z.union([z.string(), z.null()]).default(null), "updated_at": z.union([z.number().int(), z.null()]).default(null), "updated_by_service": z.union([z.string(), z.null()]).default(null), "valid_from": z.union([z.number().int(), z.null()]).default(null), "valid_to": z.union([z.number().int(), z.null()]).default(null) }).catchall(z.any()).describe("An on-chain address row.\n\nDDB key: ``HASH = chain``, ``RANGE = address``. The Pydantic surface\nstores the raw fields and lets ``DynamoDBHelper`` compose the\ncomposite key from them.");
var AddressClusterRowSchema = z.object({ "owner_user_id": z.union([z.string(), z.null()]).default(null), "visible_to_user_ids": z.array(z.string()).optional(), "visible_to_team_ids": z.array(z.string()).optional(), "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "cluster_id": z.string().default(""), "seed_node_id": z.string().default(""), "run_id": z.union([z.string(), z.null()]).default(null), "confidence": z.number().gte(0).lte(1).default(0), "member_count": z.number().int().default(0), "member_preview": z.array(z.string()).optional(), "chain_ids": z.array(z.string()).optional(), "internal_tx_count": z.number().int().default(0), "internal_value_raw": z.union([z.string(), z.null()]).default("0"), "internal_values_raw": z.record(z.string(), z.string()).optional(), "transaction_ids": z.union([z.array(z.string()), z.null()]).default(null), "evidence": z.array(z.any()).optional(), "explanation": z.array(z.string()).optional(), "rationale": z.string().default(""), "link_count": z.number().int().default(0), "link_confidence_min": z.number().default(0), "link_confidence_mean": z.number().default(0), "link_confidence_max": z.number().default(0), "weakest_link": z.union([z.any(), z.null()]).default(null), "policy_fingerprint": z.string().default(""), "engine_version": z.string().default(""), "superseded_by": z.union([z.string(), z.null()]).default(null), "superseded_at": z.union([z.number().int(), z.null()]).default(null), "retracted_by_run": z.union([z.string(), z.null()]).default(null), "retracted_at": z.union([z.number().int(), z.null()]).default(null), "updated_at": z.union([z.number().int(), z.null()]).default(null), "created_by": z.union([z.string(), z.null()]).default(null), "schema_version": z.string().default("1"), "cluster_sk": z.string().default(""), "recent_sk": z.string().default("") }).catchall(z.any()).describe("A group of addresses judged to belong to one actor, with its evidence.\n\nThe persisted twin of ``address_clustering.models.AddressCluster``: the engine\nproduces that in memory, a service converts it to this. Kept as a separate type\nrather than shared, because commons must not depend on the engine, and because\nthe two have genuinely different jobs \u2014 the engine's carries the full member\nlist it just computed, this one carries tenancy, keys, audit and a pointer to\nthe run that produced it.");
var AddressClusterMemberRowSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "cluster_id": z.string().default(""), "node_id": z.string().default(""), "address": z.string().default(""), "chain_id": z.string().default(""), "role": z.any().default("member"), "confidence": z.number().gte(0).lte(1).default(0), "link_kinds": z.array(z.string()).optional(), "run_id": z.union([z.string(), z.null()]).default(null), "updated_at": z.union([z.number().int(), z.null()]).default(null), "schema_version": z.string().default("1"), "cluster_pk": z.string().default(""), "member_sk": z.string().default(""), "address_pk": z.string().default(""), "cluster_sk": z.string().default("") }).catchall(z.any()).describe(`One address's membership of one cluster.

Exists as its own table for two reasons. A cluster's membership is unbounded, so
it cannot live inside the cluster item. And the query the product asks first is
the *reverse* one \u2014 "this address turned up in a transaction; is it part of
anything we know?" \u2014 which needs the address as a partition key, not a scan of
every cluster's member list.

Carries no ABAC fields: visibility is a property of the cluster, so a reader
resolves the cluster row and checks there. Duplicating it per member would give
fifty thousand rows to keep in sync on every share change.`);
var RelatedAddressLinkRowSchema = z.object({ "owner_user_id": z.union([z.string(), z.null()]).default(null), "visible_to_user_ids": z.array(z.string()).optional(), "visible_to_team_ids": z.array(z.string()).optional(), "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "a_id": z.string().default(""), "b_id": z.string().default(""), "a_address": z.string().default(""), "b_address": z.string().default(""), "a_chain_id": z.string().default(""), "b_chain_id": z.string().default(""), "chain_ids": z.array(z.string()).optional(), "kind": z.string().default(""), "same_actor": z.boolean().default(true), "deterministic": z.boolean().default(false), "confidence": z.number().gte(0).lte(1).default(0), "description": z.string().default(""), "values": z.record(z.string(), z.any()).optional(), "evidence": z.array(z.any()).optional(), "explanation": z.array(z.string()).optional(), "tx_count": z.number().int().default(0), "forward_count": z.number().int().default(0), "reverse_count": z.number().int().default(0), "value_raw_total": z.union([z.string(), z.null()]).default("0"), "asset_values_raw": z.record(z.string(), z.string()).optional(), "transaction_ids": z.union([z.array(z.string()), z.null()]).default(null), "flow_edges": z.array(z.any()).optional(), "seed_evidence": z.record(z.string(), z.any()).optional(), "rejection_reason": z.union([z.string(), z.null()]).default(null), "first_seen_block": z.union([z.number().int(), z.null()]).default(null), "last_seen_block": z.union([z.number().int(), z.null()]).default(null), "tx_hash": z.union([z.string(), z.null()]).default(null), "cluster_id": z.union([z.string(), z.null()]).default(null), "run_id": z.union([z.string(), z.null()]).default(null), "policy_fingerprint": z.string().default(""), "updated_at": z.union([z.number().int(), z.null()]).default(null), "schema_version": z.string().default("1"), "link_pk": z.string().default(""), "link_sk": z.string().default(""), "peer_pk": z.string().default(""), "peer_sk": z.string().default(""), "cluster_pk": z.union([z.string(), z.null()]).default(null) }).catchall(z.any()).describe('One evidenced relationship between two addresses.\n\nThe atom of the whole feature. A cluster is a conclusion drawn from these, and\nconclusions change: retune a weight, add an exclusion, and the same links\nproduce different clusters. So the links are persisted in their own right, keyed\nby endpoint rather than by cluster, and outlive any particular grouping of them.\n\nStored once in canonical endpoint order (``a_id`` < ``b_id``) rather than twice.\nA relationship is undirected, and writing both directions would double the rows\nand create two places for the evidence to disagree. The cost is that "every link\ntouching X" is two queries \u2014 the base partition where X is ``a_id``, and\n``by-peer-index`` where X is ``b_id``.');
var AddressClusterRunRowSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "run_id": z.string().default(""), "resume_available": z.boolean().default(false), "continuation_count": z.number().int().default(0), "progress": z.record(z.string(), z.any()).optional(), "trigger": z.any().default("adhoc"), "status": z.any().default("queued"), "seed_node_id": z.string().default(""), "seed_address": z.string().default(""), "seed_chain_id": z.string().default(""), "chain_ids": z.array(z.string()).optional(), "max_hops": z.number().int().default(0), "requested_by": z.union([z.string(), z.null()]).default(null), "correlation_id": z.union([z.string(), z.null()]).default(null), "policy": z.record(z.string(), z.any()).optional(), "policy_fingerprint": z.string().default(""), "engine_version": z.string().default(""), "request_fingerprint": z.string().default(""), "excluded_ids": z.array(z.string()).optional(), "address_count": z.number().int().default(0), "pairs_evaluated": z.number().int().default(0), "pairs_linked": z.number().int().default(0), "pairs_vetoed": z.number().int().default(0), "cluster_count": z.number().int().default(0), "singleton_count": z.number().int().default(0), "seed_cluster_id": z.union([z.string(), z.null()]).default(null), "seed_actor_id": z.union([z.string(), z.null()]).default(null), "seed_excluded_by": z.union([z.string(), z.null()]).default(null), "result_fingerprint": z.string().default(""), "fingerprint_version": z.number().int().default(0), "application_version": z.union([z.string(), z.null()]).default(null), "deployment_colour": z.union([z.string(), z.null()]).default(null), "sources_used": z.array(z.string()).optional(), "source_stats": z.record(z.string(), z.any()).optional(), "truncated": z.boolean().default(false), "duration_ms": z.union([z.number().int(), z.null()]).default(null), "started_at": z.union([z.number().int(), z.null()]).default(null), "finished_at": z.union([z.number().int(), z.null()]).default(null), "artifact_bucket": z.union([z.string(), z.null()]).default(null), "artifact_key": z.union([z.string(), z.null()]).default(null), "errors": z.array(z.string()).optional(), "expires_at": z.union([z.number().int(), z.null()]).default(null), "schema_version": z.string().default("1"), "run_sk": z.string().default(""), "seed_pk": z.union([z.string(), z.null()]).default(null) }).catchall(z.any()).describe('One execution of the clustering engine: what was asked, and under what rules.\n\nA cluster without a run is an undated assertion. This row is what makes one\nreproducible \u2014 the policy, the engine version, the exclusion set, the data\nsources consulted and whether they answered completely \u2014 and what lets the\nad-hoc path distinguish "nobody has asked about this address" from "we asked,\nand the answer was that it stands alone".');
var AddressActorRowSchema = z.object({ "owner_user_id": z.union([z.string(), z.null()]).default(null), "visible_to_user_ids": z.array(z.string()).optional(), "visible_to_team_ids": z.array(z.string()).optional(), "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "actor_id": z.string().default(""), "founding_node_id": z.string().default(""), "merged_into": z.union([z.string(), z.null()]).default(null), "merged_at": z.union([z.number().int(), z.null()]).default(null), "absorbed": z.array(z.string()).optional(), "rehome_pending": z.boolean().default(false), "member_count": z.number().int().default(0), "member_preview": z.array(z.string()).optional(), "chain_ids": z.array(z.string()).optional(), "link_count": z.number().int().default(0), "confidence": z.number().gte(0).lte(1).default(0), "confidence_max": z.number().gte(0).lte(1).default(0), "weakest_link": z.union([z.any(), z.null()]).default(null), "strength_anchor_node_id": z.union([z.string(), z.null()]).default(null), "run_id": z.union([z.string(), z.null()]).default(null), "policy_fingerprint": z.string().default(""), "engine_version": z.string().default(""), "updated_at": z.union([z.number().int(), z.null()]).default(null), "created_by": z.union([z.string(), z.null()]).default(null), "schema_version": z.string().default("1"), "actor_sk": z.string().default("") }).catchall(z.any()).describe('The standing identity of one actor: stable, and merged rather than replaced.\n\nA cluster is what one run concluded, and its id is a hash of its membership \u2014\nso it cannot grow. Find one more address and you have written a *different*\ncluster, which is honest about the claim and useless as an identity: the two\nrows compete, the reverse lookup has to rank them, and which one a reader is\nshown depends on the order the runs happened to go in. This row is the fix.\nIts id is minted once and never derived from who is in it, so membership can\nchange underneath a stable identity.\n\nTwo actors that turn out to be one are **merged**, not re-published. The\nyounger row keeps a forward pointer (:attr:`merged_into`) and stops being\nlive; the older absorbs the membership. Union is commutative, associative and\nidempotent, which is exactly the property the old design lacked and the reason\nthe stored answer no longer depends on the order the questions were asked in.\nThe pointer is kept forever rather than deleted, so an id that was shown to\nsomeone \u2014 in a URL, an export, another service\'s row \u2014 still resolves.\n\n**Only the structural tier merges.** A deployment, a sweep, a burst of payouts,\na bridge leg with matching amounts: facts about what an address did, which\nmore looking can add to but not contradict. The flow tier ("these two\ntransacted enough to be worth linking") is a *verdict* under a threshold\ncomputed over whatever history a walk happened to fetch, so it moves with the\nbudget \u2014 it belongs on :class:`RelatedAddressLinkRow` as relatedness and must\nnever move identity.');
var AddressActorMemberRowSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "actor_id": z.string().default(""), "node_id": z.string().default(""), "address": z.string().default(""), "chain_id": z.string().default(""), "join_kinds": z.array(z.string()).optional(), "confidence": z.number().gte(0).lte(1).default(0), "joined_at": z.union([z.number().int(), z.null()]).default(null), "run_id": z.union([z.string(), z.null()]).default(null), "updated_at": z.union([z.number().int(), z.null()]).default(null), "schema_version": z.string().default("1"), "member_pk": z.string().default(""), "member_sk": z.string().default(""), "actor_pk": z.string().default("") }).catchall(z.any()).describe("One address's place in one actor \u2014 and the row that enforces \"only one\".\n\nThe primary key is the **address alone** (``member_pk``), with no sort key, so\nthe table cannot physically hold an address in two actors. That is the\ninvariant the whole design turns on, and the reason it is expressed as a key\nrather than as a rule in the service: a rule has to be remembered by every\nwriter, and this one had already been broken once by a second code path.\n\nThe point read on that key is the ``find`` of the union-find, so it can be\nmade strongly consistent when a merge is about to be decided. ``by-actor-index``\nis the reverse \u2014 the actor's membership \u2014 and is eventually consistent like\nany GSI, which is fine for rendering a list and not fine for deciding a merge.\n\nCarries no ABAC fields for the same reason the cluster's member row does not:\nvisibility belongs to the actor, and duplicating it per address would be\nfifty thousand rows to update on a share change.");
var EntitySchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "user_id": z.union([z.string(), z.null()]).default(null), "name": z.string(), "entity_type": z.any().default("other"), "description": z.union([z.string(), z.null()]).default(null), "website": z.union([z.string(), z.null()]).default(null), "twitter": z.union([z.string(), z.null()]).default(null), "risk_level": z.union([z.any(), z.null()]).default(null), "logo_s3_uri": z.union([z.string(), z.null()]).default(null), "color": z.union([z.string(), z.null()]).default(null), "parent_entity_id": z.union([z.string(), z.null()]).default(null), "related_entity_ids": z.array(z.string()).optional(), "metadata": z.record(z.string(), z.any()).optional(), "source": z.union([z.string(), z.null()]).default(null), "confidence_scored": z.union([z.boolean(), z.null()]).default(null), "confidence": z.number().gte(0).lte(1).default(1), "evidence": z.array(z.any()).optional(), "explanation": z.array(z.string()).optional(), "is_human_approved": z.boolean().default(false), "system_default": z.boolean().default(false), "lifecycle": z.any().default("candidate"), "aliases": z.array(z.string()).optional(), "severity": z.union([z.any(), z.null()]).default(null), "created_by": z.union([z.string(), z.null()]).default(null), "created_by_service": z.union([z.string(), z.null()]).default(null), "updated_by": z.union([z.string(), z.null()]).default(null), "updated_at": z.union([z.number().int(), z.null()]).default(null), "updated_by_service": z.union([z.string(), z.null()]).default(null), "valid_from": z.union([z.number().int(), z.null()]).default(null), "valid_to": z.union([z.number().int(), z.null()]).default(null), "applies_to_chains": z.array(z.string()).describe("Chains this entity APPLIES to \u2014 its applicability scope (hex ``chain_id`` or slug). Empty = all chains (chain-agnostic). This is what the catalog editor's \u201CApplies to chains\u201D control writes (via ``get_catalog_chains`` / ``set_catalog_chains``) and what the per-chain address-overlay reconcile materialises; empty falls back to the product default chain set at materialisation time. Distinct from ``primary_chains`` (a presentation hint), which MUST be a subset of this when this is non-empty.").optional(), "primary_chains": z.array(z.string()).describe("Chains this entity is primarily / canonically deployed on (hex ``chain_id`` or slug). A presentation hint \u2014 a subset of ``applies_to_chains`` (the applicability scope) \u2014 empty means none flagged.").optional(), "overrides_by_chain": z.record(z.string(), z.any()).describe("Per-chain presentation overrides keyed by hex ``chain_id`` (``0x1``, ``0x89``, \u2026). The Phase 3 mechanism for a single entity to surface a chain-flavoured logo / name / colour without forking the entity row identity. See :class:`commons.interfaces.models.field_overrides.FieldOverrides`.").optional(), "name_lower": z.string().default("") }).catchall(z.any()).describe("Canonical entity record.\n\n``id`` defaults to the deterministic SHA256 id from\n:func:`commons.catalog.ids.generate_default_entity_id` when omitted, so\ntwo callers writing the same name produce the same row.");
var EntityHintSchema = z.object({ "address": z.string().min(1).describe("Address the hint is about. EVM addresses are lowercased by the normalize step; non-EVM addresses keep their native casing."), "chain_id": z.union([z.string(), z.null()]).describe("Chain the address belongs to. ``None`` only when the upstream provider doesn't disclose a chain (rare for transaction-attached hints; common for entity-directory hints which are intentionally chain-agnostic).").default(null), "provider_id": z.any().describe("Provider that emitted the hint. Accepts a :class:`ProviderId` directly or a free-form string that :func:`normalize_provider_id` lifts into the enum. Unknown strings fall through to :attr:`ProviderId.UNKNOWN` with a one-shot deprecation warning at the call-site (the warn-once helper keeps the log noise bounded even on hot ingest loops)."), "source": z.string().describe('Structural location of the hint within the raw payload. Free-form so each provider can describe its shape: ``"from_address"``, ``"to_address"``, ``"internal_tx.to[3]"``, ``"log.address[7]"``, etc. Defaults to ``"raw_payload"`` for hints that arrived outside a known structural field.').default("raw_payload"), "entity_name": z.union([z.string(), z.null()]).describe('Display name the provider attached, e.g. ``"Binance Hot Wallet 7"``. ``None`` when the provider supplied only a label / logo without an entity.').default(null), "entity_logo": z.union([z.string(), z.null()]).describe("URL of the entity's logo when the provider supplied one.").default(null), "label": z.union([z.string(), z.null()]).describe('Address-scoped label, e.g. ``"Hot Wallet"`` / ``"Deployer"``. Distinct from ``entity_name`` because many addresses carry a label without belonging to a named entity ("contract creator", "router", "safe owner").').default(null), "confidence": z.number().gte(0).lte(1).describe("Provider-reported confidence in the hint, clamped to [0, 1]. Phase 8 reconciler uses this to weight competing hints from multiple providers.").default(1), "metadata": z.record(z.string(), z.any()).describe("Provider-specific extras the typed fields don't cover (category, twitter handle, country tags, \u2026).").optional() }).describe("One provider-attributed entity / label / logo hint on a transaction.\n\nConstructed by the normalize step from the raw provider payload and\nstamped onto :attr:`CanonicalTransaction.tx_entity_hints`. Carries\nenough provenance for the catalog discoverer + reconciler to make\na per-provider, per-chain, per-side decision without re-parsing the\nraw payload.");
var TagSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "user_id": z.union([z.string(), z.null()]).default(null), "name": z.string(), "description": z.union([z.string(), z.null()]).default(null), "color": z.union([z.string(), z.null()]).default(null), "icon": z.union([z.string(), z.null()]).default(null), "category": z.union([z.string(), z.null()]).default(null), "metadata": z.record(z.string(), z.any()).optional(), "source": z.union([z.string(), z.null()]).default(null), "confidence_scored": z.union([z.boolean(), z.null()]).default(null), "confidence": z.number().gte(0).lte(1).default(1), "evidence": z.array(z.any()).optional(), "explanation": z.array(z.string()).optional(), "is_human_approved": z.boolean().default(false), "lifecycle": z.any().default("candidate"), "aliases": z.array(z.string()).optional(), "severity": z.union([z.any(), z.null()]).default(null), "created_by": z.union([z.string(), z.null()]).default(null), "created_by_service": z.union([z.string(), z.null()]).default(null), "updated_by": z.union([z.string(), z.null()]).default(null), "updated_at": z.union([z.number().int(), z.null()]).default(null), "updated_by_service": z.union([z.string(), z.null()]).default(null), "valid_from": z.union([z.number().int(), z.null()]).default(null), "valid_to": z.union([z.number().int(), z.null()]).default(null), "applies_to_chains": z.array(z.string()).describe("Chains this tag APPLIES to \u2014 its applicability scope (hex ``chain_id`` or slug). Empty = all chains (chain-agnostic). This is what the catalog editor's \u201CApplies to chains\u201D control writes (via ``get_catalog_chains`` / ``set_catalog_chains``) and what the per-chain address-overlay reconcile materialises; empty falls back to the product default chain set at materialisation time. Distinct from ``primary_chains`` (a presentation hint), which MUST be a subset of this when this is non-empty.").optional(), "primary_chains": z.array(z.string()).describe("Chains this tag is primarily / canonically used on (hex ``chain_id`` or slug). A presentation hint \u2014 a subset of ``applies_to_chains`` (the applicability scope) \u2014 empty means none flagged.").optional(), "overrides_by_chain": z.record(z.string(), z.any()).describe("Per-chain presentation overrides keyed by hex ``chain_id`` (``0x1``, ``0x89``, \u2026). Phase 3 \u2014 see :class:`commons.interfaces.models.field_overrides.FieldOverrides`. Defaults to ``{}`` so existing tags serialise unchanged.").optional(), "system_default": z.boolean().default(false), "name_lower": z.string().default("") }).catchall(z.any()).describe("A tag (e.g. ``call:transfer``, ``protocol:uniswap``).");
var LabelSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "user_id": z.union([z.string(), z.null()]).default(null), "name": z.string(), "description": z.union([z.string(), z.null()]).default(null), "severity": z.union([z.any(), z.null()]).default(null), "risk_level": z.union([z.any(), z.null()]).default(null), "address": z.union([z.string(), z.null()]).default(null), "chain": z.union([z.string(), z.null()]).default(null), "color": z.union([z.string(), z.null()]).default(null), "icon": z.union([z.string(), z.null()]).default(null), "metadata": z.record(z.string(), z.any()).optional(), "source": z.union([z.string(), z.null()]).default(null), "confidence_scored": z.union([z.boolean(), z.null()]).default(null), "confidence": z.number().gte(0).lte(1).default(1), "evidence": z.array(z.any()).optional(), "explanation": z.array(z.string()).optional(), "is_human_approved": z.boolean().default(false), "lifecycle": z.any().default("candidate"), "aliases": z.array(z.string()).optional(), "created_by": z.union([z.string(), z.null()]).default(null), "created_by_service": z.union([z.string(), z.null()]).default(null), "updated_by": z.union([z.string(), z.null()]).default(null), "updated_at": z.union([z.number().int(), z.null()]).default(null), "updated_by_service": z.union([z.string(), z.null()]).default(null), "valid_from": z.union([z.number().int(), z.null()]).default(null), "valid_to": z.union([z.number().int(), z.null()]).default(null), "applies_to_chains": z.array(z.string()).describe("Chains this label APPLIES to \u2014 its applicability scope (hex ``chain_id`` or slug). Empty = all chains (chain-agnostic). This is what the catalog editor's \u201CApplies to chains\u201D control writes (via ``get_catalog_chains`` / ``set_catalog_chains``) and what the per-chain address-overlay reconcile materialises; empty falls back to the product default chain set at materialisation time. Distinct from ``primary_chains`` (a presentation hint), which MUST be a subset of this when this is non-empty.").optional(), "primary_chains": z.array(z.string()).describe("Chains this label is primarily / canonically used on (hex ``chain_id`` or slug). A presentation hint \u2014 a subset of ``applies_to_chains`` (the applicability scope) \u2014 empty means none flagged.").optional(), "overrides_by_chain": z.record(z.string(), z.any()).describe("Per-chain presentation overrides keyed by hex ``chain_id``. Phase 3 \u2014 see :class:`commons.interfaces.models.field_overrides.FieldOverrides`. Defaults to ``{}`` so existing labels serialise unchanged.").optional(), "system_default": z.boolean().default(false), "name_lower": z.string().default("") }).catchall(z.any()).describe("A free-form, scope-able label.\n\n``id`` defaults to either\n:func:`commons.catalog.ids.generate_default_label_id` (global label) or\n:func:`generate_default_address_label_id` (address-bound), depending on\nwhether :attr:`address` is provided.");
var GroupSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "user_id": z.union([z.string(), z.null()]).default(null), "name": z.string(), "description": z.union([z.string(), z.null()]).default(null), "member_tag_ids": z.array(z.string()).optional(), "member_label_ids": z.array(z.string()).optional(), "member_entity_ids": z.array(z.string()).optional(), "member_transaction_group_ids": z.array(z.string()).describe("Ids of machine-generated transaction groups (:class:`commons.interfaces.models.transaction_group.TransactionGroup`, the tx_grouping engine output) bundled under this curated group. Lets a human group nest auto-detected bridge/saga groups so the unified ``/groups`` view can expand them into their member transactions. The transaction-group rows live in their own tables (read via :mod:`commons.aws.dynamodb.queries.transaction_group_queries`); this is a reference-by-id, mirroring the other ``member_*_ids``.").optional(), "color": z.union([z.string(), z.null()]).default(null), "icon": z.union([z.string(), z.null()]).default(null), "metadata": z.record(z.string(), z.any()).optional(), "source": z.union([z.string(), z.null()]).default(null), "confidence_scored": z.union([z.boolean(), z.null()]).default(null), "confidence": z.number().gte(0).lte(1).default(1), "evidence": z.array(z.any()).optional(), "explanation": z.array(z.string()).optional(), "is_human_approved": z.boolean().default(false), "lifecycle": z.any().default("candidate"), "aliases": z.array(z.string()).optional(), "severity": z.union([z.any(), z.null()]).default(null), "created_by": z.union([z.string(), z.null()]).default(null), "created_by_service": z.union([z.string(), z.null()]).default(null), "updated_by": z.union([z.string(), z.null()]).default(null), "updated_at": z.union([z.number().int(), z.null()]).default(null), "updated_by_service": z.union([z.string(), z.null()]).default(null), "valid_from": z.union([z.number().int(), z.null()]).default(null), "valid_to": z.union([z.number().int(), z.null()]).default(null), "applies_to_chains": z.array(z.string()).describe("Chains this group APPLIES to \u2014 its applicability scope (hex ``chain_id`` or slug). Empty = all chains (chain-agnostic). This is what the catalog editor's \u201CApplies to chains\u201D control writes (via ``get_catalog_chains`` / ``set_catalog_chains``) and what the per-chain address-overlay reconcile materialises; empty falls back to the product default chain set at materialisation time. Distinct from ``primary_chains`` (a presentation hint), which MUST be a subset of this when this is non-empty.").optional(), "primary_chains": z.array(z.string()).describe("Chains this group is primarily / canonically used on (hex ``chain_id`` or slug). A presentation hint \u2014 a subset of ``applies_to_chains`` (the applicability scope) \u2014 empty means none flagged.").optional(), "overrides_by_chain": z.record(z.string(), z.any()).describe("Per-chain presentation overrides keyed by hex ``chain_id``. Phase 3 \u2014 see :class:`commons.interfaces.models.field_overrides.FieldOverrides`.").optional(), "system_default": z.boolean().default(false), "name_lower": z.string().default("") }).catchall(z.any()).describe("A grouping that bundles tags/labels/entities together.");
var CatalogAssignmentSchema = z.object({ "owner_user_id": z.union([z.string(), z.null()]).default(null), "visible_to_user_ids": z.array(z.string()).optional(), "visible_to_team_ids": z.array(z.string()).optional(), "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).describe("Audit \xA77 #1 (task N): the strict four-level hierarchy is ``customer \u2192 org \u2192 tenant \u2192 user``. This field is **additive** during the Stage-2 dual-write window: when populated, the v2 shadow keys (:attr:`by_target_sk_v2`, :attr:`by_catalog_pk_v2`) are emitted using :func:`tenant_user_path` so the eventual GSI cut-over is a no-op. When ``None`` (default), the v2 shadows collapse the customer segment to the ``*`` sentinel \u2014 still well-formed for ``begins_with`` reads, just less selective. Callers that already know the customer scope should pass it through to keep the v2 SK partition selective.").default(null), "customer_org_tenant_pk_v2": z.string().default(""), "scope": z.string(), "scope_target_id": z.string(), "catalog_kind": z.string(), "catalog_id": z.string(), "chain_id": z.union([z.string(), z.null()]).default(null), "user_id": z.union([z.string(), z.null()]).describe("Phase 3: when set, the assignment is **private to this user** within ``org_id_tenant_id``. The SK includes the user segment so user-scoped rows do not collide with the tenant-wide assignment for the same ``(scope, chain, target, catalog row)``. ``None`` (default) means tenant-wide, fully back-compatible with pre-Phase-3 rows.").default(null), "asserted_at": z.union([z.number().int(), z.null()]).default(null), "sort_ts": z.union([z.number().int(), z.null()]).describe("Newest-first sort discriminant (Unix seconds) embedded into the ``by_catalog_sk`` reverse-time segment so a catalog row's resources page newest-first off ``by-catalog-v2-index`` without a separate GSI. For transaction/event scopes set this to the resource's ``block_timestamp`` so the reverse feed merges coherently with the by-chain-time / by-entity-time transaction feeds. When ``None`` it falls back to ``created_at`` (the row's write time) \u2014 fine for resources without an on-chain timestamp.").default(null), "valid_from": z.union([z.number().int(), z.null()]).default(null), "valid_to": z.union([z.number().int(), z.null()]).default(null), "window_keyed": z.boolean().describe("Whether the validity window is part of this row's IDENTITY rather than an attribute of it. Set by writers of *dated observations* \u2014 behaviour asserted over a period, e.g. 'acted as a market maker through Q1'. The window then joins the sort key (and the id hash), so a later period is a second row instead of a write that erases the first.\n\nOff by default, and deliberately not inferred from ``valid_from`` being set: an operator editing the dates on an attribution is correcting one claim and expects one row, and the provider discovery path stamps ``valid_from=as_of`` on every tag it emits \u2014 keying on that would write a new row per transaction.\n\nA verdict that *changes* is not a new window on the same period: supersede it (:attr:`superseded_by`) so the record still says what the platform believed and when.").default(false), "superseded_by": z.union([z.string(), z.null()]).default(null), "superseded_at": z.union([z.number().int(), z.null()]).default(null), "confidence_scored": z.union([z.boolean(), z.null()]).default(null), "confidence": z.number().gte(0).lte(1).default(1), "source": z.string().default(""), "evidence": z.array(z.any()).optional(), "explanation": z.array(z.string()).optional(), "evidence_refs": z.array(z.string()).optional(), "inherited_from_entity_id": z.union([z.string(), z.null()]).default(null), "is_human_approved": z.boolean().default(false), "metadata": z.record(z.string(), z.any()).optional(), "by_target_pk": z.string().default(""), "by_catalog_sk": z.string().default(""), "by_target_sk_v2": z.string().default(""), "by_catalog_pk_v2": z.string().default(""), "by_author_pk": z.union([z.string(), z.null()]).default(null), "by_author_sk": z.union([z.string(), z.null()]).default(null) }).catchall(z.any()).describe('Generic assignment row.\n\nUse the typed subclasses (:class:`AddressTagAssignment`,\n:class:`EntityLabelAssignment`, etc.) for stronger typing; this\nbase class exists so the helper / reconciler can write a generic\niterable without per-kind branches.\n\nOverlay sharing (ABAC)\n----------------------\n\nAn assignment IS the per-user / per-tenant / per-team *overlay* on a\nshared catalog target (e.g. the global ``(chain, address)`` row). The\ntarget row itself is a global canonical registry and therefore carries\nno ownership/visibility \u2014 those live HERE, on the overlay, via the\n:class:`~commons.interfaces.models.abac.ABACFields` mixin\n(``owner_user_id`` / ``visible_to_user_ids`` / ``visible_to_team_ids``).\nThat lets two users keep distinct private overlays on the same address\n(user A\'s "my wallet" tags/logo vs user B\'s "User A\'s wallet" label),\nand lets a team share an overlay \u2014 :mod:`commons.permissions.abac`\n(``can_view`` / ``filter_viewable``) reads these carriers directly off\nthe row. ``user_id`` still controls KEY placement (tenant-wide vs\nuser-private partition); ``owner_user_id`` / ``visible_to_*`` control\nVISIBILITY \u2014 the two are complementary.');
var FieldOverridesSchema = z.object({ "name": z.union([z.string(), z.null()]).describe("Override the displayed name for this chain. Example: a Safe module on Polygon may want to display ``Safe (Polygon)`` while the entity name stays ``Safe`` globally.").default(null), "description": z.union([z.string(), z.null()]).describe("Optional per-chain description override (long-form).").default(null), "color": z.union([z.string(), z.null()]).describe("Hex / CSS colour override. UIs use this to colour-code the row in chain-specific lists.").default(null), "icon": z.union([z.string(), z.null()]).describe("Per-chain icon identifier (URL or token).").default(null), "logo": z.union([z.string(), z.null()]).describe("Per-chain logo URI. The single most common override: most bridged tokens / entities want a chain-flavoured logo.").default(null), "aliases": z.union([z.array(z.string()), z.null()]).describe("Per-chain aliases (e.g. ``USDC.e`` on Avalanche vs ``USDC`` everywhere else). When set, **replaces** the base list \u2014 we do not implicitly union so the override is fully predictable.").default(null), "metadata": z.union([z.record(z.string(), z.any()), z.null()]).describe("Free-form per-chain metadata overrides. When set, the dict is *merged* over the base metadata (per-key overlay) rather than replacing the whole map \u2014 metadata is a bag, not a single value.").default(null) }).strict().describe("Per-chain override of presentation fields on a catalog row.\n\nFields default to ``None`` \u2014 only set the ones that should override\nthe base catalog row for the chain in question. Unset fields fall\nthrough to the base row's values via :func:`apply_overrides`.\n\nThe fields covered here are deliberately the *presentation* surface\nevery catalog row family (Tag / Label / Entity / Group) shares.\nFamily-specific overrides (e.g. ``Entity.entity_type``) are not\noverridable on purpose \u2014 overriding identity-shaping fields would\nquietly partition entities by chain which is the exact failure mode\nwe are designing against.");
var TransactionRowSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.union([z.string(), z.null()]).default("*"), "tenant_id": z.union([z.string(), z.null()]).default("*"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default("*"), "customer_org_tenant_pk_v2": z.string().default(""), "user_id": z.union([z.string(), z.null()]).default("*"), "canonical_tx_id": z.string().min(1), "chain_id": z.string().min(1), "tx_hash": z.string().default(""), "block_number": z.union([z.number().int(), z.null()]).default(null), "block_hash": z.union([z.string(), z.null()]).default(null), "block_timestamp": z.number().int().default(0), "transaction_index": z.union([z.number().int(), z.null()]).default(null), "type": z.any().default("transaction"), "finality": z.any().default("unknown"), "decode_confidence": z.union([z.number(), z.null()]).default(null), "decode_coverage": z.union([z.number(), z.null()]).default(null), "decode_counts": z.record(z.string(), z.number().int()).optional(), "explanation": z.array(z.string()).optional(), "abi_source": z.union([z.string(), z.null()]).default(null), "abi_confidence": z.union([z.number(), z.null()]).default(null), "abi_explanation": z.array(z.string()).optional(), "participant_summary": z.record(z.string(), z.any()).optional(), "total_value_raw": z.string().default("0"), "amount_hidden": z.boolean().default(false), "counts": z.record(z.string(), z.number().int()).optional(), "value_usd": z.union([z.number(), z.null()]).default(null), "price_quality": z.record(z.string(), z.any()).optional(), "primary_entity_id": z.union([z.string(), z.null()]).default(null), "primary_protocol_id": z.union([z.string(), z.null()]).default(null), "protocol_ids": z.array(z.string()).optional(), "s3_key": z.union([z.string(), z.null()]).default(null), "s3_etag": z.union([z.string(), z.null()]).default(null), "body_digest": z.union([z.string(), z.null()]).default(null), "output_digest": z.union([z.string(), z.null()]).default(null), "body_bytes": z.number().int().default(0), "body_compression": z.string().default("none"), "body_offset": z.union([z.number().int(), z.null()]).default(null), "body_length": z.union([z.number().int(), z.null()]).default(null), "body_slices": z.record(z.string(), z.record(z.string(), z.any())).optional(), "pipeline_run_id": z.union([z.string(), z.null()]).default(null), "tx_correlation_id": z.union([z.string(), z.null()]).default(null), "config_hash": z.union([z.string(), z.null()]).default(null), "pipeline_id": z.union([z.string(), z.null()]).default(null), "pipeline_name": z.union([z.string(), z.null()]).default(null), "updated_at": z.union([z.number().int(), z.null()]).default(null), "deployment_colour": z.union([z.string(), z.null()]).default(null), "application_version": z.union([z.string(), z.null()]).default(null), "decoder_version": z.union([z.string(), z.null()]).default(null), "schema_version": z.string().default("1"), "customer_id_org_id_tenant_id": z.string().default(""), "user_id_rev_ts_chain_id_canonical_tx_id_type": z.string().default(""), "customer_org_tenant_chain": z.string().default(""), "customer_org_tenant_entity": z.string().default(""), "customer_org_tenant_protocol": z.string().default(""), "rev_ts_canonical_tx_id": z.string().default(""), "chain_hash": z.string().default(""), "tx_hash_scope_sk": z.string().default("") }).catchall(z.any()).describe("Canonical DDB shape for a tenant-scoped transaction list row.\n\nSee module docstring for the full key schema, GSI list, and field\nrationale. Constructed by :class:`PersistStep` per canonical tx;\nconsumed by the read facade (``commons.aws.dynamodb.queries`` \u2014\nPhase 11C) and any future API repo that needs typed rows without\ndepending on ``authe_tx_pipeline``.\n\nConstruction conventions:\n\n- Every shadow attribute (``customer_id_org_id_tenant_id``,\n  ``user_id_rev_ts_chain_id_canonical_tx_id_type``,\n  ``customer_org_tenant_chain``, ``customer_org_tenant_entity``,\n  ``customer_org_tenant_protocol``, ``rev_ts_canonical_tx_id``) is\n  re-derived by the\n  :meth:`_populate_keys` validator from the source fields. Callers\n  pass tenancy + identity + ``primary_entity_id`` and the row\n  computes the keys; they do not need to (and should not) build\n  the composite strings by hand.\n- ``block_timestamp`` is Unix seconds (matching\n  :class:`EnvelopeHeader.block_timestamp`); the validator converts\n  to nanoseconds before feeding :func:`reverse_time_segment` so\n  two txs in the same second don't collide on the SK.");
var TxParticipantSchema = z.object({ "address": z.any().describe("The participating address, scoped to its chain."), "role": z.any().describe("Role this participant played in the tx.").default("unknown"), "provenance": z.any().describe("Where the pipeline discovered this participant.").default("tx_root"), "user_op_index": z.union([z.number().int().gte(0), z.null()]).describe("Position inside the ``handleOps`` user-op array for ERC-4337 participants.").default(null), "internal_tx_index": z.union([z.number().int().gte(0), z.null()]).describe("Position inside the ``internal_transactions`` array.").default(null), "log_index": z.union([z.number().int().gte(0), z.null()]).describe("Position inside the ``logs`` array.").default(null), "multisend_index": z.union([z.number().int().gte(0), z.null()]).describe("Position inside the Safe MultiSend operations array.").default(null), "on_behalf_of": z.union([z.any(), z.null()]).describe("The *beneficiary* of this action. For Safe MultiSend recipients, the Safe; for aggregator users, the EOA; for ERC-4337 user-ops, the sender.").default(null), "acts_as": z.union([z.any(), z.null()]).describe("The *delegator* whose code executed. For proxy delegatecall the implementation address; for ERC-4337 the EntryPoint.").default(null), "metadata": z.record(z.string(), z.any()).describe("Per-role free-form data (Safe ``threshold``, paymaster ``type``, user-op ``nonce``, \u2026). Kept small \u2014 the persist step caps this at 1 KB serialised per participant.").optional() }).strict().describe("A single participant in a transaction.\n\nSee :mod:`commons.interfaces.models.participant` module docstring for\nthe design rationale. This class is intentionally not a\n:class:`DynamoTableModel` \u2014 it always lives inside another row.");
var TokenSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "chain_id": z.string().min(1), "family": z.any().default("evm"), "kind": z.any().default("unknown"), "category": z.any().describe("Economic/display role (stablecoin, wrapped_native, bridged, ...). Lets the UI group the long tail of variants without re-deriving it from the symbol.").default("unknown"), "identifier": z.string().min(1).describe("Lowercased EVM contract address / Solana mint pubkey / ``native:{chain_id}`` for the chain's native coin. Doubles as the DDB SK so cross-chain joins by token id use a stable key."), "symbol": z.union([z.string().max(32), z.null()]).describe("Trading symbol (``USDC``, ``WETH``, ``WIF``).").default(null), "name": z.union([z.string().max(256), z.null()]).describe("Human-readable name (``USD Coin``, ``Bitcoin``).").default(null), "aliases": z.array(z.string()).describe('Alternate symbols (lowercased) for search + dedup of variants. A bridged ``USDC.e`` row carries ``["usdc"]`` so a lookup by the canonical ticker still finds it.').optional(), "decimals": z.union([z.number().int().gte(0).lte(36), z.null()]).describe("Number of decimal places for amount display. EVM ERC-20: the contract's ``decimals()``. Solana SPL: from the mint account. Native: chain default (18 for EVM, 9 for SOL, 8 for BTC).").default(null), "logo_uri": z.union([z.string(), z.null()]).describe("HTTPS / IPFS / S3 URI to a stable logo asset.").default(null), "description": z.union([z.string(), z.null()]).default(null), "is_verified": z.boolean().describe("Verified by a trustworthy registry (Uniswap default list, Solana Token List, etc.). Drives whether the UI shows a checkmark.").default(false), "total_supply": z.union([z.string(), z.null()]).describe("Total supply as a decimal string in base units (pre-decimals). String so very large supplies don't blow JSON / DDB number ranges.").default(null), "is_spam": z.boolean().describe("Provider spam / scam signal (e.g. Moralis ``possible_spam``). Lets the UI de-emphasise or hide likely-spam tokens without deleting the row (it may still be referenced by a historical transfer).").default(false), "security_score": z.union([z.number().int().gte(0).lte(100), z.null()]).describe("Provider security / trust score (0..100) when available (Moralis ``security_score``). Orthogonal to :attr:`is_verified`, which is a boolean registry-verification flag.").default(null), "categories": z.array(z.string()).describe("Free-form provider categories (``stablecoin``, ``defi``, ``meme`` \u2026) for discovery / filtering. Distinct from the single canonical :attr:`category` role; this is the raw multi-label provider view.").optional(), "links": z.record(z.string(), z.string()).describe("Off-chain links keyed by kind (``website``, ``twitter``, ``telegram`` \u2026). Display-only; never used for resolution.").optional(), "source": z.union([z.string(), z.null()]).describe("Provider id of the source that wrote this row (``coingecko``, ``solana_token_list``, ``manual``, ...).").default(null), "confidence": z.number().gte(0).lte(1).describe("Aggregated headline confidence across all evidence. Writers keep the highest seen value. Read it together with :attr:`confidence_scored` \u2014 the 1.0 default is the ABSENCE of a score, not a perfect one.").default(1), "confidence_scored": z.union([z.boolean(), z.null()]).describe("Whether :attr:`confidence` was actually computed for this row. ``None`` on every row written before scoring existed. A refresh policy that filtered on ``confidence`` alone would skip exactly those rows, because their default reads as maximum confidence.").default(null), "evidence": z.array(z.any()).describe("One entry per source that asserted this row, each with its own ``confidence`` / ``last_seen_at`` / ``reasons``.").optional(), "explanation": z.array(z.string()).describe('Glass-box "why" bullets rendered from :attr:`evidence` at write time (:func:`commons.evidence.explain`).').optional(), "is_human_approved": z.boolean().describe("``True`` once a human confirmed the row. A refresh must leave it alone: the person approved the whole row, including the fields they left empty.").default(false), "lifecycle": z.any().describe("Lifecycle state. Seeded rows land CANDIDATE; the confidence gate or a human promotes to VERIFIED.").default("candidate"), "updated_at": z.union([z.number().int(), z.null()]).describe("Unix seconds of the last write to this row. The clock a max-age refresh policy runs on. ``None`` means the row predates provenance and has never been refreshed \u2014 which a policy should treat as infinitely stale, not as missing data to skip.").default(null), "last_verified_at": z.union([z.number().int().gte(0), z.null()]).describe("Last successful seed verification, independent of unrelated edits.").default(null), "last_seen_at": z.union([z.number().int(), z.null()]).describe('Unix seconds a provider last CONFIRMED this token, which is not the same as :attr:`updated_at` (a write that changed nothing still bumps the latter). Kept apart so "nobody has looked in 90 days" and "nothing has changed in 90 days" stay different questions.').default(null), "canonical_ticker_id": z.union([z.string(), z.null()]).describe("Canonical ``AssetTicker.coin_id`` (e.g. ``coingecko:usd-coin``) this token is an incarnation of, for cross-chain dedup.").default(null), "not_found": z.boolean().describe("Negative-cache sentinel marker. ``True`` means 'looked up but no definition exists anywhere we asked' \u2014 never a real token.").default(false), "ttl": z.union([z.number().int(), z.null()]).describe("Unix-seconds DynamoDB TTL attribute. Only set on ``not_found`` sentinel rows so negative lookups expire (and are retried) after the window; real definitions leave it unset and never expire. Enabling TTL on this attribute is table infra (separate repo).").default(null), "base_identifier": z.union([z.string(), z.null()]).describe("Clean token identifier on an override row (the SK itself is the composite ``{base_identifier}#{override_scope}``). Unset on system rows, where ``identifier`` already is the clean id.").default(null), "override_scope": z.union([z.string(), z.null()]).describe("The ``commons.tenancy.scope`` storage key this override belongs to (``customer::cus_1``, ``org::cus_1::org_1``, ...). Non-null marks an override row; ``None`` is a system / global definition.").default(null), "patch": z.record(z.string(), z.any()).describe("Sparse field-level override payload deep-merged over the resolved base on an override row (only the keys the operator set, so model defaults never clobber the base). Empty on system rows.").optional(), "metadata": z.record(z.string(), z.any()).optional() }).catchall(z.any()).describe("Cross-family token row.");
var NftSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "chain_id": z.string().min(1), "family": z.any().default("evm"), "collection": z.string().min(1).describe("EVM contract address / Metaplex collection pubkey / BTC parent inscription id. Lowercased for EVM, raw for the others."), "token_id": z.string().min(1).describe("Per-NFT identifier \u2014 integer id (as string) on EVM, mint pubkey (base58) on Solana, inscription id on BTC."), "owner": z.union([z.string(), z.null()]).describe("Current owner address. Snapshot \u2014 refreshed by the ownership indexer; can lag a few blocks behind the chain.").default(null), "name": z.union([z.string().max(256), z.null()]).default(null), "description": z.union([z.string(), z.null()]).default(null), "image_uri": z.union([z.string(), z.null()]).describe("HTTPS / IPFS / Arweave URI to the rendered image asset.").default(null), "metadata_uri": z.union([z.string(), z.null()]).describe("Token URI (EIP-721 ``tokenURI`` / Metaplex ``uri``).").default(null), "collection_info": z.union([z.any(), z.null()]).describe("Embedded collection metadata when known. Optional so individual NFT rows don't carry redundant data; readers join from the collection cache.").default(null), "supply": z.union([z.string(), z.null()]).describe('Total minted supply for SFTs / ERC-1155. ``None`` or ``"1"`` for one-of-one ERC-721 / Metaplex NFTs.').default(null), "source": z.union([z.string(), z.null()]).default(null), "metadata": z.record(z.string(), z.any()).optional(), "chain_id_collection": z.string().describe("Composite HASH = ``chain_id`` + ``#`` + ``collection``.").default("") }).catchall(z.any()).describe('One NFT (or one SFT class) across EVM / Solana / Ordinals.\n\nDDB layout:\n\n* ``HASH`` = ``chain_id_collection`` (composite of ``chain_id``\n  + lowercased ``collection``) \u2014 keeps all NFTs of one\n  collection co-located so "show me a collection" queries are a\n  single partition scan.\n* ``RANGE`` = ``token_id`` (lowercased string \u2014 the type varies\n  by family).\n\nBegins-with cookbook\n--------------------\n\nThe composite HASH co-locates a collection in a single partition;\nthe SK is the per-token id. Audit \xA71b.4 \u2014 ``begins_with`` on the\nSK is family-shaped:\n\n* **EVM (numeric token_id)**: token ids are decimal integers as\n  strings. They are NOT zero-padded, so ``begins_with`` on a\n  numeric prefix is dangerous (``"100"`` matches both ``"100"``\n  and ``"1000"``). Use ``BatchGetItem`` with the full token id\n  list instead, or zero-pad at write time if range scans are\n  needed.\n* **Solana (base58 mint pubkey)**: no useful prefix structure \u2014\n  ``begins_with`` returns scattered subsets. ``GetItem`` by full\n  mint or paginate the whole collection partition.\n* **BTC ordinals (inscription id)**: format is\n  ``"{txid}i{index}"``; ``begins_with(SK, f"{txid}i")`` returns\n  every inscription minted by the same transaction, which is the\n  one prefix walk that\'s both safe and useful::\n\n      Query(PK=f"bitcoin#{collection}",\n            KeyCondition=begins_with(SK, f"{mint_txid}i"))\n\nFor "every NFT owned by address X" use the ``by-owner-index`` GSI\nkeyed on ``owner``; the SK is ``token_id`` again so the same\nfamily-specific caveats apply.');
var AssetTickerSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "coin_id": z.string().min(1).describe("Canonical id, namespaced by source when it isn't globally unique. Examples: ``coingecko:ethereum``, ``moralis:0x1/0xc02aaa...``, ``cmc:1027``. Used as the DDB hash key."), "source": z.string().min(1).describe("Provider that produced this row: ``coingecko``, ``moralis_official``, ``moralis_explorer``, ``thegraph``, ``manual``, ..."), "symbol": z.string().min(1).describe("Ticker symbol (``ETH``, ``BTC``)."), "name": z.string().min(1).describe("Human-readable name."), "decimals": z.number().int().gte(0).lte(24).default(18), "chain_id": z.union([z.string(), z.null()]).describe("Chain this ticker is anchored to when applicable (EVM hex id like ``0x1`` or registry slug like ``solana``). ``None`` for cross-chain canonical coins.").default(null), "address": z.union([z.string(), z.null()]).describe("Contract address (lowercased) when applicable; ``None`` for native.").default(null), "logo_uri": z.union([z.string(), z.null()]).default(null), "homepage_url": z.union([z.string(), z.null()]).default(null), "description": z.union([z.string(), z.null()]).default(null), "coingecko_id": z.union([z.string(), z.null()]).default(null), "moralis_id": z.union([z.string(), z.null()]).default(null), "cmc_id": z.union([z.string(), z.null()]).default(null), "aliases": z.array(z.string()).describe("Alternate symbols or names (lowercased) for search.").optional(), "market_cap_usd": z.union([z.number().gte(0), z.null()]).default(null), "fully_diluted_valuation_usd": z.union([z.number().gte(0), z.null()]).default(null), "price_usd": z.union([z.number().gte(0), z.null()]).describe("Snapshot USD price at ``updated_at`` (not the live price).").default(null), "volume_24h_usd": z.union([z.number().gte(0), z.null()]).default(null), "price_change_24h_pct": z.union([z.number(), z.null()]).describe("Rolling 24h price change as a percentage (may be negative).").default(null), "circulating_supply": z.union([z.string(), z.null()]).describe("Circulating supply as a decimal string (string so large supplies don't overflow JSON / DDB number ranges).").default(null), "total_supply": z.union([z.string(), z.null()]).describe("Total supply as a decimal string.").default(null), "rank": z.union([z.number().int().gte(0), z.null()]).default(null), "confidence": z.number().gte(0).lte(1).describe("0..1 weight the reconciler uses to break conflicts.").default(1), "updated_at": z.union([z.number().int(), z.null()]).describe("Last refresh as Unix epoch seconds.").default(null), "metadata": z.record(z.string(), z.any()).optional() }).catchall(z.any()).describe("Canonical ticker row for a coin/token.\n\nDesigned to be additive to :class:`Address`: contract tokens get\nindexed both ways (Address by ``chain_id`` + ``address``, AssetTicker\nby ``coin_id`` + ``source``). Native assets (BTC, ETH, SOL) only\nhave AssetTicker rows.");
var AbiSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "chain_id": z.string().default(""), "address": z.string(), "valid_from_block": z.number().int().default(0), "valid_to_block": z.union([z.number().int(), z.null()]).default(null), "contract": z.string().default(""), "block_sk": z.string().default(""), "contract_name": z.union([z.string(), z.null()]).default(null), "compiler_version": z.union([z.string(), z.null()]).default(null), "optimization_used": z.union([z.boolean(), z.null()]).default(null), "runs": z.union([z.number().int(), z.null()]).default(null), "license": z.union([z.string(), z.null()]).default(null), "source_code": z.union([z.string(), z.null()]).default(null), "source_code_s3_uri": z.union([z.string(), z.null()]).default(null), "entries": z.array(z.record(z.string(), z.any())).optional(), "abi_ref": z.union([z.string(), z.null()]).default(null), "entries_s3_uri": z.union([z.string(), z.null()]).default(null), "selector_index": z.record(z.string(), z.string()).optional(), "event_topic_index": z.record(z.string(), z.string()).optional(), "origin": z.any().default("unknown"), "fetched_from": z.union([z.string(), z.null()]).default(null), "is_proxy": z.boolean().default(false), "implementation_addresses": z.array(z.string()).optional(), "source": z.union([z.string(), z.null()]).default(null), "confidence": z.number().gte(0).lte(1).default(1), "evidence": z.array(z.any()).optional(), "explanation": z.array(z.string()).optional(), "is_human_approved": z.boolean().default(false), "lifecycle": z.any().default("candidate"), "created_by": z.union([z.string(), z.null()]).default(null), "created_by_service": z.union([z.string(), z.null()]).default(null), "updated_by": z.union([z.string(), z.null()]).default(null), "updated_at": z.union([z.number().int(), z.null()]).default(null), "updated_by_service": z.union([z.string(), z.null()]).default(null), "metadata": z.record(z.string(), z.any()).optional() }).catchall(z.any()).describe("A contract ABI, block-ranged per ``(chain_id, address)``.\n\nDDB key: ``HASH = contract`` (``{chain}#{addr}``), ``RANGE = block_sk``\n(``valid_from_block`` zero-padded). One contract partition holds every\nblock-version; the ABI live at a block is the row with the highest\n``valid_from_block`` \u2264 that block whose ``valid_to_block`` has not yet\nelapsed.");
var StateVarRefSchema = z.object({ "key": z.string().max(512).describe("Function name, canonical signature, or storage-slot label."), "params": z.array(z.any()).max(64).describe("Positional args for a function call.").optional(), "kind": z.any().default("function"), "slot": z.union([z.string(), z.null()]).describe("Storage slot (``0x`` hex) when ``kind=storage_slot``.").default(null), "storage_offset": z.number().int().gte(0).lte(31).default(0), "value_type": z.union([z.string(), z.null()]).describe("Solidity ABI type hint (decode aid for storage slots).").default(null), "label": z.union([z.string().max(512), z.null()]).describe("Display label override for the FE.").default(null), "ref": z.union([z.string(), z.null()]).describe("Stable id echoed on the result so the FE maps the response back to this request. Auto-derived from key+params when omitted.").default(null) }).strict().describe('One state variable to read on a contract.\n\nA plain ``str`` in the legacy config (``"owner"``) maps to\n``StateVarRef(key="owner")``; the ``{key, params}`` object form maps to\n``StateVarRef(key=..., params=[...])``.');
var CombineRuleSchema = z.object({ "strategy": z.any().default("collect"), "over": z.union([z.string(), z.null()]).describe("Variable key whose values are combined (e.g. ``balanceOf``).").default(null), "label": z.union([z.string(), z.null()]).describe("Display label for the result.").default(null), "params": z.record(z.string(), z.any()).optional() }).strict().describe("Configuration for combining values across contracts / children.");
var BlockSpecSchema = z.object({ "number": z.union([z.number().int().gte(0), z.null()]).default(null), "block_hash": z.union([z.string().regex(new RegExp("^0x[0-9a-fA-F]{64}$")), z.null()]).default(null), "timestamp": z.union([z.number().int().gte(0), z.null()]).describe("Unix seconds; resolved to a block per chain.").default(null), "tag": z.union([z.string(), z.null()]).describe('Named tag ("latest"/"finalized"/\u2026).').default(null) }).strict().describe("Point in time to read at \u2014 number, timestamp, or named tag.\n\nExactly one of ``number`` / ``timestamp`` / ``tag`` should be set;\nwhen none are, the reader uses ``latest``. ``timestamp`` resolution to\na block number is the reader's responsibility (per chain).");
var RpcBudgetSchema = z.object({ "max_calls_total": z.number().int().gte(1).lte(5e4).default(500), "max_calls_per_contract": z.number().int().gte(1).lte(1e4).default(128), "max_drilldown_addresses": z.number().int().gte(0).lte(1e4).default(64), "max_batch_size": z.number().int().gte(1).lte(1e3).default(100), "max_variables_per_contract": z.number().int().gte(1).lte(1e3).default(128), "max_lookup_calls": z.number().int().gte(0).lte(2e3).default(256) }).strict().describe("Caps that bound RPC spend + blast radius for one read.\n\nCarried on the request so the FE can scope an expensive deep read, and\nconsumed by the engine which records ``truncated=True`` when a cap is\nhit rather than silently dropping data.");
var StateValueSchema = z.object({ "ref": z.string().describe("Stable id echoing the requested variable."), "key": z.string(), "params": z.array(z.any()).optional(), "value_raw": z.union([z.any(), z.null()]).describe("Decoded primitive(s) \u2014 int as string, bytes as hex.").default(null), "value_decoded": z.union([z.any(), z.null()]).describe("Normalised value (addresses lowercased, etc.).").default(null), "display": z.union([z.string(), z.null()]).describe("Ready-to-render string (token amount, address, \u2026).").default(null), "value_type": z.union([z.string(), z.null()]).describe("Solidity ABI type.").default(null), "value_schema": z.union([z.record(z.string(), z.any()), z.null()]).describe("ABI parameter describing value_decoded, including tuple components and their normalized object keys.").default(null), "is_address": z.boolean().default(false), "label": z.union([z.string(), z.null()]).default(null), "error": z.union([z.string(), z.null()]).default(null) }).strict().describe("One resolved value (or per-value error) on a contract.");
var CombinedValueSchema = z.object({ "strategy": z.any(), "label": z.union([z.string(), z.null()]).default(null), "over": z.union([z.string(), z.null()]).default(null), "value": z.any().default(null), "display": z.union([z.string(), z.null()]).default(null), "members": z.array(z.string()).describe("Refs of the values that were combined.").optional() }).strict().describe("Output of a :class:`CombineRule` over children / contracts.");
var TransferEventSchema = z.object({ "token_address": z.string(), "from_address": z.string(), "to_address": z.string(), "amount_raw": z.string().regex(new RegExp("^[0-9]{1,78}$")).describe("Unscaled integer amount as a decimal string."), "log_index": z.union([z.number().int().gte(0), z.null()]).default(null), "standard": z.enum(["erc20", "erc721", "erc1155"]).default("erc20") }).strict().describe("One token movement within a transaction (ERC-20 in v1).");
var NativeTransferSchema = z.object({ "from_address": z.union([z.string(), z.null()]).default(null), "to_address": z.union([z.string(), z.null()]).default(null), "amount_raw": z.string().regex(new RegExp("^[0-9]{1,78}$")).default("0") }).strict().describe("A gas-token (native) value movement (top-level value or internal tx).");
var IdentityOverrideSchema = z.object({ "labels": z.array(z.string()).optional(), "tags": z.array(z.string()).optional(), "entity_id": z.union([z.string(), z.null()]).default(null), "entity_name": z.union([z.string(), z.null()]).default(null) }).strict().describe("Caller-supplied identity for an address (the 'provide our own' path).");
var BalanceTimelinePointSchema = z.object({ "seq": z.number().int(), "log_index": z.union([z.number().int().gte(0), z.null()]).default(null), "delta_raw": z.string(), "balance_raw": z.string(), "display": z.union([z.string(), z.null()]).default(null) }).strict().describe("One step of the intra-transaction running balance for a holder/token.");
var AddressIdentitySchema = z.object({ "address": z.string(), "chain_id": z.union([z.string(), z.null()]).default(null), "labels": z.array(z.string()).optional(), "tags": z.array(z.string()).optional(), "entity_id": z.union([z.string(), z.null()]).default(null), "entity_name": z.union([z.string(), z.null()]).default(null), "source": z.union([z.string(), z.null()]).describe("Which provider resolved it (provided / catalog / external).").default(null) }).strict().describe("Tags / labels / entity for one address (from a configurable source).");
var SemanticActorSchema = z.object({ "role": z.any().default("other"), "address": z.string().describe("Lowercase 0x-prefixed address."), "chain_id": z.union([z.string(), z.null()]).describe("Chain the address lives on. Differs from the tx chain for the destination side of a bridge node.").default(null), "entity_id": z.union([z.string(), z.null()]).describe("Resolved catalog entity id (populated post-decode).").default(null), "label": z.union([z.string(), z.null()]).describe("Human label/name for the address, if known.").default(null) }).describe("An address participating in a semantic node, with optional\ncatalog/entity decoration filled in by the pipeline annotate step.");
var SemanticAssetSchema = z.object({ "token_address": z.union([z.string(), z.null()]).describe("Token contract; None / 'native' for the chain's gas token.").default(null), "symbol": z.union([z.string(), z.null()]).describe("e.g. 'LINK', 'USDC'.").default(null), "decimals": z.union([z.number().int().gte(0).lte(36), z.null()]).default(null), "amount_raw": z.union([z.string(), z.null()]).describe("Unscaled integer amount as a decimal string (no precision loss).").default(null), "token_id": z.union([z.string(), z.null()]).describe("The id of a non-fungible (ERC-721) or multi-token (ERC-1155) asset, as a decimal string. ``amount_raw`` is then the count of that id (1 for an ERC-721) \u2014 the same convention as ``TokenFlow.token_id``.").default(null), "amount": z.union([z.string(), z.null()]).describe("Human-formatted amount ('2747.8') when decimals are known.").default(null), "usd": z.union([z.string(), z.null()]).describe("USD value as a string, when a price was available.").default(null), "direction": z.union([z.string(), z.null()]).describe("'in' | 'out' relative to the node's initiator.").default(null), "basis": z.union([z.string(), z.null()]).describe("What ``amount`` represents (an :class:`AmountBasis` value): 'actual' (the settled amount, the default) or 'quoted_minimum' (a swap min-return floor read from the call). None == unspecified.").default(null), "min_amount_raw": z.union([z.string(), z.null()]).describe("The quoted-minimum (floor) as an unscaled integer string, present only when ``amount`` is the reconciled ACTUAL and a floor was also known \u2014 so the UI can render 'actual (min: floor)'.").default(null), "min_amount": z.union([z.string(), z.null()]).describe("Human-formatted floor matching ``min_amount_raw``.").default(null), "impersonates": z.union([z.record(z.string(), z.any()), z.null()]).describe("The well-known asset this token CLAIMS to be and is not (``commons.tokens.impersonation.Impersonation.to_dict``): its symbol is still what the token calls itself \u2014 the evidence \u2014 but no title or label may show it bare. Absent for an honest token.").default(null) }).describe("A value movement expressed in human units where possible.");
var SemanticSourceRefSchema = z.object({ "ref_type": z.string().describe("'top_call' | 'log' | 'internal_tx' | 'nested_calldata'."), "address": z.union([z.string(), z.null()]).default(null), "log_index": z.union([z.number().int(), z.null()]).default(null), "internal_tx_index": z.union([z.number().int(), z.null()]).default(null), "path": z.array(z.string()).describe("Param path walked into nested calldata (e.g. ['message', 'data'] or ['commands', '2']).").optional() }).describe("A back-link from a semantic node to the raw decode artifact it was\nderived from, so a UI can drill down to the EVM-level detail.");
var EvidencePolicySchema = z.object({ "method_base": z.record(z.string(), z.number()).optional(), "signal_deltas": z.record(z.string(), z.number()).optional(), "base_overrides": z.record(z.string(), z.record(z.string(), z.number())).optional(), "floors": z.record(z.string(), z.number()).optional(), "gate_actions": z.record(z.string(), z.string()).optional(), "tier_cutoffs": z.record(z.string(), z.number()).optional(), "confidence_floor": z.number().gte(0).lte(1).default(0.5), "materialize_threshold": z.number().gte(0).lte(1).default(0.8), "rotation_threshold": z.number().gte(0).lte(1).default(0.95), "self_write_min_trust": z.number().gte(0).lte(1).default(0.8), "min_sources_for_verified": z.number().int().gte(1).default(1), "require_human_for_verified": z.boolean().default(false), "allow_automation_promotion": z.boolean().default(false), "agreement_per_source": z.number().gte(0).lte(1).default(0.02), "agreement_max_boost": z.number().gte(0).lte(1).default(0.08), "decay_half_life_days": z.number().default(0), "decay_floor": z.number().gte(0).lte(1).default(0), "attribution_gate_enabled": z.boolean().default(true), "catalog_read_floor": z.number().gte(0).lte(1).default(0), "catalog_include_unscored": z.union([z.boolean(), z.null()]).default(null), "metadata_read_floor": z.number().gte(0).lte(1).default(0) }).describe("Resolved, effective evidence tunables for one caller/scope.\n\nGoverns both decode scoring (``method_base`` / ``signal_deltas``) and catalog\nwrite gating (``confidence_floor`` / ``materialize_threshold`` / corroboration\n/ agreement / decay). The single per-scope authority for confidence + evidence\nacross the platform \u2014 resolved once per request and threaded into the decode\n:class:`~commons.evidence.engine` calls and the catalog write paths.");
var PolicyTierSchema = z.object({ "tier": z.string(), "label": z.string(), "section": z.record(z.string(), z.any()).optional(), "note": z.union([z.string(), z.null()]).default(null), "editable": z.boolean().default(true) }).describe('One rung\'s authored contribution.\n\n``section`` is SPARSE \u2014 only what this rung set. An empty section is a real\nand useful answer ("this rung authors nothing"), which is why the tier is\nreported at all rather than omitted.');
var KnobOriginSchema = z.object({ "path": z.string(), "value": z.any(), "tier": z.string(), "label": z.string(), "overridden_tiers": z.array(z.string()).optional() }).describe(`Where one knob's effective value came from.

The unit a UI renders per row: the value in force, the rung that set it, and
whether anything below it was overridden (so "3 rungs set this" can be shown
without re-walking the ladder in the client).`);
var ResolvedLadderSchema = z.object({ "tiers": z.array(z.any()), "effective": z.record(z.string(), z.any()), "origins": z.array(z.any()), "problems": z.array(z.string()).optional() }).describe("The full picture: each rung, the effective policy, and per-knob origins.");
var IncidentReferenceSchema = z.object({ "label": z.string().describe("Display name of the source, e.g. 'Bubblemaps'."), "url": z.string().describe("Link to the report."), "handle": z.string().describe("Social handle including the '@', when the report was published under one. Rendered as the credit in the banner.").default("") }).describe("One public source for the incident, and who it is by.\n\nBoth halves are required to be useful and neither is sufficient: a URL with\nno attribution reads as an anonymous accusation, and a handle with no link\ncannot be checked.");
var AddressIncidentNoticeSchema = z.object({ "incident_id": z.string(), "incident_name": z.string(), "chain_id": z.string(), "address": z.string(), "role": z.any(), "severity": z.any(), "occurred_on": z.string().default(""), "summary": z.string().default(""), "note": z.string().default(""), "headline": z.string().describe("The banner's own sentence, composed server-side by :func:`commons.incidents.notice_for`. Always hedged \u2014 'reported to be involved in' \u2014 because the registry records what a source said, not a finding of fact.").default(""), "references": z.array(z.any()).optional() }).describe('The rendered banner for one address, on one incident.\n\nA flat, pre-composed shape rather than the :class:`Incident` itself, for two\nreasons. The front end must not have to re-derive wording \u2014 two clients\nphrasing the same claim differently is how a hedge ("reported to be") turns\ninto an assertion in one of them. And a viewer is entitled to the addresses\nnamed in an incident they are looking at, but not to the whole registry,\nwhich this shape simply does not carry.');
var IncidentAddressSchema = z.object({ "chain_id": z.string().describe("CAIP-2 chain id."), "address": z.string().describe("Lowercased address."), "role": z.any().default("intermediary"), "note": z.string().describe("One line on what this address specifically did, in the reporter's terms. Carried per address rather than per incident because the same incident says very different things about an exploiter and about a wallet that received one transfer from one.").default("") }).describe("One address named in an incident, and in what capacity.");
var IncidentSchema = z.object({ "id": z.string().describe("Stable slug, e.g. 'nesa_cosmos_evm_2026'."), "name": z.string().describe("Short display name, e.g. 'Nesa / Cosmos EVM'."), "summary": z.string().describe("One sentence on what happened, for the banner's tooltip.").default(""), "occurred_on": z.string().describe("ISO-8601 date (YYYY-MM-DD) the exploit happened. A date rather than a timestamp: public reporting resolves to a day, and a fabricated hour would imply a precision nobody has.").default(""), "severity": z.any().default("high"), "chains": z.array(z.string()).describe("CAIP-2 chains the incident spans.").optional(), "references": z.array(z.any()).optional(), "addresses": z.array(z.any()).optional() }).describe("A publicly reported exploit and the addresses named in it.");
var TokenFlowSchema = z.object({ "kind": z.enum(["native", "internal", "closure", "erc20", "trc20", "nep141", "erc721", "erc1155", "spl", "hip1", "metaprotocol", "cosmos_bank"]), "token_address": z.union([z.string(), z.null()]).default(null), "from_address": z.union([z.string(), z.null()]).default(null), "to_address": z.union([z.string(), z.null()]).default(null), "raw_amount": z.string().default("0"), "token_id": z.union([z.string(), z.null()]).default(null), "log_index": z.union([z.number().int(), z.null()]).default(null), "decimals": z.union([z.number().int(), z.null()]).default(null), "price_usd": z.union([z.number(), z.null()]).default(null), "value_usd": z.union([z.number(), z.null()]).default(null) }).describe('One value movement inside a transaction (native / internal / ERC-20 / NFT).\n\nCarries the raw on-chain amount plus the token contract so a later step can\nprice it; ``token_address is None`` means the chain\'s native asset. For a\nnon-fungible (``erc721``) or multi-token (``erc1155``) move, ``token_id``\ncarries the moved id and ``raw_amount`` is the count (``"1"`` for ERC-721).');
var NetFlowSchema = z.object({ "address": z.string(), "token_address": z.union([z.string(), z.null()]).default(null), "raw_delta": z.string().default("0"), "value_usd": z.union([z.number(), z.null()]).default(null) }).describe('Net per-(address, token) delta across a transaction\'s flows.\n\nThe "net flow" a tx-table shows next to a complex DeFi tx: positive\n``raw_delta`` is a net inflow to ``address``, negative a net outflow.');
var TokenPriceSchema = z.object({ "token_address": z.string(), "chain_id": z.string(), "symbol": z.union([z.string(), z.null()]).default(null), "decimals": z.union([z.number().int(), z.null()]).default(null), "price_usd": z.union([z.number(), z.null()]).default(null), "block_number": z.union([z.number().int(), z.null()]).default(null), "block_timestamp": z.union([z.number().int(), z.null()]).default(null), "source": z.union([z.string(), z.null()]).default(null), "confidence": z.union([z.number(), z.null()]).default(null), "metadata": z.record(z.string(), z.any()).optional() }).describe("A resolved token price, optionally as-of a specific block.\n\nProvider-agnostic: a price source (Moralis today, others later) maps its\npayload onto this shape. ``confidence`` lets a multi-source resolver pick or\nblend.");
var GraphNodeSchema = z.object({ "id": z.string().default(""), "node_type": z.string().default("address"), "address": z.string().default(""), "chain_id": z.union([z.string(), z.null()]).default(null), "label": z.union([z.string(), z.null()]).default(null), "degree": z.number().int().default(0), "tx_count": z.number().int().default(0), "hop": z.union([z.number().int(), z.null()]).default(null) }).describe('A node in the interaction graph.\n\nUsually an on-chain address (``node_type="address"``) but may be an\noff-chain actor (bridge / DON / mixer / exchange entity / cluster) when\n``chain_id`` is unset and ``node_type`` says what it is.');
var GraphEdgeSchema = z.object({ "from_address": z.string().default(""), "to_address": z.string().default(""), "from_id": z.string().default(""), "to_id": z.string().default(""), "from_chain": z.union([z.string(), z.null()]).default(null), "to_chain": z.union([z.string(), z.null()]).default(null), "token_address": z.union([z.string(), z.null()]).default(null), "token_chain_id": z.union([z.string(), z.null()]).default(null), "kind": z.union([z.string(), z.null()]).default(null), "interaction_count": z.number().int().default(0), "transaction_ids": z.union([z.array(z.string()), z.null()]).default(null), "raw_amount_total": z.string().default("0"), "decimals": z.union([z.number().int(), z.null()]).default(null) }).describe("A directed ``(from -> to)`` aggregation for one token + flow kind.\n\nEndpoints reference node **ids** via :attr:`from_id` / :attr:`to_id` (which\ndefault to the chain-scoped address) so an edge stays unambiguous in a\nmulti-chain graph; :attr:`from_address` / :attr:`to_address` are retained for\ndisplay + backward compatibility.");
var InteractionGraphSchema = z.object({ "nodes": z.array(z.any()).default([]), "edges": z.array(z.any()).default([]), "node_count": z.number().int().default(0), "edge_count": z.number().int().default(0), "chain_ids": z.array(z.string()).default([]), "multi_chain": z.boolean().default(false) }).describe("A compact node/edge graph a UI can render directly.\n\nSingle-chain by default; set :attr:`chain_ids` / :attr:`multi_chain` when the\ngraph merges flows from more than one chain (see\n:func:`commons.authe.graph.merge_interaction_graphs`).");
var EnrichedTransactionSchema = z.object({ "transaction": z.record(z.string(), z.any()), "unique_addresses": z.array(z.string()).optional(), "token_flows": z.array(z.any()).optional(), "net_flows": z.array(z.any()).optional() }).describe("One transaction plus the per-tx enrichments the flags asked for.");
var BlockProcessingResultSchema = z.object({ "transactions": z.array(z.any()).optional(), "unique_addresses": z.array(z.string()).optional(), "overlays": z.record(z.string(), z.record(z.string(), z.any())).optional(), "overlays_bounded": z.record(z.string(), z.number().int()).optional(), "coins": z.record(z.string(), z.record(z.string(), z.any())).optional(), "tokens": z.record(z.string(), z.record(z.string(), z.any())).optional(), "market": z.record(z.string(), z.record(z.string(), z.any())).optional(), "prices": z.record(z.string(), z.any()).optional(), "graph": z.union([z.any(), z.null()]).default(null) }).describe("Aggregate result for one processed set of transactions (one chain).");
var EntityLinkSchema = z.object({ "entity": z.any(), "role": z.union([z.string(), z.null()]).default(null), "confidence": z.number().default(1), "is_human_approved": z.boolean().default(false), "valid_from": z.union([z.number().int(), z.null()]).default(null), "valid_to": z.union([z.number().int(), z.null()]).default(null), "source": z.union([z.string(), z.null()]).default(null), "explanation": z.array(z.string()).optional() }).describe("A single entity-address linkage with the temporal window.");
var AddressRelationshipSchema = z.object({ "address": z.any(), "entities": z.array(z.any()).optional(), "labels": z.array(z.any()).optional(), "tags": z.array(z.any()).optional(), "inherited_labels": z.array(z.any()).optional(), "inherited_tags": z.array(z.any()).optional(), "groups": z.array(z.any()).optional(), "primary_entity_id": z.union([z.string(), z.null()]).default(null) }).describe("All known facts about an address at a point in time.");
var EntityRelationshipSchema = z.object({ "entity": z.any(), "addresses": z.array(z.any()).optional(), "labels": z.array(z.any()).optional(), "tags": z.array(z.any()).optional(), "groups": z.array(z.any()).optional(), "related_entities": z.array(z.any()).optional(), "transaction_groups": z.array(z.any()).optional() }).describe("All known facts about an entity.");
var TransactionRelationshipSchema = z.object({ "transaction_hash": z.string(), "chain_id": z.string().default(""), "labels": z.array(z.any()).optional(), "tags": z.array(z.any()).optional(), "groups": z.array(z.any()).optional(), "from_entity": z.union([z.any(), z.null()]).default(null), "to_entity": z.union([z.any(), z.null()]).default(null) }).describe("All known facts about a single transaction.");
var StreamedTransactionSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "stream_id": z.string(), "sequence": z.string(), "source": z.any().default("unknown"), "hash": z.string(), "chain_id": z.string().default("0x1"), "block_number": z.union([z.number().int(), z.null()]).default(null), "block_timestamp": z.union([z.number().int(), z.null()]).default(null), "from_address": z.string().default(""), "to_address": z.union([z.string(), z.null()]).default(null), "value": z.string().default("0"), "type": z.any().default("transaction"), "direction": z.any().default("unknown"), "status": z.any().default("unknown"), "raw_payload": z.record(z.string(), z.any()).optional(), "received_at": z.union([z.number().int(), z.null()]).default(null), "processed_at": z.union([z.number().int(), z.null()]).default(null), "consumed_by": z.array(z.string()).optional(), "chain_id_hash": z.string().default("") }).catchall(z.any()).describe("A transaction observed on a real-time stream, before persistence.\n\nDDB key: ``HASH = stream_id``, ``RANGE = sequence``. The ``sequence``\nfield is the monotonic offset from the upstream provider (Moralis\nstreamId offset, Kinesis sequence, etc.).");
var StreamDeliverySchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "stream_id": z.string(), "consumer_id": z.string(), "last_sequence": z.union([z.string(), z.null()]).default(null), "last_acked_at": z.union([z.number().int(), z.null()]).default(null), "pending_count": z.number().int().default(0), "metadata": z.record(z.string(), z.any()).optional() }).catchall(z.any()).describe("Per-consumer delivery cursor for a stream.");
var UserSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "email": z.string(), "status": z.any().default("active"), "role": z.any().default("member"), "display_name": z.union([z.string(), z.null()]).default(null), "avatar_url": z.union([z.string(), z.null()]).default(null), "auth_provider": z.union([z.string(), z.null()]).default(null), "last_login_at": z.union([z.number().int(), z.null()]).default(null), "aws_rep_deleting": z.union([z.boolean(), z.null()]).default(null), "aws_rep_updateregion": z.union([z.string(), z.null()]).default(null), "aws_rep_updatetime": z.union([z.number(), z.null()]).default(null), "updated_at": z.union([z.number().int(), z.null()]).default(null), "updated_by": z.union([z.string(), z.null()]).default(null), "updated_by_service": z.union([z.string(), z.null()]).default(null), "metadata": z.record(z.string(), z.any()).optional() }).catchall(z.any()).describe("A user *identity* row \u2014 one per human, keyed by subject, under a customer.\n\nDDB key: ``HASH = id`` (Auth0/Google subject) \u2014 a single identity row per\nuser. The user no longer carries an org in its key: organization membership\nis a separate many-per-user row\n(:class:`~commons.interfaces.models.membership.Membership`), so one identity\ncan belong to many orgs and be managed in one place. Identities are listed /\nmanaged per customer via the ``by-customer-index`` GSI.\n\nv3.0.0 (users-under-customer_id): dropped the ``tenant_id`` RANGE segment\n(identity is no longer per-tenant) and the legacy ``org_id`` validator\n(identity is no longer org-scoped). ``org_id`` / ``tenant_id`` remain inert\ninherited attributes (tenancy carriers) but are no longer keys or required.");
var ApiKeySchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "key_hash": z.string(), "key_prefix": z.string(), "owner_user_id": z.string(), "name": z.union([z.string(), z.null()]).default(null), "scopes": z.array(z.string()).optional(), "expires_at": z.union([z.number().int(), z.null()]).default(null), "last_used_at": z.union([z.number().int(), z.null()]).default(null), "revoked_at": z.union([z.number().int(), z.null()]).default(null) }).catchall(z.any()).describe("An API key issued to a user / service.");
var AuthSessionSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "user_id": z.string(), "session_token_hash": z.string(), "refresh_token_hash": z.union([z.string(), z.null()]).default(null), "expires_at": z.number().int(), "last_refreshed_at": z.union([z.number().int(), z.null()]).default(null), "user_agent": z.union([z.string(), z.null()]).default(null), "ip_address": z.union([z.string(), z.null()]).default(null) }).catchall(z.any()).describe("A short-lived auth session row (Auth0 exchange + refresh tracking).");
var ActivityLogSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "function_name": z.string().default(""), "function_version": z.union([z.string(), z.null()]).default(null), "function_arn": z.union([z.string(), z.null()]).default(null), "aws_request_id": z.union([z.string(), z.null()]).default(null), "correlation_id": z.union([z.string(), z.null()]).default(null), "requester_org_id": z.union([z.string(), z.null()]).default(null), "requester_tenant_id": z.union([z.string(), z.null()]).default(null), "requester_user_id": z.union([z.string(), z.null()]).default(null), "requester_application": z.union([z.string(), z.null()]).default(null), "source_ip": z.union([z.string(), z.null()]).default(null), "user_agent": z.union([z.string(), z.null()]).default(null), "invoked_at": z.number().int(), "timestamp": z.number().int().optional(), "duration_ms": z.union([z.number(), z.null()]).default(null), "cold_start": z.boolean().default(false), "deployment_colour": z.union([z.string(), z.null()]).default(null), "success": z.boolean().default(true), "status_code": z.union([z.number().int(), z.null()]).default(null), "payload_summary": z.record(z.string(), z.any()).optional(), "error_summary": z.union([z.record(z.string(), z.any()), z.null()]).default(null), "ttl": z.union([z.number().int(), z.null()]).default(null) }).catchall(z.any()).describe("Lambda invocation activity log row.");
var AccessWarningSchema = z.object({ "code": z.string().default("access_filtered"), "message": z.string().default(""), "resource_type": z.string().default("transaction"), "hidden_count": z.number().int().default(0), "sample_ids": z.array(z.string()).optional() }).describe('A non-fatal notice that part of a shared view was filtered for the viewer.\n\nReturned alongside a resolved/reloaded view so the UI can render a banner\nlike "12 transactions in this view are not visible to you". Pure wire model\n(not persisted) \u2014 the FE generated types pick it up so the dashboard banner\nand the backend agree on the shape.');
var SavedViewSchema = z.object({ "owner_user_id": z.union([z.string(), z.null()]).default(null), "visible_to_user_ids": z.array(z.string()).optional(), "visible_to_team_ids": z.array(z.string()).optional(), "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "table_id": z.string().min(1), "name": z.string().default(""), "description": z.union([z.string(), z.null()]).default(null), "scope": z.any().default("user"), "data_mode": z.any().default("static"), "catalog_resolution": z.any().default("viewer"), "payload_visibility": z.any().default("private"), "is_default": z.boolean().default(false), "settings": z.record(z.string(), z.any()).optional(), "view_state": z.record(z.string(), z.any()).optional(), "snapshot_s3_key": z.union([z.string(), z.null()]).default(null), "snapshot_bytes": z.number().int().default(0), "snapshot_etag": z.union([z.string(), z.null()]).default(null), "dynamic_seed": z.record(z.string(), z.any()).optional(), "catalog_snapshot": z.record(z.string(), z.any()).optional(), "view_count": z.number().int().default(0), "last_accessed_at": z.union([z.number().int(), z.null()]).default(null), "created_by": z.union([z.string(), z.null()]).default(null), "updated_at": z.union([z.number().int(), z.null()]).default(null), "updated_by": z.union([z.string(), z.null()]).default(null), "view_sk": z.string().default("") }).catchall(z.any()).describe("A reusable, shareable snapshot of a UI surface's state.\n\nSee the module docstring for the key schema, the ``static`` vs ``dynamic``\ndata model, and the catalog-resolution policy. Callers pass tenancy + the\nsurface payload; the validator derives the composite ``view_sk``.");
var SavedViewShareLinkSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "token": z.string().default(""), "view_id": z.string().min(1), "target": z.any().default("public"), "payload_visibility": z.any().default("private"), "data_mode": z.any().default("static"), "catalog_resolution": z.any().default("viewer"), "password_hash": z.union([z.string(), z.null()]).default(null), "expires_at": z.union([z.number().int(), z.null()]).default(null), "max_views": z.union([z.number().int(), z.null()]).default(null), "view_count": z.number().int().default(0), "revoked": z.boolean().default(false), "ttl": z.union([z.number().int(), z.null()]).default(null), "target_user_ids": z.array(z.string()).optional(), "target_team_ids": z.array(z.string()).optional(), "owner_user_id": z.union([z.string(), z.null()]).default(null), "created_by": z.union([z.string(), z.null()]).default(null), "updated_at": z.union([z.number().int(), z.null()]).default(null), "last_accessed_at": z.union([z.number().int(), z.null()]).default(null) }).catchall(z.any()).describe("A tokenized link granting access to a saved view.\n\nThe owner mints one via ``share-write``; ``share-read`` resolves it by token\non every open, applying the expiry / password / max-views / revoke gates and\nincrementing :attr:`view_count`.");
var SavedViewAccessLogSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "view_id": z.string().default(""), "token": z.string().default(""), "accessor_user_id": z.union([z.string(), z.null()]).default(null), "accessor_type": z.any().default("anonymous"), "ip_hash": z.union([z.string(), z.null()]).default(null), "user_agent": z.union([z.string(), z.null()]).default(null), "referer": z.union([z.string(), z.null()]).default(null), "extra": z.record(z.string(), z.any()).optional(), "ttl": z.union([z.number().int(), z.null()]).default(null), "access_sk": z.string().default("") }).catchall(z.any()).describe("Append-only record of one open of a shared saved view.");
var ExportRecordSchema = z.object({ "owner_user_id": z.union([z.string(), z.null()]).default(null), "visible_to_user_ids": z.array(z.string()).optional(), "visible_to_team_ids": z.array(z.string()).optional(), "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "resource": z.string().max(120).default(""), "table_id": z.union([z.string(), z.null()]).default(null), "view_id": z.union([z.string(), z.null()]).default(null), "export_format": z.string().default("csv"), "filename": z.string().default(""), "row_count": z.number().int().default(0), "byte_size": z.number().int().default(0), "columns": z.array(z.string()).optional(), "filters": z.record(z.string(), z.any()).optional(), "status": z.any().default("recorded"), "s3_key": z.union([z.string(), z.null()]).default(null), "s3_etag": z.union([z.string(), z.null()]).default(null), "error": z.union([z.string(), z.null()]).default(null), "created_by": z.union([z.string(), z.null()]).default(null), "updated_at": z.union([z.number().int(), z.null()]).default(null), "ttl": z.union([z.number().int(), z.null()]).default(null), "export_sk": z.string().default("") }).catchall(z.any()).describe("Audit row for one dataset exported out of the product.\n\nSee the module docstring for the key schema and the ``recorded`` vs\n``stored`` distinction. Callers pass tenancy + what was exported; the\nvalidator mints the id and derives the composite ``export_sk``.");
var PathSpecSchema = z.object({ "path_id": z.string().min(1), "namespace": z.string().default("decoded.event"), "expression": z.string().min(1), "canonical_name": z.string().min(1), "canonical_type": z.any().default("string"), "fallbacks": z.array(z.string()).optional(), "version_expressions": z.record(z.string(), z.string()).optional(), "version_fallbacks": z.record(z.string(), z.array(z.string())).optional(), "filter": z.record(z.string(), z.any()).optional(), "extra_bindings": z.array(z.any()).optional(), "description": z.union([z.string(), z.null()]).default(null), "enabled": z.boolean().default(true) }).describe("One registered, extractable path with a stable :attr:`path_id`.\n\nData-only (no engine logic). The\n:func:`commons.metadata.resolver.resolve_paths` resolver turns a set of\nthese into a :class:`~commons.protocols.definition.FieldMappingSpec` and\nruns the shared :class:`~commons.protocols.field_mapper.FieldMapper`.");
var FacetDefinitionSchema = z.object({ "name": z.string().min(1), "canonical_name": z.string().min(1), "canonical_type": z.any().default("string"), "description": z.union([z.string(), z.null()]).default(null), "visibility": z.any().default("system"), "indexing": z.any().default("indexed"), "paths": z.array(z.any()).optional(), "namespaces": z.array(z.string()).optional(), "enabled": z.boolean().default(true), "deprecated": z.boolean().default(false), "highlight": z.boolean().default(false), "display_order": z.number().int().default(1e4) }).describe("A tenant/user-declared semantic facet over normalized metadata.");
var MaterializedViewSpecSchema = z.object({ "view_id": z.string().min(1), "facet_names": z.array(z.string()).optional(), "scope": z.any().default("system"), "refresh": z.any().default("scheduled"), "schedule": z.union([z.string(), z.null()]).default(null), "group_by": z.array(z.string()).optional(), "measures": z.array(z.string()).optional(), "measure_per_entity": z.any().default("sum"), "source": z.any().default("metadata"), "protocol_id": z.union([z.string(), z.null()]).default(null), "enabled": z.boolean().default(true), "deprecated": z.boolean().default(false), "estimated_cardinality": z.union([z.number().int(), z.null()]).default(null), "description": z.union([z.string(), z.null()]).default(null) }).describe("One registered materialized view over normalized metadata.");
var ExtractedFacetValueSchema = z.object({ "path_id": z.string().min(1), "canonical_name": z.string().min(1), "namespace": z.string().min(1), "entity_type": z.enum(["transaction", "decoded_call", "event", "internal_tx", "enriched", "instruction", "transfer", "utxo_input", "utxo_output"]).default("transaction"), "entity_id": z.string().min(1), "chain_id": z.string().default("0x1"), "tx_id": z.string().min(1), "value": z.any().default(null), "confidence": z.number().gte(0).lte(1).default(1), "source": z.string().default("decoded"), "original_name": z.union([z.string(), z.null()]).default(null), "original_path": z.union([z.string(), z.null()]).default(null), "block_timestamp": z.union([z.number().int(), z.null()]).default(null), "definition_scope": z.union([z.string(), z.null()]).default(null), "is_highlight": z.boolean().default(false), "highlight_order": z.number().int().default(1e4) }).describe("One normalized fact for one entity \u2014 an EAV row before persistence.");
var MetadataPolicySchema = z.object({ "enabled": z.boolean().default(true), "enrich_tokens": z.boolean().default(true), "enabled_namespaces": z.array(z.string()).refine((arr) => arr.every((item, i) => arr.indexOf(item) == i), "All items must be unique!").optional(), "default_refresh": z.any().default("scheduled"), "view_caps": z.any().optional(), "write_per_kind_tables": z.boolean().default(false) }).describe("Resolved, tenant-aware metadata-registry configuration.");
var CustomerSchema = z.object({ "id": z.string().default(""), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "name": z.string(), "slug": z.string().default(""), "status": z.any().default("active"), "display_name": z.union([z.string(), z.null()]).default(null), "billing_email": z.union([z.string(), z.null()]).default(null), "plan_id": z.union([z.string(), z.null()]).default(null), "settings": z.record(z.string(), z.any()).optional(), "system_default": z.boolean().default(false), "created_by": z.union([z.string(), z.null()]).default(null), "updated_at": z.union([z.number().int(), z.null()]).default(null), "updated_by": z.union([z.string(), z.null()]).default(null) }).catchall(z.any()).describe("A billing customer \u2014 the root tenancy principal (owns many orgs).\n\nDDB key: ``HASH = id`` (the ``customer_id`` carrier value).");
var OrganizationSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "name": z.string(), "slug": z.string().default(""), "status": z.any().default("active"), "display_name": z.union([z.string(), z.null()]).default(null), "billing_email": z.union([z.string(), z.null()]).default(null), "plan_id": z.union([z.string(), z.null()]).default(null), "settings": z.record(z.string(), z.any()).optional(), "system_default": z.boolean().default(false), "is_root": z.boolean().default(false), "created_by": z.union([z.string(), z.null()]).default(null), "updated_at": z.union([z.number().int(), z.null()]).default(null), "updated_by": z.union([z.string(), z.null()]).default(null) }).catchall(z.any()).describe("A customer's top-level organisation (Auth0 Organization mirror).\n\nDDB key: ``HASH = id`` (the ``org_\u2026`` id).");
var TenantSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "name": z.string(), "slug": z.string().default(""), "status": z.any().default("active"), "display_name": z.union([z.string(), z.null()]).default(null), "environment": z.union([z.string(), z.null()]).default(null), "settings": z.record(z.string(), z.any()).optional(), "system_default": z.boolean().default(false), "is_root": z.boolean().default(false), "created_by": z.union([z.string(), z.null()]).default(null), "updated_at": z.union([z.number().int(), z.null()]).default(null), "updated_by": z.union([z.string(), z.null()]).default(null) }).catchall(z.any()).describe("A sub-organisational workspace inside an org.\n\nDDB key: ``HASH = id`` (the ``ten_\u2026`` id, which equals ``tenant_id``).");
var TeamSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "name": z.string(), "slug": z.string().default(""), "description": z.union([z.string(), z.null()]).default(null), "avatar_url": z.union([z.string(), z.null()]).default(null), "status": z.any().default("active"), "member_count": z.number().int().default(0), "settings": z.record(z.string(), z.any()).optional(), "created_by": z.union([z.string(), z.null()]).default(null), "updated_at": z.union([z.number().int(), z.null()]).default(null), "updated_by": z.union([z.string(), z.null()]).default(null) }).catchall(z.any()).describe("A team / workspace inside a tenant.\n\nDDB key: ``HASH = id`` (the ``team_\u2026`` id).");
var TeamInviteSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "team_id": z.string(), "email": z.string(), "role_ids": z.array(z.string()).optional(), "status": z.any().default("pending"), "token": z.string().default(""), "message": z.union([z.string(), z.null()]).default(null), "invited_by": z.union([z.string(), z.null()]).default(null), "invited_at": z.union([z.number().int(), z.null()]).default(null), "expires_at": z.union([z.number().int(), z.null()]).default(null) }).catchall(z.any()).describe("A pending invitation to join a team.\n\nDDB key: ``HASH = team_id``, ``RANGE = id`` (the ``tinv_\u2026`` id).");
var TeamMemberSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "team_id": z.string(), "user_id": z.string(), "role_ids": z.array(z.string()).optional(), "status": z.any().default("active"), "display_name": z.union([z.string(), z.null()]).default(null), "email": z.union([z.string(), z.null()]).default(null), "avatar_url": z.union([z.string(), z.null()]).default(null), "joined_at": z.union([z.number().int(), z.null()]).default(null), "last_active_at": z.union([z.number().int(), z.null()]).default(null), "invited_by": z.union([z.string(), z.null()]).default(null), "updated_at": z.union([z.number().int(), z.null()]).default(null), "updated_by": z.union([z.string(), z.null()]).default(null) }).catchall(z.any()).describe("A user's membership in a team.\n\nDDB key: ``HASH = team_id``, ``RANGE = user_id``.");
var TeamRoleSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "team_id": z.string(), "name": z.string(), "description": z.union([z.string(), z.null()]).default(null), "permissions": z.record(z.string(), z.array(z.string())).optional(), "system": z.boolean().default(false), "member_count": z.number().int().default(0), "created_by": z.union([z.string(), z.null()]).default(null), "updated_at": z.union([z.number().int(), z.null()]).default(null), "updated_by": z.union([z.string(), z.null()]).default(null) }).catchall(z.any()).describe("A permission role within a team.\n\nDDB key: ``HASH = team_id``, ``RANGE = id`` (the ``trole_\u2026`` id).");
var NodeUiHintsSchema = z.object({ "x": z.number().default(0), "y": z.number().default(0), "color": z.union([z.string(), z.null()]).default(null), "collapsed": z.boolean().default(false) }).catchall(z.any()).describe("Non-semantic layout hints the visual builder round-trips.\n\nCarried verbatim through compile/validate (the engine ignores them) so a\nuser's canvas positions survive a save/load cycle.");
var PipelineNodeDocSchema = z.object({ "id": z.string().min(1), "step_name": z.string().min(1), "step_version": z.string().min(1), "name": z.union([z.string(), z.null()]).default(null), "needs": z.array(z.string()).optional(), "options": z.record(z.string(), z.any()).optional(), "enabled": z.boolean().default(true), "ui": z.any().optional() }).catchall(z.any()).describe("One node in an authored pipeline DAG.\n\nMaps onto a :class:`~authe_tx_pipeline.engine.pipeline.PipelineNode`: the\n``(step_name, step_version)`` pair resolves a registered step class, and\n``options`` are passed to ``step_cls(**options)`` per run. ``id`` is the\nbuilder-stable node identity used by edges (``needs``); ``name`` is the\noptional DAG alias when the same step appears twice.");
var PipelineDocSchema = z.object({ "id": z.string().optional(), "name": z.string().default(""), "description": z.union([z.string(), z.null()]).default(null), "doc_version": z.number().int().default(1), "target_tier": z.string().default("confirmed"), "nodes": z.array(z.any()).optional(), "parallel_execution": z.boolean().default(false), "dynamic_step_budget": z.number().int().default(8), "config": z.record(z.string(), z.any()).optional(), "metadata": z.record(z.string(), z.any()).optional() }).catchall(z.any()).describe("The editable, persistable representation of a pipeline.\n\nA doc is authored in the dashboard, validated + compiled by the engine,\nand stored inside a\n:class:`~commons.interfaces.models.pipeline_definition.PipelineDefinition`.\nThe string ``target_tier`` is mapped to a\n:class:`~authe_tx_pipeline.models.latency.LatencyTier` by the compiler.");
var StepOptionSpecSchema = z.object({ "name": z.string().min(1), "type": z.string().default("string"), "required": z.boolean().default(false), "default": z.any().default(null), "description": z.string().default(""), "enum_values": z.array(z.string()).optional() }).catchall(z.any()).describe("One configurable option a step's ``__init__`` accepts.\n\nSurfaced in the builder's inspector panel. ``type`` is a coarse JSON-ish\nhint (``string`` / ``number`` / ``boolean`` / ``object`` / ``array``).");
var StepMetadataSchema = z.object({ "name": z.string().min(1), "version": z.string().min(1), "title": z.string().default(""), "description": z.string().default(""), "category": z.string().default("general"), "owner_team": z.union([z.string(), z.null()]).default(null), "supported_tiers": z.array(z.string()).optional(), "needs": z.array(z.string()).optional(), "failure_policy": z.string().default("fail_step"), "required_flags": z.array(z.string()).optional(), "options": z.array(z.any()).optional(), "runtime_deps": z.array(z.string()).optional(), "custom": z.boolean().default(false), "code_runtime_id": z.union([z.string(), z.null()]).default(null) }).catchall(z.any()).describe("Catalog entry describing a registered (or custom) step.\n\nBuilt by ``authe_tx_pipeline.authoring.catalog`` from the live\n:class:`~authe_tx_pipeline.engine.registry.StepRegistry`. The builder palette\nrenders one card per entry; the inspector uses ``options`` to render the\nconfig form. ``custom`` flags a user/3rd-party-authored step whose body\nruns through the custom-code executor seam rather than a bundled class.");
var StepCatalogSchema = z.object({ "steps": z.array(z.any()).optional(), "generated_at": z.number().int().optional() }).catchall(z.any()).describe("The full palette of steps available to author with.");
var PipelineIssueSchema = z.object({ "level": z.any().default("error"), "code": z.string().default(""), "message": z.string().default(""), "node_id": z.union([z.string(), z.null()]).default(null), "field": z.union([z.string(), z.null()]).default(null) }).catchall(z.any()).describe("A single validation finding against a :class:`PipelineDoc`.");
var PipelineValidationSchema = z.object({ "ok": z.boolean().default(true), "issues": z.array(z.any()).optional() }).catchall(z.any()).describe("Outcome of validating a doc against the live registry.\n\n``ok`` is ``False`` when any :class:`PipelineIssue` is at\n:attr:`IssueLevel.ERROR`; warnings/info never block a save.");
var StepDecisionDTOSchema = z.object({ "reason": z.string().default("ran"), "summary": z.string().default(""), "detail": z.record(z.string(), z.any()).optional() }).catchall(z.any()).describe("Wire mirror of :class:`authe_tx_pipeline.engine.decision.StepDecision`.\n\n``reason`` is the closed-vocabulary code (``ran`` / ``feature_flag_off`` /\n``dependency_not_wired`` / ``upstream_skipped`` / \u2026); ``summary`` is the\nhuman one-liner; ``detail`` is the machine-readable context (which flag,\nwhich dependency, which config layer set it).");
var StepOutcomeDTOSchema = z.object({ "node_name": z.string().default(""), "step_name": z.string().default(""), "step_version": z.string().default(""), "status": z.string().default("ok"), "error": z.union([z.string(), z.null()]).default(null), "enrichment_count": z.number().int().default(0), "attempts": z.number().int().default(1), "duration_ms": z.union([z.number(), z.null()]).default(null), "decision": z.union([z.any(), z.null()]).default(null), "debug": z.union([z.record(z.string(), z.any()), z.null()]).default(null) }).catchall(z.any()).describe("Wire mirror of :class:`authe_tx_pipeline.engine.pipeline.StepOutcome`.\n\nDecoupled from the engine dataclass so the run console renders per-step\nstatus without taking a pipeline dependency.");
var PipelineRunResultDTOSchema = z.object({ "pipeline_run_id": z.string().default(""), "tx_correlation_id": z.string().default(""), "status": z.string().default("ok"), "outcomes": z.array(z.any()).optional(), "dynamic_steps_run": z.number().int().default(0), "enrichment_layer_count": z.number().int().default(0), "canonical_tx_id": z.union([z.string(), z.null()]).default(null), "config_hash": z.union([z.string(), z.null()]).default(null), "pipeline_version": z.union([z.number().int(), z.null()]).default(null), "applied_configuration": z.union([z.record(z.string(), z.any()), z.null()]).default(null), "deployment_colour": z.union([z.string(), z.null()]).default(null), "application_version": z.union([z.string(), z.null()]).default(null), "fingerprint": z.union([z.string(), z.null()]).default(null), "fingerprint_version": z.number().int().default(0), "scoped_fingerprints": z.union([z.record(z.string(), z.any()), z.null()]).default(null), "scope_coverage": z.union([z.record(z.string(), z.any()), z.null()]).default(null), "covered_kinds": z.union([z.record(z.string(), z.any()), z.null()]).default(null) }).catchall(z.any()).describe("Wire mirror of :class:`authe_tx_pipeline.engine.pipeline.PipelineRunResult`.\n\nReturned by ``POST /process`` (single-tx local run) and the dry-run\nendpoint so the dashboard can show the run timeline + the resulting\ncanonical-tx pointer without deserialising the full envelope.");
var PipelineDefinitionSchema = z.object({ "owner_user_id": z.union([z.string(), z.null()]).default(null), "visible_to_user_ids": z.array(z.string()).optional(), "visible_to_team_ids": z.array(z.string()).optional(), "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "definition_sk": z.string().default(""), "name": z.string().default(""), "description": z.union([z.string(), z.null()]).default(null), "scope": z.any().default("user"), "enabled": z.boolean().default(true), "is_default": z.boolean().default(false), "preset": z.union([z.any(), z.null()]).default(null), "doc": z.any().optional(), "target_chain_ids": z.array(z.string()).optional(), "config_hash": z.union([z.string(), z.null()]).default(null), "latest_run_id": z.union([z.string(), z.null()]).default(null), "created_by": z.union([z.string(), z.null()]).default(null), "updated_at": z.union([z.number().int(), z.null()]).default(null), "updated_by": z.union([z.string(), z.null()]).default(null), "schema_version": z.string().default("1"), "live_version": z.union([z.number().int(), z.null()]).default(null) }).catchall(z.any()).describe("A persisted, ownable, shareable pipeline definition.\n\nSee the module docstring for the key schema and scope precedence. Callers\npass tenancy + the authored ``doc``; the validator derives ``definition_sk``\nand keeps the embedded doc id in sync with the row id.");
var PipelineRunSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "pipeline_run_id": z.string().min(1), "pipeline_id": z.string().min(1), "tx_correlation_id": z.union([z.string(), z.null()]).default(null), "status": z.any().default("running"), "trigger": z.any().default("api"), "target_tier": z.string().default("confirmed"), "chain_id": z.union([z.string(), z.null()]).default(null), "canonical_tx_id": z.union([z.string(), z.null()]).default(null), "tx_hash": z.union([z.string(), z.null()]).default(null), "replay_of_run_id": z.union([z.string(), z.null()]).default(null), "run_job_id": z.union([z.string(), z.null()]).default(null), "step_count": z.number().int().default(0), "ok_count": z.number().int().default(0), "degraded_count": z.number().int().default(0), "failed_count": z.number().int().default(0), "dynamic_steps_run": z.number().int().default(0), "enrichment_layer_count": z.number().int().default(0), "started_at": z.union([z.number().int(), z.null()]).default(null), "finished_at": z.union([z.number().int(), z.null()]).default(null), "duration_ms": z.union([z.number().int(), z.null()]).default(null), "config_hash": z.union([z.string(), z.null()]).default(null), "pipeline_version": z.union([z.number().int(), z.null()]).default(null), "applied_configuration": z.union([z.record(z.string(), z.any()), z.null()]).default(null), "triggered_by": z.union([z.string(), z.null()]).default(null), "error": z.union([z.string(), z.null()]).default(null), "deployment_colour": z.union([z.string(), z.null()]).default(null), "application_version": z.union([z.string(), z.null()]).default(null), "result_fingerprint": z.union([z.string(), z.null()]).default(null), "fingerprint_version": z.number().int().default(0), "scoped_fingerprints": z.record(z.string(), z.string()).optional(), "scope_coverage": z.record(z.string(), z.any()).optional(), "covered_kinds": z.record(z.string(), z.array(z.string())).optional(), "schema_version": z.string().default("1"), "run_sk": z.string().default(""), "rev_ts_run_id": z.string().default("") }).catchall(z.any()).describe("Header row for one pipeline execution.\n\nCallers pass tenancy + identity + the rolled-up step counts; the validator\nderives ``run_sk`` / ``rev_ts_run_id`` from ``created_at`` so the most\nrecent run pages first.");
var StepRunSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "pipeline_run_id": z.string().min(1), "pipeline_id": z.union([z.string(), z.null()]).default(null), "node_name": z.string().min(1), "step_name": z.string().default(""), "step_version": z.string().default(""), "ordinal": z.number().int().default(0), "status": z.string().default("ok"), "error": z.union([z.string(), z.null()]).default(null), "enrichment_count": z.number().int().default(0), "attempts": z.number().int().default(1), "decision_reason": z.union([z.string(), z.null()]).default(null), "decision_summary": z.union([z.string(), z.null()]).default(null), "decision_detail": z.record(z.string(), z.string()).optional(), "started_at": z.union([z.number().int(), z.null()]).default(null), "finished_at": z.union([z.number().int(), z.null()]).default(null), "duration_ms": z.union([z.number().int(), z.null()]).default(null), "idempotency_key": z.union([z.string(), z.null()]).default(null), "input_s3_key": z.union([z.string(), z.null()]).default(null), "output_s3_key": z.union([z.string(), z.null()]).default(null), "schema_version": z.string().default("1"), "runtime_id": z.union([z.string(), z.null()]).default(null), "canonical_tx_id": z.union([z.string(), z.null()]).default(null), "integration_activity_pk": z.union([z.string(), z.null()]).default(null), "integration_activity_sk": z.union([z.string(), z.null()]).default(null), "step_sk": z.string().default("") }).catchall(z.any()).describe("Per-step execution record within a pipeline run.\n\n``status`` mirrors :class:`authe_tx_pipeline.engine.step.StepStatus`\n(``ok`` / ``degraded`` / ``failed``) plus a ``skipped`` value for\ntier/flag/upstream skips. The validator derives ``step_sk`` from the\nzero-padded ``ordinal`` so the rows page in DAG order.");
var ReplayJobSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "pipeline_id": z.string().min(1), "target_kind": z.any().default("run"), "target_run_id": z.union([z.string(), z.null()]).default(null), "target_tx_ids": z.array(z.string()).optional(), "target_group_id": z.union([z.string(), z.null()]).default(null), "chain_id": z.union([z.string(), z.null()]).default(null), "from_block": z.union([z.number().int(), z.null()]).default(null), "to_block": z.union([z.number().int(), z.null()]).default(null), "replay_mode": z.any().default("reuse"), "from_step": z.union([z.string(), z.null()]).default(null), "to_step": z.union([z.string(), z.null()]).default(null), "target_tier": z.union([z.string(), z.null()]).default(null), "pipeline_doc": z.record(z.string(), z.any()).optional(), "pipeline_doc_hash": z.union([z.string(), z.null()]).default(null), "pipeline_version": z.union([z.number().int(), z.null()]).default(null), "pipeline_target_chain_ids": z.array(z.string()).optional(), "user_id": z.union([z.string(), z.null()]).default(null), "body_scope": z.union([z.string(), z.null()]).default(null), "group_scope": z.union([z.string(), z.null()]).default(null), "store_body": z.union([z.boolean(), z.null()]).default(null), "discovery_index": z.union([z.boolean(), z.null()]).default(null), "transaction_summaries": z.union([z.boolean(), z.null()]).default(null), "status": z.any().default("queued"), "requested_by": z.union([z.string(), z.null()]).default(null), "started_at": z.union([z.number().int(), z.null()]).default(null), "finished_at": z.union([z.number().int(), z.null()]).default(null), "total_count": z.number().int().default(0), "processed_count": z.number().int().default(0), "failed_count": z.number().int().default(0), "error": z.union([z.string(), z.null()]).default(null), "applied_configuration": z.union([z.record(z.string(), z.any()), z.null()]).default(null), "deployment_colour": z.union([z.string(), z.null()]).default(null), "application_version": z.union([z.string(), z.null()]).default(null), "qa_config": z.union([z.record(z.string(), z.any()), z.null()]).default(null), "schema_version": z.string().default("1"), "replay_sk": z.string().default(""), "rev_ts_id": z.string().default("") }).catchall(z.any()).describe("A queued/observed replay or backfill request.\n\nCallers pass tenancy + the target selector + mode; the validator derives\n``replay_sk`` / ``rev_ts_id`` so jobs page newest-first.");
var SystemPipelineSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "name": z.string().default(""), "description": z.union([z.string(), z.null()]).default(null), "enabled": z.boolean().default(true), "chain_ids": z.array(z.string()).optional(), "mode": z.any().default("both"), "discovery_mode": z.any().default("event_filter"), "discovery_topic0s": z.array(z.string()).optional(), "companion_topic0s": z.array(z.string()).optional(), "discovery_addresses": z.array(z.string()).optional(), "template_id": z.union([z.string(), z.null()]).default(null), "doc": z.any().optional(), "execution": z.any().optional(), "start_block": z.union([z.number().int().gte(0), z.null()]).default(null), "block_limit": z.union([z.number().int().gte(1), z.null()]).default(null), "poll_interval_seconds": z.number().int().gte(1).lte(3600).default(12), "lag_budget_seconds": z.union([z.number().int().gte(0), z.null()]).default(null), "max_blocks_per_batch": z.union([z.number().int().gte(1).lte(1e4), z.null()]).default(null), "max_blocks_per_batch_by_chain": z.record(z.string(), z.number().int()).optional(), "skip_head_requests": z.record(z.string(), z.any()).optional(), "config_hash": z.union([z.string(), z.null()]).default(null), "created_by": z.union([z.string(), z.null()]).default(null), "updated_at": z.union([z.number().int(), z.null()]).default(null), "updated_by": z.union([z.string(), z.null()]).default(null), "revision": z.number().int().gte(0).default(0), "schema_version": z.string().default("1") }).catchall(z.any()).describe("A platform-managed, always-on pipeline processing public transactions.\n\nCallers pass a name + the chains it targets + the authored ``doc`` (or a\n``template_id`` the worker seeds a doc from); the validator keeps the\nembedded doc id aligned with the row id and stamps the ``spl_`` prefix.");
var SystemPipelineExecutionSchema = z.object({ "transaction_summaries": z.boolean().default(true), "discovery_index": z.union([z.boolean(), z.null()]).default(null), "s3_bodies": z.boolean().default(true), "discovery_projection": z.any().optional(), "write_mode": z.union([z.enum(["batched", "transactional"]), z.null()]).default(null), "body_layout": z.enum(["per_tx", "bundle"]).default("per_tx"), "filter_indexes": z.union([z.boolean(), z.null()]).default(null), "context_store": z.union([z.enum(["durable", "memory"]), z.null()]).default(null), "profile_id": z.union([z.string().max(80), z.null()]).default(null), "profile_revision": z.union([z.number().int().gte(1), z.null()]).default(null), "quality": z.enum(["minimal", "fast", "default", "thorough", "forensic"]).default("default"), "warehouse": z.enum(["off", "inline"]).default("off"), "billing_enabled": z.boolean().default(false), "defer_abi_lookups": z.boolean().default(true), "transaction_concurrency": z.number().int().gte(1).lte(64).default(8), "config": z.record(z.string(), z.any()).optional() }).strict().describe("Portable settings; infrastructure endpoints and credentials stay on the worker.");
var PipelineExecutionPolicySchema = z.object({ "protocols": z.union([z.boolean(), z.null()]).default(null), "graphs": z.union([z.boolean(), z.null()]).default(null), "semantics": z.union([z.boolean(), z.null()]).default(null), "record_fingerprints": z.boolean().default(true), "batch_overlap": z.number().int().gte(1).lte(4).default(1), "block_prefetch": z.number().int().gte(0).lte(4).default(0), "block_prefetch_mode": z.enum(["fetch", "decode"]).default("fetch"), "payloads": z.enum(["explorer", "rpc"]).default("explorer"), "cpu_offload": z.boolean().default(false), "cpu_min_artifacts": z.union([z.number().int().gte(1).lte(1e6), z.null()]).default(null), "skip_failing_transactions_after": z.union([z.number().int().gte(1).lte(100), z.null()]).default(5), "refresh_steps": z.union([z.string(), z.array(z.string())]).optional(), "reconciliation": z.union([z.boolean(), z.string(), z.null()]).default(null), "batch_publication_reads": z.boolean().default(true), "publication_read_batch_wait_ms": z.number().gte(0).lte(1e3).default(2) });
var ExecutionProfileSchema = z.object({ "id": z.string().regex(new RegExp("^[a-z][a-z0-9-]{0,63}$")), "name": z.string().min(1).max(100), "revision": z.number().int().gte(1).default(1), "builtin": z.boolean().default(false), "execution": z.any() }).strict();
var WorkerObservabilitySettingsSchema = z.object({ "log_level": z.enum(["INFO", "WARNING", "ERROR"]).default("INFO"), "telemetry_summaries": z.boolean().default(true), "cursor_window_trail": z.number().int().gte(0).lte(8).default(0), "metrics_enabled": z.union([z.boolean(), z.null()]).default(null), "ddb_ledger": z.enum(["off", "summary", "detail"]).default("summary"), "ddb_capacity": z.enum(["NONE", "TOTAL", "INDEXES"]).default("TOTAL"), "quiet_loggers": z.array(z.string()).optional(), "debug_loggers": z.array(z.string()).optional() }).strict().describe("Operator-visible worker instrumentation.\n\nDefaults are today's behaviour, with ONE deliberate exception:\n``cursor_window_trail`` defaults to off, because it was measured as 88% of a\nchain-cursor row that DynamoDB bills whole on every checkpoint.");
var ChainCursorSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "pipeline_id": z.string().default(""), "chain_id": z.string().default(""), "initialized": z.boolean().default(false), "stop_after_block": z.union([z.number().int(), z.null()]).default(null), "current_block": z.union([z.number().int(), z.null()]).default(null), "lease_owner": z.union([z.string(), z.null()]).default(null), "lease_until": z.number().int().default(0), "telemetry": z.record(z.string(), z.any()).optional(), "skip_head_request": z.union([z.any(), z.null()]).default(null), "skip_head_applied_id": z.union([z.string(), z.null()]).default(null), "skipped_blocks": z.number().int().gte(0).default(0), "last_head_skip": z.record(z.string(), z.any()).optional(), "seed_block": z.number().int().default(0), "last_processed_block": z.number().int().default(0), "head_block": z.number().int().default(0), "confirmed_block": z.number().int().default(0), "blocks_behind": z.number().int().default(0), "block_time_ms": z.union([z.number().int().gte(1), z.null()]).default(null), "status": z.any().default("idle"), "processed_count": z.number().int().default(0), "sample_tx_hashes": z.array(z.string()).optional(), "last_error": z.union([z.string(), z.null()]).default(null), "skipped_windows": z.array(z.record(z.string(), z.any())).optional(), "updated_at": z.number().int().optional() }).catchall(z.any()).describe("Per-(pipeline, chain) block checkpoint for the tailing worker.");
var PipelineBackfillJobSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "pipeline_id": z.string().default(""), "chain_id": z.string().default(""), "from_block": z.number().int().default(0), "to_block": z.number().int().default(0), "overwrite": z.boolean().default(false), "status": z.any().default("queued"), "cursor_block": z.union([z.number().int(), z.null()]).default(null), "requested_by": z.union([z.string(), z.null()]).default(null), "started_at": z.union([z.number().int(), z.null()]).default(null), "finished_at": z.union([z.number().int(), z.null()]).default(null), "processed_count": z.number().int().default(0), "failed_count": z.number().int().default(0), "error": z.union([z.string(), z.null()]).default(null), "sample_tx_hashes": z.array(z.string()).optional(), "pipeline_snapshot": z.union([z.record(z.string(), z.any()), z.null()]).default(null), "telemetry": z.record(z.string(), z.any()).optional(), "worker_id": z.union([z.string(), z.null()]).default(null), "schema_version": z.string().default("1"), "job_sk": z.string().default(""), "rev_ts_id": z.string().default("") }).catchall(z.any()).describe("A queued/observed historical block-range backfill for a system pipeline.");
var WorkerHeartbeatSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "worker_id": z.string().default(""), "host": z.union([z.string(), z.null()]).default(null), "pid": z.union([z.number().int(), z.null()]).default(null), "pipeline_id": z.union([z.string(), z.null()]).default(null), "pipeline_ids": z.array(z.string()).optional(), "chain_id": z.union([z.string(), z.null()]).default(null), "status": z.any().default("starting"), "lag_blocks": z.number().int().default(0), "last_processed_block": z.number().int().default(0), "last_error": z.union([z.string(), z.null()]).default(null), "metrics": z.union([z.any(), z.null()]).default(null), "application_version": z.union([z.string(), z.null()]).default(null), "deployment_colour": z.union([z.string(), z.null()]).default(null), "started_at": z.union([z.number().int(), z.null()]).default(null), "last_seen": z.number().int().optional(), "expires_at": z.number().int().default(0) }).catchall(z.any()).describe("Per-process liveness beacon for the tailing worker daemon.");
var AlertDestinationSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "name": z.string().default(""), "description": z.union([z.string(), z.null()]).default(null), "kind": z.any().default("webhook"), "enabled": z.boolean().default(true), "config": z.record(z.string(), z.any()).optional(), "secret_ref": z.union([z.string(), z.null()]).default(null), "last_delivery_at": z.union([z.number().int(), z.null()]).default(null), "last_delivery_ok": z.union([z.boolean(), z.null()]).default(null), "failure_count": z.number().int().default(0), "created_by": z.union([z.string(), z.null()]).default(null), "updated_at": z.union([z.number().int(), z.null()]).default(null), "updated_by": z.union([z.string(), z.null()]).default(null), "schema_version": z.string().default("1"), "destination_sk": z.string().default("") }).catchall(z.any()).describe("A reusable alert delivery target.\n\n``config`` carries non-secret channel parameters (webhook url, SNS topic\narn, slack channel, connector id); ``secret_ref`` points at the secret\nstore for any credential. The validator derives ``destination_sk``.");
var TriggerRuleRowSchema = z.object({ "owner_user_id": z.union([z.string(), z.null()]).default(null), "visible_to_user_ids": z.array(z.string()).optional(), "visible_to_team_ids": z.array(z.string()).optional(), "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "name": z.string().default(""), "description": z.union([z.string(), z.null()]).default(null), "scope": z.any().default("user"), "enabled": z.boolean().default(true), "severity": z.any().default("medium"), "priority": z.number().int().default(100), "pipeline_id": z.union([z.string(), z.null()]).default(null), "source_watch_id": z.union([z.string(), z.null()]).default(null), "source_watch_sk": z.union([z.string(), z.null()]).default(null), "chain_ids": z.array(z.string()).optional(), "saga_kinds": z.array(z.string()).optional(), "condition": z.record(z.string(), z.any()).optional(), "actions": z.array(z.record(z.string(), z.any())).optional(), "destination_ids": z.array(z.string()).optional(), "cooldown_seconds": z.number().int().default(0), "quiet_hours": z.union([z.record(z.string(), z.any()), z.null()]).default(null), "muted_until": z.union([z.number().int(), z.null()]).default(null), "match_count": z.number().int().default(0), "last_matched_at": z.union([z.number().int(), z.null()]).default(null), "created_by": z.union([z.string(), z.null()]).default(null), "updated_at": z.union([z.number().int(), z.null()]).default(null), "updated_by": z.union([z.string(), z.null()]).default(null), "schema_version": z.string().default("1"), "rule_sk": z.string().default("") }).catchall(z.any()).describe("A persisted alert / trigger rule (condition tree + actions).\n\nCallers pass tenancy + the condition/actions payload; the validator\nderives ``rule_sk``.");
var WatchRowSchema = z.object({ "owner_user_id": z.union([z.string(), z.null()]).default(null), "visible_to_user_ids": z.array(z.string()).optional(), "visible_to_team_ids": z.array(z.string()).optional(), "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "name": z.string().default(""), "description": z.union([z.string(), z.null()]).default(null), "kind": z.any().default("address"), "enabled": z.boolean().default(true), "severity": z.any().default("medium"), "scope": z.any().default("user"), "target": z.string().default(""), "params": z.record(z.string(), z.any()).optional(), "chain_ids": z.array(z.string()).optional(), "pipeline_id": z.union([z.string(), z.null()]).default(null), "destination_ids": z.array(z.string()).optional(), "trigger_rule_id": z.union([z.string(), z.null()]).default(null), "muted_until": z.union([z.number().int(), z.null()]).default(null), "hit_count": z.number().int().default(0), "last_hit_at": z.union([z.number().int(), z.null()]).default(null), "created_by": z.union([z.string(), z.null()]).default(null), "updated_at": z.union([z.number().int(), z.null()]).default(null), "updated_by": z.union([z.string(), z.null()]).default(null), "schema_version": z.string().default("1"), "watch_sk": z.string().default(""), "target_key": z.string().default("") }).catchall(z.any()).describe("A persisted watch on one on-chain subject.\n\nCallers pass tenancy + ``kind``/``target``; the validator normalises the\ntarget and derives ``target_key`` and ``watch_sk``.");
var TriggerEventRowSchema = z.object({ "owner_user_id": z.union([z.string(), z.null()]).default(null), "visible_to_user_ids": z.array(z.string()).optional(), "visible_to_team_ids": z.array(z.string()).optional(), "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "audience_version": z.union([z.number().int(), z.null()]).default(null), "rule_id": z.string().min(1), "rule_name": z.string().default(""), "severity": z.any().default("medium"), "summary": z.string().default(""), "canonical_tx_id": z.union([z.string(), z.null()]).default(null), "tx_hash": z.union([z.string(), z.null()]).default(null), "chain_id": z.union([z.string(), z.null()]).default(null), "group_id": z.union([z.string(), z.null()]).default(null), "pipeline_id": z.union([z.string(), z.null()]).default(null), "pipeline_run_id": z.union([z.string(), z.null()]).default(null), "payload": z.record(z.string(), z.any()).optional(), "dispatched_destination_ids": z.array(z.string()).optional(), "delivery_status": z.record(z.string(), z.string()).optional(), "notification_id": z.union([z.string(), z.null()]).default(null), "matched_at": z.union([z.number().int(), z.null()]).default(null), "acknowledged_at": z.union([z.number().int(), z.null()]).default(null), "acknowledged_by": z.union([z.string(), z.null()]).default(null), "superseded_at": z.union([z.number().int(), z.null()]).default(null), "superseded_by_tx_id": z.union([z.string(), z.null()]).default(null), "expires_at": z.union([z.number().int(), z.null()]).default(null), "schema_version": z.string().default("1"), "event_sk": z.string().default(""), "rev_ts_id": z.string().default("") }).catchall(z.any()).describe("A persisted record of one fired trigger rule.\n\nCallers pass tenancy + the match payload; the validator derives both key\nshadows \u2014 ``event_sk`` (the id, so re-matching overwrites) and the\nreverse-time ``rev_ts_id`` the feeds page newest-first by.");
var ChainScopedAddressSchema = z.object({ "chain_id": z.string().describe("The platform's canonical chain id \u2014 CAIP-2 (``eip155:1``, ``solana:5eykt4us\u2026``). Accepted in any spelling and folded on construction via :func:`commons.chains.chain_key`, so ``0x1`` / ``eth`` / ``eip155:1`` all store the same value. That fold is load-bearing here rather than cosmetic: ``__str__`` is the de-dup key in :func:`commons.ddb.index_items._participant_addresses`, so two spellings would count one address twice."), "value": z.string().describe("Address string in its **chain-family-canonical form** \u2014 lower-case hex for EVM, bech32 with lower HRP for BTC, case-preserving base58 for BTC base58 + Solana.") }).strict().describe("``(chain_id, value)`` pair carried by every participant / index row.\n\nFrozen so two participants comparing the same address-on-chain\nproduce the same hash key \u2014 critical for de-dup in\n:func:`commons.ddb.index_items._participant_addresses`.");
var TracerSchema = z.object({ "owner_user_id": z.union([z.string(), z.null()]).default(null), "visible_to_user_ids": z.array(z.string()).optional(), "visible_to_team_ids": z.array(z.string()).optional(), "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "name": z.string().default(""), "description": z.union([z.string(), z.null()]).default(null), "scope": z.any().default("user"), "status": z.any().default("active"), "investigation_id": z.union([z.string(), z.null()]).default(null), "seeds": z.array(z.any()).optional(), "direction": z.any().default("outbound"), "max_hops": z.number().int().gte(1).lte(8).default(3), "chain_ids": z.array(z.string()).optional(), "token_filters": z.array(z.string()).optional(), "depth": z.union([z.enum(["header", "body", "live", "mixed"]), z.null()]).default(null), "min_value_raw": z.union([z.string().regex(new RegExp("^[0-9]+$")), z.null()]).default(null), "follow_bridges": z.boolean().default(true), "last_run_id": z.union([z.string(), z.null()]).default(null), "last_run_at": z.union([z.number().int(), z.null()]).default(null), "run_count": z.number().int().default(0), "created_by": z.union([z.string(), z.null()]).default(null), "updated_at": z.union([z.number().int(), z.null()]).default(null), "updated_by": z.union([z.string(), z.null()]).default(null), "schema_version": z.string().default("1"), "tracer_sk": z.string().default("") }).catchall(z.any()).describe("A persisted flow-tracking definition.\n\nCallers pass tenancy + the seed addresses + walk parameters; the validator\nderives ``tracer_sk``.");
var TracerRunSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "org_id_tenant_id": z.string().default("main-main"), "customer_id": z.union([z.string(), z.null()]).default(null), "customer_org_tenant_pk_v2": z.string().default(""), "tracer_id": z.string().min(1), "triggered_by_run_id": z.union([z.string(), z.null()]).default(null), "triggered_by_tx_id": z.union([z.string(), z.null()]).default(null), "status": z.any().default("running"), "hops_walked": z.number().int().default(0), "node_count": z.number().int().default(0), "edge_count": z.number().int().default(0), "total_value_raw": z.string().default("0"), "terminal_node_count": z.number().int().default(0), "chain_ids": z.array(z.string()).optional(), "multi_chain": z.boolean().default(false), "nodes": z.array(z.record(z.string(), z.any())).optional(), "edges": z.array(z.record(z.string(), z.any())).optional(), "graph_s3_key": z.union([z.string(), z.null()]).default(null), "graph_bytes": z.number().int().default(0), "started_at": z.union([z.number().int(), z.null()]).default(null), "finished_at": z.union([z.number().int(), z.null()]).default(null), "duration_ms": z.union([z.number().int(), z.null()]).default(null), "error": z.union([z.string(), z.null()]).default(null), "bounded_by": z.array(z.string()).optional(), "status_reason": z.union([z.string(), z.null()]).default(null), "schema_version": z.string().default("1"), "run_sk": z.string().default("") }).catchall(z.any()).describe("A persisted snapshot of a tracer's N-hop flow graph.\n\nCallers pass the tracer id + the discovered graph; the validator derives\nthe reverse-time ``run_sk``.");
var EventBaseSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org": z.string(), "org_id": z.string(), "tenant_id": z.string(), "org_id_tenant_id": z.string(), "address": z.union([z.string(), z.null()]).default(null), "chain": z.any().default("unknown"), "resource": z.any(), "criticality_level": z.any().default("4"), "message_summary": z.string().default(""), "message": z.string().default(""), "link_id": z.string().default(""), "created_by": z.string().default(""), "created_by_service": z.string().default(""), "metadata": z.record(z.string(), z.any()).optional() }).describe("Base shape for every commons event.\n\nConcrete subclasses set the class-level :attr:`resource` and\n:attr:`event_version` and add any domain fields they need.");
var AddressEventSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org": z.string(), "org_id": z.string(), "tenant_id": z.string(), "org_id_tenant_id": z.string(), "address": z.union([z.string(), z.null()]).default(null), "chain": z.any().default("unknown"), "resource": z.any().default("address"), "criticality_level": z.any().default("4"), "message_summary": z.string().default(""), "message": z.string().default(""), "link_id": z.string().default(""), "created_by": z.string().default(""), "created_by_service": z.string().default(""), "metadata": z.record(z.string(), z.any()).optional(), "labels": z.union([z.array(z.string()), z.null()]).default(null), "chains": z.union([z.array(z.string()), z.null()]).default(null), "address_type": z.union([z.string(), z.null()]).default(null) }).describe("Latest address event (v3-equivalent, Pydantic edition).");
var TransactionEventSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org": z.string(), "org_id": z.string(), "tenant_id": z.string(), "org_id_tenant_id": z.string(), "address": z.union([z.string(), z.null()]).default(null), "chain": z.any().default("unknown"), "resource": z.any().default("transaction"), "criticality_level": z.any().default("4"), "message_summary": z.string().default(""), "message": z.string().default(""), "link_id": z.string().default(""), "created_by": z.string().default(""), "created_by_service": z.string().default(""), "metadata": z.record(z.string(), z.any()).optional(), "transaction_type": z.union([z.string(), z.null()]).default(null), "transaction_hash": z.union([z.string(), z.null()]).default(null), "transaction_status": z.union([z.string(), z.null()]).default(null), "transaction_timestamp": z.union([z.string(), z.null()]).default(null), "transaction_amount": z.union([z.string(), z.null()]).default(null), "transaction_currency": z.union([z.string(), z.null()]).default(null), "transaction_from": z.union([z.string(), z.null()]).default(null), "transaction_to": z.union([z.string(), z.null()]).default(null) });
var LabelEventSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org": z.string(), "org_id": z.string(), "tenant_id": z.string(), "org_id_tenant_id": z.string(), "address": z.union([z.string(), z.null()]).default(null), "chain": z.any().default("unknown"), "resource": z.any().default("label"), "criticality_level": z.any().default("4"), "message_summary": z.string().default(""), "message": z.string().default(""), "link_id": z.string().default(""), "created_by": z.string().default(""), "created_by_service": z.string().default(""), "metadata": z.record(z.string(), z.any()).optional(), "label_id": z.union([z.string(), z.null()]).default(null), "label_name": z.union([z.string(), z.null()]).default(null), "label_type": z.union([z.string(), z.null()]).default(null) });
var TagEventSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org": z.string(), "org_id": z.string(), "tenant_id": z.string(), "org_id_tenant_id": z.string(), "address": z.union([z.string(), z.null()]).default(null), "chain": z.any().default("unknown"), "resource": z.any().default("tag"), "criticality_level": z.any().default("4"), "message_summary": z.string().default(""), "message": z.string().default(""), "link_id": z.string().default(""), "created_by": z.string().default(""), "created_by_service": z.string().default(""), "metadata": z.record(z.string(), z.any()).optional(), "tag_id": z.union([z.string(), z.null()]).default(null), "tag_name": z.union([z.string(), z.null()]).default(null) });
var GroupEventSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org": z.string(), "org_id": z.string(), "tenant_id": z.string(), "org_id_tenant_id": z.string(), "address": z.union([z.string(), z.null()]).default(null), "chain": z.any().default("unknown"), "resource": z.any().default("group"), "criticality_level": z.any().default("4"), "message_summary": z.string().default(""), "message": z.string().default(""), "link_id": z.string().default(""), "created_by": z.string().default(""), "created_by_service": z.string().default(""), "metadata": z.record(z.string(), z.any()).optional(), "group_id": z.union([z.string(), z.null()]).default(null), "group_name": z.union([z.string(), z.null()]).default(null) });
var EntityEventSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org": z.string(), "org_id": z.string(), "tenant_id": z.string(), "org_id_tenant_id": z.string(), "address": z.union([z.string(), z.null()]).default(null), "chain": z.any().default("unknown"), "resource": z.any().default("entity"), "criticality_level": z.any().default("4"), "message_summary": z.string().default(""), "message": z.string().default(""), "link_id": z.string().default(""), "created_by": z.string().default(""), "created_by_service": z.string().default(""), "metadata": z.record(z.string(), z.any()).optional(), "entity_id": z.union([z.string(), z.null()]).default(null), "entity_name": z.union([z.string(), z.null()]).default(null), "entity_type": z.union([z.string(), z.null()]).default(null) });
var AbiEventSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org": z.string(), "org_id": z.string(), "tenant_id": z.string(), "org_id_tenant_id": z.string(), "address": z.union([z.string(), z.null()]).default(null), "chain": z.any().default("unknown"), "resource": z.any().default("abi"), "criticality_level": z.any().default("4"), "message_summary": z.string().default(""), "message": z.string().default(""), "link_id": z.string().default(""), "created_by": z.string().default(""), "created_by_service": z.string().default(""), "metadata": z.record(z.string(), z.any()).optional(), "abi_id": z.union([z.string(), z.null()]).default(null), "contract_address": z.union([z.string(), z.null()]).default(null) });
var ProxyEventSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org": z.string(), "org_id": z.string(), "tenant_id": z.string(), "org_id_tenant_id": z.string(), "address": z.union([z.string(), z.null()]).default(null), "chain": z.any().default("unknown"), "resource": z.any().default("proxy"), "criticality_level": z.any().default("4"), "message_summary": z.string().default(""), "message": z.string().default(""), "link_id": z.string().default(""), "created_by": z.string().default(""), "created_by_service": z.string().default(""), "metadata": z.record(z.string(), z.any()).optional(), "proxy_address": z.union([z.string(), z.null()]).default(null), "implementation_addresses": z.array(z.string()).optional(), "proxy_pattern": z.union([z.string(), z.null()]).default(null), "detection_methods": z.array(z.string()).optional(), "block_number": z.union([z.number().int(), z.null()]).default(null), "valid_from_block": z.union([z.number().int(), z.null()]).default(null), "valid_until_block": z.union([z.number().int(), z.null()]).default(null), "depth": z.number().int().default(0), "is_proxy": z.boolean().default(false) }).describe("Emitted whenever a proxy chain is discovered or refreshed.\n\nCarries the resolved implementation set, the dominant pattern\n(``eip1967`` / ``eip1822`` / ``eip1167`` / ``beacon`` / ``diamond``\n/ ``getter:<name>`` / ``metadata`` / ``ambiguous``), and the block\nwindow the resolution is valid for. Per-strategy evidence + audit\ntiming land in the inherited ``metadata`` dict so dashboards can\ndrill in without a separate event type.");
var StreamEventSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org": z.string(), "org_id": z.string(), "tenant_id": z.string(), "org_id_tenant_id": z.string(), "address": z.union([z.string(), z.null()]).default(null), "chain": z.any().default("unknown"), "resource": z.any().default("stream"), "criticality_level": z.any().default("4"), "message_summary": z.string().default(""), "message": z.string().default(""), "link_id": z.string().default(""), "created_by": z.string().default(""), "created_by_service": z.string().default(""), "metadata": z.record(z.string(), z.any()).optional(), "stream_id": z.union([z.string(), z.null()]).default(null) });
var UserEventSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org": z.string(), "org_id": z.string(), "tenant_id": z.string(), "org_id_tenant_id": z.string(), "address": z.union([z.string(), z.null()]).default(null), "chain": z.any().default("unknown"), "resource": z.any().default("user"), "criticality_level": z.any().default("4"), "message_summary": z.string().default(""), "message": z.string().default(""), "link_id": z.string().default(""), "created_by": z.string().default(""), "created_by_service": z.string().default(""), "metadata": z.record(z.string(), z.any()).optional(), "user_id": z.union([z.string(), z.null()]).default(null) });
var OrganizationEventSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org": z.string(), "org_id": z.string(), "tenant_id": z.string(), "org_id_tenant_id": z.string(), "address": z.union([z.string(), z.null()]).default(null), "chain": z.any().default("unknown"), "resource": z.any().default("organization"), "criticality_level": z.any().default("4"), "message_summary": z.string().default(""), "message": z.string().default(""), "link_id": z.string().default(""), "created_by": z.string().default(""), "created_by_service": z.string().default(""), "metadata": z.record(z.string(), z.any()).optional() });
var AuthenticationEventSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org": z.string(), "org_id": z.string(), "tenant_id": z.string(), "org_id_tenant_id": z.string(), "address": z.union([z.string(), z.null()]).default(null), "chain": z.any().default("unknown"), "resource": z.any().default("authentication"), "criticality_level": z.any().default("4"), "message_summary": z.string().default(""), "message": z.string().default(""), "link_id": z.string().default(""), "created_by": z.string().default(""), "created_by_service": z.string().default(""), "metadata": z.record(z.string(), z.any()).optional(), "authentication_type": z.union([z.string(), z.null()]).default(null) });
var ActivityLogEventSchema = z.object({ "id": z.string().optional(), "created_at": z.number().int().optional(), "org": z.string(), "org_id": z.string(), "tenant_id": z.string(), "org_id_tenant_id": z.string(), "address": z.union([z.string(), z.null()]).default(null), "chain": z.any().default("unknown"), "resource": z.any().default("activity_log"), "criticality_level": z.any().default("4"), "message_summary": z.string().default(""), "message": z.string().default(""), "link_id": z.string().default(""), "created_by": z.string().default(""), "created_by_service": z.string().default(""), "metadata": z.record(z.string(), z.any()).optional(), "function_name": z.string(), "function_version": z.union([z.string(), z.null()]).default(null), "function_arn": z.union([z.string(), z.null()]).default(null), "aws_request_id": z.union([z.string(), z.null()]).default(null), "correlation_id": z.union([z.string(), z.null()]).default(null), "requester_org_id": z.union([z.string(), z.null()]).default(null), "requester_tenant_id": z.union([z.string(), z.null()]).default(null), "requester_user_id": z.union([z.string(), z.null()]).default(null), "requester_application": z.union([z.string(), z.null()]).default(null), "source_ip": z.union([z.string(), z.null()]).default(null), "user_agent": z.union([z.string(), z.null()]).default(null), "invoked_at": z.number().int(), "timestamp": z.number().int().optional(), "duration_ms": z.union([z.number(), z.null()]).default(null), "cold_start": z.boolean().default(false), "deployment_colour": z.union([z.string(), z.null()]).default(null), "success": z.boolean().default(true), "status_code": z.union([z.number().int(), z.null()]).default(null), "payload_summary": z.record(z.string(), z.any()).optional(), "error_summary": z.union([z.record(z.string(), z.any()), z.null()]).default(null), "ttl": z.union([z.number().int(), z.null()]).default(null) }).describe("Emitted by ``@lambda_handler`` for every invocation.\n\nRecords who/what hit the Lambda, plus a high-level summary of the request\nso UI consumers can show an audit trail without re-reading raw logs.");
var TenantContextSchema = z.object({ "customer_id": z.union([z.string(), z.null()]).default(null), "org_id": z.string().default("main"), "tenant_id": z.string().default("main"), "user_id": z.union([z.string(), z.null()]).default(null), "api_key_id": z.union([z.string(), z.null()]).default(null), "org_id_tenant_id": z.string().default("main-main") }).describe("Per-request tenancy quadruple (+ optional attribution handle).\n\nFrozen / hashable so it can be used as a dict key (e.g. for per-tenant\ncaches) and to make sure no step accidentally mutates it mid-pipeline.\n\n``api_key_id`` is NOT a tenancy segment and never participates in key\nderivation. It exists so a site handed only a :class:`TenantContext` can\nstill name the actor of a service-key call via\n:func:`commons.keys.actor_of` \u2014 without it, ``user_id=None`` for a service\nkey would leave attribution empty (the B5 structural blocker).");
var NormalizedTaskSchema = z.object({ "execution_time": z.number().default(0), "error": z.union([z.record(z.string(), z.any()), z.null()]).default(null), "meta_data": z.record(z.string(), z.any()).optional() }).catchall(z.any()).describe("Typed envelope returned by :func:`normalize_task_result`.\n\nMirrors the dict shape produced by :func:`get_task_meta_data` so\ncallers can opt into the typed surface incrementally.");
var TxTriggerEventSchema = z.object({ "id": z.string(), "rule_id": z.string(), "rule_name": z.string().default(""), "customer_id": z.union([z.string(), z.null()]).default(null), "tx_id": z.string(), "tx_hash": z.union([z.string(), z.null()]).default(null), "chain_id": z.union([z.string(), z.null()]).default(null), "group_id": z.union([z.string(), z.null()]).default(null), "finality": z.string().default("unknown"), "occurred_at": z.string().datetime({ offset: true }), "severity": z.union([z.string(), z.null()]).default(null), "matched_enrichments": z.array(z.record(z.string(), z.any())).optional(), "metadata": z.record(z.string(), z.any()).optional() }).describe("One trigger event emitted by a rule match on a single transaction.");
var CorrectionEventSchema = z.object({ "canonical_tx_id": z.string().describe("ID of the canonical row that needs to be corrected. Downstream consumers use this to look up whatever derived state was built from it."), "chain_id": z.string(), "block_number": z.number().int(), "old_block_hash": z.string().describe("The block hash this canonical row claimed when it was emitted."), "new_block_hash": z.union([z.string(), z.null()]).describe("The block hash the chain state currently reports for the same block_number. None when the new branch is not yet known (rare; happens for deep reorgs where we only saw the divergence).").default(null), "reason": z.enum(["reorg", "supersede_mempool", "manual"]).default("reorg"), "detected_at": z.string().datetime({ offset: true }).optional(), "detector_step_name": z.string().default("reorg_detector"), "detector_step_version": z.string().default("0.1.0"), "pipeline_run_id": z.union([z.string(), z.null()]).default(null), "tx_correlation_id": z.union([z.string(), z.null()]).default(null) }).describe("A structured rollback signal.\n\nFields are stable and persisted; renaming requires a migration. The\npipeline library defines the model; the AWS-side repo defines how to\ntransport it (SNS, EventBridge, Kinesis).");

// src/models/wire/semantic.ts
var SEMANTIC_NODE_KINDS = [
  "proposal",
  "message",
  "envelope",
  "payload",
  "action",
  "batch",
  "multicall",
  "transfer",
  "swap",
  "order_place",
  "arbitrage",
  "approval",
  "deposit",
  "withdraw",
  "mint",
  "burn",
  "ownership_transfer",
  "contract_deploy",
  "instance_create",
  "account_close",
  "bridge_send",
  "bridge_receive",
  "governance_execute",
  "safe_execution",
  "user_operation",
  "unknown"
];
var SEMANTIC_ACTOR_ROLES = [
  "initiator",
  "sender",
  "receiver",
  "relayer",
  "spender",
  "owner",
  "contract",
  "counterparty",
  "other"
];
var AMOUNT_BASIS = {
  ACTUAL: "actual",
  QUOTED_MINIMUM: "quoted_minimum"
};
var ORDER_STATUS = {
  OPEN: "open",
  FILLED: "filled"
};
var SemanticNodeSchema = z.lazy(
  () => z.object({
    id: z.string(),
    kind: z.string().default("unknown"),
    title: z.string(),
    protocol_id: z.union([z.string(), z.null()]).default(null),
    category: z.union([z.string(), z.null()]).default(null),
    chain_id: z.union([z.string(), z.null()]).default(null),
    actors: z.array(SemanticActorSchema).default([]),
    assets: z.array(SemanticAssetSchema).default([]),
    attributes: z.record(z.string(), z.unknown()).default({}),
    children: z.array(SemanticNodeSchema).default([]),
    source_refs: z.array(SemanticSourceRefSchema).default([]),
    confidence: z.number().min(0).max(1).default(1),
    evidence: z.union([EvidenceSchema, z.null()]).default(null),
    proxy_chain: z.array(z.string()).default([])
  })
);
var SemanticTreeSchema = z.object({
  version: z.string().default("1"),
  summary: z.union([z.string(), z.null()]).default(null),
  roots: z.array(SemanticNodeSchema).default([])
});
function walkSemanticTree(tree) {
  const out = [];
  const visit = (node) => {
    out.push(node);
    for (const child of node.children) visit(child);
  };
  for (const root of tree.roots) visit(root);
  return out;
}
var ContractObservationSchema = z.object({
  observation_id: z.string(),
  chain_id: z.string(),
  subject: z.string(),
  target: z.string(),
  predicate: z.string(),
  category: z.string(),
  source: z.string(),
  assurance: z.enum(["observed", "inferred", "candidate"]),
  confidence: z.number().min(0).max(1),
  confidence_reason: z.string(),
  block_number: z.number().int().nonnegative().nullish(),
  block_hash: z.string().nullish(),
  observed_at: z.number().int().nonnegative().nullish(),
  finality: z.enum(["canonical", "safe", "finalized", "observed"]),
  value_ref: z.string(),
  output_path: z.string().default(""),
  signature: z.string().nullish(),
  selector: z.string().nullish(),
  slot: z.string().nullish(),
  storage_offset: z.number().nullish(),
  abi_source: z.string().nullish(),
  abi_fingerprint: z.string().nullish(),
  code_hash: z.string().nullish(),
  call_from: z.string().nullish(),
  params: z.array(z.unknown()).default([]),
  implementation_addresses: z.array(z.string()).default([]),
  context: z.record(z.string(), z.unknown()).default({}),
  extractor_version: z.string()
}).superRefine((fact, ctx) => {
  const family = resolveChain(fact.chain_id)?.family;
  const hash = fact.block_hash;
  const dated = fact.finality === "observed" ? fact.source === "account_state" && fact.observed_at != null : fact.block_number != null && hash != null;
  const hashValid = hash == null || (family === "near" || family === "solana" ? /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(hash) : (family === "evm" ? /^0x[0-9a-fA-F]{64}$/ : /^(?:0x)?[0-9a-fA-F]{64}$/).test(hash));
  if (!dated || !hashValid || !family || !isValidAddress(fact.subject, fact.chain_id) || !isValidAddress(fact.target, fact.chain_id)) {
    ctx.addIssue({ code: "custom", message: "Invalid chain-specific observation provenance" });
  }
});
var ContractRelationshipOptionsSchema = z.object({
  enabled: z.boolean(),
  native_accounts: z.boolean(),
  getters: z.boolean(),
  proxies: z.boolean(),
  roles: z.boolean(),
  role_events: z.boolean(),
  role_event_lookback_blocks: z.number().int().min(1).max(1e5),
  modules: z.boolean(),
  bytecode: z.boolean(),
  inferred_storage: z.boolean(),
  drilldown: z.boolean(),
  max_depth: z.number().int().min(0).max(3),
  max_contracts: z.number().int().min(1).max(64),
  max_variables: z.number().int().min(1).max(64),
  max_observations: z.number().int().min(1).max(1024),
  max_rpc_calls: z.number().int().min(1).max(1024),
  max_lookup_calls: z.number().int().min(1).max(512),
  max_role_members: z.number().int().min(1).max(64),
  timeout_seconds: z.number().positive().max(120),
  observed_confidence: z.number().min(0).max(1),
  inferred_confidence: z.number().min(0).max(1),
  candidate_confidence: z.number().min(0).max(1)
}).partial().strict();
var COMBINE_STRATEGIES = [
  "collect",
  "sum",
  "cross_chain_join_by_address"
];
var DrillDownRuleSchema = z.object({
  enabled: z.boolean().default(false),
  max_depth: z.number().int().gte(0).lte(8).default(1),
  address_types: z.array(z.string()).default(["address", "address[]"]),
  from_functions: z.array(z.string()).default([]),
  variables: z.array(StateVarRefSchema).max(1e3).default([]),
  inherit_variables: z.boolean().default(true),
  dedupe: z.boolean().default(true),
  combine: z.union([CombineRuleSchema, z.null()]).default(null)
});
var ContractTargetSchema = z.object({
  address: z.string(),
  name: z.union([z.string(), z.null()]).default(null),
  category: z.union([z.string(), z.null()]).default(null),
  variables: z.array(StateVarRefSchema).max(1e3).default([]),
  use_category_defaults: z.boolean().default(true),
  auto_detect_proxy: z.boolean().default(true),
  // No `proxy_detection_method`: the backend model dropped it (the canonical
  // `ProxyDiscoverer.discover` takes no strategy selector) and every model on
  // this request forbids extra keys, so declaring it here is how a builder came
  // to send a field that 400s the whole read.
  drilldown: z.union([DrillDownRuleSchema, z.null()]).default(null),
  combine: z.union([CombineRuleSchema, z.null()]).default(null)
});
var ChainStateRequestSchema = z.object({
  chain_id: z.string(),
  block: BlockSpecSchema.optional(),
  contracts: z.array(ContractTargetSchema).max(128).default([])
});
var ContractStateRequestSchema = z.object({
  relationships: ContractRelationshipOptionsSchema.nullish(),
  request_id: z.union([z.string(), z.null()]).default(null),
  chains: z.array(ChainStateRequestSchema).max(16).default([]),
  drilldown_defaults: z.union([DrillDownRuleSchema, z.null()]).default(null),
  combine: z.union([CombineRuleSchema, z.null()]).default(null),
  budget: RpcBudgetSchema.optional(),
  include_source_code: z.boolean().default(false)
});
var ContractStateNodeSchema = z.lazy(
  () => z.object({
    ref: z.string(),
    chain_id: z.string(),
    address: z.string(),
    name: z.union([z.string(), z.null()]).default(null),
    category: z.union([z.string(), z.null()]).default(null),
    block_number: z.union([z.number().int(), z.null()]).default(null),
    block_hash: z.union([z.string(), z.null()]).default(null),
    block_tag: z.union([z.string(), z.null()]).default(null),
    is_contract: z.boolean().default(true),
    is_proxy: z.boolean().default(false),
    proxy_pattern: z.union([z.string(), z.null()]).default(null),
    proxy_admin: z.string().nullable().default(null),
    proxy_chain: z.array(z.string()).default([]),
    proxy_depth: z.number().int().default(0),
    implementation_addresses: z.array(z.string()).default([]),
    abi_source: z.union([z.string(), z.null()]).default(null),
    source_code_available: z.boolean().default(false),
    values: z.array(StateValueSchema).default([]),
    relationships: z.array(ContractObservationSchema).default([]),
    relationship_coverage: z.array(z.string()).default([]),
    proxy_provenance: z.record(z.string(), z.unknown()).default({}),
    abi_provenance: z.array(z.record(z.string(), z.unknown())).default([]),
    relationship_analysis: z.record(z.string(), z.unknown()).default({}),
    children: z.array(ContractStateNodeSchema).default([]),
    combined: z.array(CombinedValueSchema).default([]),
    errors: z.array(z.string()).default([]),
    rpc_calls: z.number().int().default(0),
    lookup_calls: z.number().int().default(0),
    truncated: z.boolean().default(false)
  })
);
var ContractStateResultSchema = z.object({
  request_id: z.union([z.string(), z.null()]).default(null),
  nodes: z.array(ContractStateNodeSchema).default([]),
  index: z.record(z.string(), StateValueSchema).default({}),
  combined: z.array(CombinedValueSchema).default([]),
  rpc_calls: z.number().int().default(0),
  lookup_calls: z.number().int().default(0),
  errors: z.array(z.string()).default([]),
  truncated: z.boolean().default(false)
});
function walkContractStateTree(result) {
  const out = [];
  const visit = (node) => {
    out.push(node);
    for (const child of node.children) visit(child);
  };
  for (const node of result?.nodes ?? []) visit(node);
  return out;
}
var ChainInsightInputSchema = z.object({
  chain_id: z.string(),
  block_number: z.number().int().gte(0),
  transaction_hash: z.string().regex(/^0x[0-9a-fA-F]{64}$/).nullable().default(null),
  transaction_index: z.number().int().nonnegative().nullable().default(null),
  logs: z.array(z.record(z.string(), z.any())).default([]),
  transfers: z.array(TransferEventSchema).max(4096).default([]),
  native_transfers: z.array(NativeTransferSchema).max(4096).default([]),
  extra_holders: z.array(z.string()).default([]),
  extra_tokens: z.array(z.string()).default([])
});
var TransactionInsightRequestSchema = z.object({
  request_id: z.union([z.string(), z.null()]).default(null),
  chains: z.array(ChainInsightInputSchema).max(16).default([]),
  enrichers: z.array(z.string()).default(["balances", "identity"]),
  timeline_strategy: z.enum(["events", "none", "trace"]).default("events"),
  identity_overrides: z.record(z.string(), IdentityOverrideSchema).default({}),
  identity_provider_order: z.array(z.string()).default(["provided", "catalog", "external"]),
  budget: RpcBudgetSchema.optional(),
  contract_state_request: z.union([ContractStateRequestSchema, z.null()]).default(null)
});
var BalanceChangeSchema = z.object({
  chain_id: z.string(),
  holder: z.string(),
  balance_scope: z.enum(["block", "transaction"]).default("block"),
  timeline_scope: z.enum(["estimated", "transaction", "none"]).default("none"),
  before_block: z.number().int().nullable().default(null),
  after_block: z.number().int().nullable().default(null),
  transaction_delta_raw: z.string().nullable().default(null),
  warnings: z.array(z.string()).default([]),
  token_address: z.union([z.string(), z.null()]).default(null),
  is_native: z.boolean().default(false),
  symbol: z.union([z.string(), z.null()]).default(null),
  decimals: z.union([z.number().int(), z.null()]).default(null),
  before_raw: z.union([z.string(), z.null()]).default(null),
  after_raw: z.union([z.string(), z.null()]).default(null),
  delta_raw: z.union([z.string(), z.null()]).default(null),
  before_display: z.union([z.string(), z.null()]).default(null),
  after_display: z.union([z.string(), z.null()]).default(null),
  delta_display: z.union([z.string(), z.null()]).default(null),
  during: z.array(BalanceTimelinePointSchema).default([]),
  truncated: z.boolean().default(false),
  error: z.union([z.string(), z.null()]).default(null)
});
var TransactionStateInsightSchema = z.object({
  request_id: z.union([z.string(), z.null()]).default(null),
  balance_changes: z.array(BalanceChangeSchema).default([]),
  identities: z.record(z.string(), AddressIdentitySchema).default({}),
  contract_states: z.union([ContractStateResultSchema, z.null()]).default(null),
  rpc_calls: z.number().int().default(0),
  lookup_calls: z.number().int().default(0),
  errors: z.array(z.string()).default([]),
  truncated: z.boolean().default(false)
});
var TRANSACTION_SCOPES = [
  "user",
  "tenant",
  "team",
  "org",
  "customer",
  "global"
];
var TRANSACTION_SCOPE_LABELS = {
  user: "Your copy",
  tenant: "Shared with tenant",
  team: "Other users",
  org: "Organization",
  customer: "Customer",
  global: "Global"
};
var TRANSACTION_SCOPE_DESCRIPTIONS = {
  user: "Your private copy of this transaction.",
  tenant: "The copy shared with everyone in this tenant.",
  team: "Private copies held by other users \u2014 these are not viewable.",
  org: "An organization-level copy, shared across this org's tenants.",
  customer: "A customer-level copy, shared across the whole account.",
  global: "The shared global record of this on-chain transaction."
};
function transactionScopeLabel(scope) {
  return TRANSACTION_SCOPE_LABELS[scope] ?? scope;
}
function transactionScopeDescription(scope) {
  return TRANSACTION_SCOPE_DESCRIPTIONS[scope] ?? "";
}
var ScopeOptionSchema = z.object({
  scope: z.string(),
  readable: z.boolean().default(true),
  count: z.number().int().default(1),
  overrides: z.record(z.string(), z.string()).default({})
});
var CrossChainMemberSchema = z.object({
  tx_id: z.union([z.string(), z.null()]).default(null),
  tx_hash: z.union([z.string(), z.null()]).default(null),
  chain_id: z.union([z.string(), z.null()]).default(null),
  role: z.union([z.string(), z.null()]).default(null),
  roles: z.array(z.string()).default([]),
  is_self: z.boolean().default(false),
  // Per-leg execution outcome, so the card can flag a reverted counterpart
  // leg (a bridge destination that landed but reverted) with its reason.
  // `status` is the semantic status (`success`/`failed`/…), `receipt_status`
  // the raw EVM word (1/0), `error` the revert reason; `failed` is derived.
  status: z.union([z.string(), z.null()]).default(null),
  receipt_status: z.union([z.number(), z.null()]).default(null),
  error: z.union([z.string(), z.null()]).default(null),
  failed: z.boolean().default(false)
}).catchall(z.any());
var CrossChainGroupSchema = z.object({
  group_id: z.string(),
  saga_kind: z.union([z.string(), z.null()]).default(null),
  relationship_type: z.union([z.string(), z.null()]).default(null),
  linkage_model: z.union([z.string(), z.null()]).default(null),
  group_scope: z.union([z.string(), z.null()]).default(null),
  role: z.union([z.string(), z.null()]).default(null),
  confidence: z.union([z.number(), z.null()]).default(null),
  confidence_tier: z.union([z.string(), z.null()]).default(null),
  state: z.union([z.string(), z.null()]).default(null),
  // Legs the group holds — the store's own count, keyed on `(tx_id, role)`.
  member_count: z.union([z.number(), z.null()]).default(null),
  // Distinct transactions behind those legs, i.e. `members.length`. Lower than
  // `member_count` whenever a transaction plays more than one part, which is
  // why a card counting transactions must read this one: 18 transactions can
  // produce 22 legs of a pool's lifecycle.
  transaction_count: z.union([z.number(), z.null()]).default(null),
  members: z.array(CrossChainMemberSchema).default([]),
  // Live grouping lifecycle: canonical roles still expected (a lone source's
  // `["destination"]`), whether the group is waiting on a counterpart, and the
  // unix-seconds timeout — so the detail card can show "awaiting destination"
  // with a countdown, symmetric with the group page.
  expected_roles: z.array(z.string()).default([]),
  missing_roles: z.array(z.string()).default([]),
  awaiting: z.boolean().default(false),
  // True for a group that accretes legs and has no fixed counterpart — a
  // wallet's `trade.token_position`, a `pool.lifecycle`, a `token.launch`. Such
  // a group is never "awaiting a leg". A lifecycle fact only: it does not say
  // WHICH of the three this is, so route on `saga_kind`.
  open_ended: z.boolean().default(false),
  // The group's subject: the protocol's own name for the identifier every leg
  // shares (`pool`, `message_id` — feed it to `sagaIdLabel`) and its value (the
  // pool address, the `messageId`). A card cannot name what a group is ABOUT
  // without this: "Pool activity" states an activity but not a pool.
  id_label: z.union([z.string(), z.null()]).default(null),
  id_value: z.union([z.string(), z.null()]).default(null),
  // For a wallet's position only: the token address the position is in + the
  // venue it traded on (e.g. `uniswap v2`), lifted off the legs so the card
  // can title the position by token. `null` for every other shape — a pool's
  // lifecycle has no single trader and no one token.
  token: z.union([z.string(), z.null()]).default(null),
  venue: z.union([z.string(), z.null()]).default(null),
  expires_at: z.union([z.number(), z.null()]).default(null),
  // True when any leg's execution reverted (a broken saga) — derived from the
  // members' per-leg status so the card can surface it even while state=open.
  failed: z.boolean().default(false)
}).catchall(z.any());
var ChainForkSchema = z.object({
  chain_id: z.string(),
  canonical_tx_id: z.union([z.string(), z.null()]).default(null),
  tx_hash: z.union([z.string(), z.null()]).default(null)
}).catchall(z.any());
var TransactionDetailSchema = z.object({
  canonical_tx_id: z.string(),
  chain_id: z.union([z.string(), z.null()]).default(null),
  tx_hash: z.union([z.string(), z.null()]).default(null),
  // Tenancy of the SERVED row — lets the FE show whose/which copy this is.
  customer_id: z.union([z.string(), z.null()]).default(null),
  org_id: z.union([z.string(), z.null()]).default(null),
  tenant_id: z.union([z.string(), z.null()]).default(null),
  user_id: z.union([z.string(), z.null()]).default(null),
  // Body pointer (the heavy payload stays in S3 / on the CDN).
  has_body: z.boolean().default(false),
  body_key: z.union([z.string(), z.null()]).default(null),
  body_etag: z.union([z.string(), z.null()]).default(null),
  body: z.union([z.record(z.string(), z.any()), z.null()]).default(null),
  // Manifest of heavy body parts offloaded to sibling S3 objects (`raw` /
  // `events` / `decoded`), each with its own byte size + etag. Empty when the
  // whole body is a single "core" object. Lets the detail UI paint the core
  // immediately and lazy-load + merge the heavy slices on demand (mirrors
  // `TransactionRow.body_slices` / `EnvelopeHeader.body_slices`).
  body_slices: z.record(
    z.string(),
    z.object({
      bytes: z.number().int().default(0),
      etag: z.union([z.string(), z.null()]).default(null)
    }).catchall(z.any())
  ).default({}),
  // Async background-enrichment state. `processing` = the decode/graph is
  // running on the pipeline (the row is a fast stub with no body yet), `ready`
  // = the body/graph have landed (== `has_body`), `failed` = the run errored,
  // `null` = the tx was never part of the async flow (already enriched / sync
  // live-fill). `run_id` is the enqueued pipeline run, for polling/diagnostics.
  // The FE polls the detail while `processing`, then loads the body on `ready`.
  enrichment_status: z.union([z.string(), z.null()]).default(null),
  run_id: z.union([z.string(), z.null()]).default(null),
  // Workspace + cross-scope affordance.
  saved: z.boolean().default(false),
  other_scopes: z.array(ScopeOptionSchema).default([]),
  // Cross-chain saga groups this tx is a leg of (source↔destination linkage),
  // resolved at read time from the protocols' `cross_chain_refs`.
  cross_chain_groups: z.array(CrossChainGroupSchema).default([]),
  // Forked chains (ETH/ETC) the SAME raw hash is also on — the same hash is a
  // distinct tx per chain, so each is a separate row the FE can switch to.
  chain_forks: z.array(ChainForkSchema).default([])
}).catchall(z.any());
function isWidenableScope(option) {
  return option.readable && Object.keys(option.overrides ?? {}).length > 0;
}
function scopeReadQuery(scope, opts = {}) {
  const query = {};
  if (opts.includeBody) query.include_body = true;
  if (opts.enqueue === false) query.enqueue = false;
  if (scope) {
    for (const [key, value] of Object.entries(scope.overrides ?? {})) {
      query[key] = value;
    }
  }
  return query;
}
function sameScopeOverrides(a, b) {
  const ak = Object.keys(a ?? {});
  const bk = Object.keys(b ?? {});
  if (ak.length !== bk.length) return false;
  return ak.every((key) => a[key] === (b ?? {})[key]);
}

export { AMOUNT_BASIS, AUTHORED_AT_ATTR, AUTHORED_BY_ATTR, AUTHORED_REASON_ATTR, AUTHORED_SCOPE_ATTR, AbiEventSchema, AbiSchema, AccessWarningSchema, ActivityLogEventSchema, ActivityLogSchema, AddressActorMemberRowSchema, AddressActorRowSchema, AddressClusterMemberRowSchema, AddressClusterRowSchema, AddressClusterRunRowSchema, AddressEventSchema, AddressIdentitySchema, AddressIncidentNoticeSchema, AddressRelationshipSchema, AddressSchema, AlertDestinationSchema, ApiKeySchema, AssetTickerSchema, AuthSessionSchema, AuthenticationEventSchema, BalanceChangeSchema, BalanceTimelinePointSchema, BlockProcessingResultSchema, BlockSpecSchema, ByStatusQuerySchema, CALLER_OVERRIDABLE_STATUSES, COMBINE_STRATEGIES, CONTESTED_ATTR, CatalogAssignmentSchema, ChainCursorSchema, ChainForkSchema, ChainInsightInputSchema, ChainScopedAddressSchema, ChainStateRequestSchema, CombineRuleSchema, CombinedValueSchema, ContractStateNodeSchema, ContractStateRequestSchema, ContractStateResultSchema, ContractTargetSchema, CorrectionEventSchema, CrossChainGroupSchema, CrossChainMemberSchema, CustomerSchema, DISCOVERY_INDEX_DISABLED_NOTICE, DISCOVERY_INDEX_DISABLED_REASON, DISCOVERY_INDEX_SETTING_KEY, DrillDownRuleSchema, ENABLE_VIA_CUSTOM_PIPELINE, EnrichedTransactionSchema, EntityEventSchema, EntityHintSchema, EntityLinkSchema, EntityMatch, EntityRelationshipSchema, EntitySchema, ErrorBodySchema, ErrorResponseSchema, EventBaseSchema, EvidencePolicySchema, EvidenceSchema, EvidenceSourceSchema, ExecutionProfileSchema, ExportRecordSchema, ExtractedFacetValueSchema, FILTER_INDEXES_DISABLED_REASON, FILTER_INDEXES_SETTING_KEY, FILTER_INDEX_DISABLED_NOTICE, FacetDefinitionSchema, FieldOverridesSchema, GRAPH_KINDS, GRAPH_LAYOUT_HINTS, GROUPING_DIMENSIONS, GraphEdgeSchema, GraphNodeSchema, GraphSceneSchema, GroupEventSchema, GroupSchema, GroupingDimensionOptionsSchema, GroupingSpecSchema, IdentityOverrideSchema, ImportTransactionRequestSchema, IncidentAddressSchema, IncidentReferenceSchema, IncidentSchema, InteractionGraphSchema, KnobOriginSchema, LabelEventSchema, LabelSchema, LambdaRequestSchema, ListTransactionsQuerySchema, MEDIA_REF_KINDS, MERGED_COUNT_ATTR, MERGED_TXS_ATTR, MERGED_TXS_LIMIT, MaterializedViewSpecSchema, MediaRefSchema, MetadataPolicySchema, NativeTransferSchema, NetFlowSchema, NftSchema, NodeUiHintsSchema, NormalizedTaskSchema, OFFCHAIN_SCENE_NODE_KINDS, ORDER_STATUS, OVERLAY_BODY_KEYS, OrganizationEventSchema, OrganizationSchema, PaginationCursorSchema, PathSpecSchema, PipelineBackfillJobSchema, PipelineDefinitionSchema, PipelineDocSchema, PipelineExecutionPolicySchema, PipelineIssueSchema, PipelineNodeDocSchema, PipelineRunResultDTOSchema, PipelineRunSchema, PipelineValidationSchema, PolicyTierSchema, ProxyEventSchema, RelatedAddressLinkRowSchema, ReplayJobSchema, RequestAuth, RequestAuthSchema, RequestMetadataSchema, ResolvedLadderSchema, RpcBudgetSchema, SCENE_DETAIL_TIERS, SCENE_EDGE_KINDS, SCENE_ELEMENT_ORIGINS, SCENE_EMPTY_REASONS, SCENE_EMPTY_REASON_ATTR, SCENE_GROUP_TYPES, SCENE_NODE_KINDS, SEMANTIC_ACTOR_ROLES, SEMANTIC_NODE_KINDS, SUBJECT_ANY, SavedViewAccessLogSchema, SavedViewSchema, SavedViewShareLinkSchema, SceneAssetSchema, SceneEdgeSchema, SceneFactSchema, SceneGroupSchema, SceneNodeSchema, SceneOverlaySchema, SceneSourceRefSchema, ScopeOptionSchema, SearchEntityRefSchema, SearchFacetSchema, SearchFacetValueSchema, SearchGroupRefSchema, SearchHitEnrichmentSchema, SearchHitSchema, SearchLabelRefSchema, SearchQuerySchema, SearchResponseSchema, SearchTagRefSchema, SearchTermKind, SemanticActorSchema, SemanticAssetSchema, SemanticNodeSchema, SemanticSourceRefSchema, SemanticTreeSchema, ShareTransactionRequestSchema, StateValueSchema, StateVarRefSchema, StepCatalogSchema, StepDecisionDTOSchema, StepMetadataSchema, StepOptionSpecSchema, StepOutcomeDTOSchema, StepRunSchema, StreamDeliverySchema, StreamEventSchema, StreamedTransactionSchema, SuccessResponseSchema, SystemPipelineExecutionSchema, SystemPipelineSchema, TRANSACTION_SCOPES, TRANSACTION_SCOPE_DESCRIPTIONS, TRANSACTION_SCOPE_LABELS, TX_COUNT_ATTR, TX_FILTERS_CAPABILITY_PATH, TX_INDEXED_FILTERS, TX_INDEXED_FILTER_PARAMS, TX_MEMBERSHIP_ATTR, TagEventSchema, TagSchema, TeamInviteSchema, TeamMemberSchema, TeamRoleSchema, TeamSchema, TenantContextSchema, TenantSchema, TokenFlowSchema, TokenPriceSchema, TokenSchema, TracerRunSchema, TracerSchema, TransactionDetailSchema, TransactionDirection, TransactionEventSchema, TransactionInsightRequestSchema, TransactionRelationshipSchema, TransactionRowSchema, TransactionStateInsightSchema, TransactionStatus, TransferEventSchema, TriggerEventRowSchema, TriggerRuleRowSchema, TxParticipantSchema, TxTriggerEventSchema, UpdateTransactionRequestSchema, UserEventSchema, UserSchema, WatchRowSchema, WorkerHeartbeatSchema, WorkerObservabilitySettingsSchema, addressFeedLimit, annotationLayers, coerceBool, coerceCsv, defaultGroupingSpec, emptySceneOverlay, groupSceneLegs, isError, isSceneOverlayBody, isSuccess, isTxCountSublabel, isTxIndexedFilterId, isWidenableScope, layerForSubject, mergeAnnotationLayer, mergedCount, mergedTxRef, mergedTxRefs, normaliseHash, parseGraphScene, parseGroupingSpec, parseSceneOverlay, parseTxFiltersCapability, sameScopeOverrides, scopeReadQuery, transactionScopeDescription, transactionScopeLabel, txCountSublabel, txListNoticesOf, txMembership, unavailableTxFilters, validateRecipientEmails, walkContractStateTree, walkSemanticTree };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map